import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Upload, Camera, Shirt, Plus, Trash2, Grid, BarChart3, RefreshCw, 
  Heart, Search, Filter, X, Check, List, LayoutGrid, Wand2, Eye, Star, 
  Calendar, Layers, ShoppingBag, Sun, Cloud, Snowflake, Leaf, Sparkles,
  ChevronDown, ChevronRight, ChevronLeft, Image as ImageIcon, Palette, Tag, Zap,
  Footprints, Watch, Glasses, ShoppingCart, TrendingUp, Clock, Award, AlertCircle,
  Crown, RotateCcw, Maximize2, ArrowLeftRight, MessageCircle, Send, Bot, Lightbulb,
  Briefcase, Plane, Share2, Settings, PieChart, BookOpen, Video, Users, Phone,
  Bookmark, MapPin, Luggage, Archive, Folder, CameraIcon, Grid3X3, LayoutList, UserPlus, Dog, Pencil, ArrowLeft,
  Sunrise, Sunset, Moon, HeartHandshake, Copy, Link, Unlink, Thermometer
} from 'lucide-react';
// Additional icons used
import { ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { useToast } from '../components/ui/use-toast';
import VirtualTryOn from '../components/VirtualTryOn';
import { getApiUrl, getCurrencySymbol, getSupportedCurrencies, getCurrency, setCurrency as setGlobalCurrency } from '../utils';
import { cacheSet, cacheGet } from '../utils/offlineCache';
import { cn } from '../lib/utils';

const API_URL = getApiUrl();
const cs = () => getCurrencySymbol();

// Category icons mapping
const CATEGORY_ICONS = {
  tops: Shirt,
  bottoms: () => <span className="text-lg">👖</span>,
  outerwear: () => <span className="text-lg">🧥</span>,
  dresses: () => <span className="text-lg">👗</span>,
  sportswear: () => <span className="text-lg">🏃</span>,
  footwear: Footprints,
  accessories: () => <span className="text-lg">👜</span>,
  swimwear: () => <span className="text-lg">👙</span>
};

// Consolidated accessory subcategories — groups individual clothing_types into named sections
const ACCESSORY_SUBCATEGORIES = [
  { label: 'Bags & Purses', icon: '👜', types: ['bags', 'purses', 'clutches', 'backpacks'] },
  { label: 'Jewelry', icon: '💎', types: ['jewelry', 'necklaces', 'chains', 'bracelets', 'rings', 'earrings'] },
  { label: 'Watches', icon: '⌚', types: ['watches'] },
  { label: 'Eyewear', icon: '🕶️', types: ['sunglasses', 'glasses'] },
  { label: 'Hats & Headwear', icon: '🧢', types: ['hats', 'caps', 'hair_accessories'] },
  { label: 'Scarves & Wraps', icon: '🧣', types: ['scarves', 'gloves'] },
  { label: 'Belts & Ties', icon: '👔', types: ['belts', 'ties'] },
  { label: 'Swimwear', icon: '👙', types: ['swimwear', 'bikini', 'swimsuit', 'swim_trunks'] },
];

// Color sort order — items within each category are sorted by color so same-color items sit together
const COLOR_SORT_ORDER = [
  'black', 'navy', 'gray', 'white', 'cream', 'beige', 'tan',
  'brown', 'burgundy', 'red', 'coral', 'orange', 'gold', 'yellow',
  'olive', 'green', 'teal', 'blue', 'purple', 'pink', 'silver', 'multicolor'
];
const sortItemsByColor = (a, b) => {
  const ac = (a.color || '').toLowerCase();
  const bc = (b.color || '').toLowerCase();
  const ai = COLOR_SORT_ORDER.indexOf(ac);
  const bi = COLOR_SORT_ORDER.indexOf(bc);
  return (ai === -1 ? 98 : ai) - (bi === -1 ? 98 : bi);
};

// Helper: assign an accessory item to its consolidated subcategory
const getAccessorySubcategory = (clothingType) => {
  for (const sub of ACCESSORY_SUBCATEGORIES) {
    if (sub.types.includes(clothingType)) return sub;
  }
  return null; // will fall into "Other" bucket
};

const SeasonIcon = ({ season }) => {
  switch(season) {
    case 'Spring': return <Leaf className="w-4 h-4 text-olive-500" />;
    case 'Summer': return <Sun className="w-4 h-4 text-yellow-500" />;
    case 'Fall': return <Cloud className="w-4 h-4 text-orange-500" />;
    case 'Winter': return <Snowflake className="w-4 h-4 text-mud-500" />;
    default: return <Calendar className="w-4 h-4 text-gray-400" />;
  }
};

// Client-side fallback: build grouped data from flat items array
const buildGroupedFromItems = (items) => {
  const catNames = { tops: 'Tops', bottoms: 'Bottoms', outerwear: 'Outerwear', dresses: 'Dresses & Jumpsuits', sportswear: 'Sportswear', footwear: 'Footwear', accessories: 'Accessories', swimwear: 'Swimwear' };
  const grouped = {};
  for (const key of Object.keys(catNames)) grouped[key] = { name: catNames[key], items: [], count: 0 };
  for (const item of items) {
    const cat = (item.primary_category || item.display_category || item.category || 'accessories').toLowerCase();
    if (grouped[cat]) grouped[cat].items.push(item);
    else if (grouped.accessories) grouped.accessories.items.push(item);
  }
  for (const k of Object.keys(grouped)) grouped[k].count = grouped[k].items.length;
  return grouped;
};

export default function MyWardrobe() {
  // Toast notifications
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // State — use lazy initializer to read cache once on mount
  const [items, setItems] = useState([]);
  const [groupedItems, setGroupedItems] = useState({});
  const [categories, setCategories] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState(['tops', 'bottoms', 'dresses', 'outerwear', 'footwear', 'accessories']);
  
  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    color: '', season: '', brand: '', occasion: '', sortBy: 'created_at', sortOrder: 'DESC', favorite: false
  });
  
  // View modes
  const [viewMode, setViewMode] = useState('category'); // 'category', 'grid', 'list'
  
  // Selection mode for bulk operations
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Bulk upload state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkImages, setBulkImages] = useState([]); // Array of { file, preview, base64 }
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, status: '' });
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkAddsRemaining, setBulkAddsRemaining] = useState(null);
  const bulkInputRef = useRef(null);
  const styleMaidOverlayRef = useRef(null);

  // Add item dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [imageRotation, setImageRotation] = useState(0); // Rotation angle in degrees
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [useAiDetection, setUseAiDetection] = useState(true); // Toggle for AI color detection
  const [selectedMultiColors, setSelectedMultiColors] = useState([]); // For multicolor selection
  const [showMultiColorPicker, setShowMultiColorPicker] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '', primaryCategory: '', clothingType: '', brand: '', price: '',
    color: '', size: '', material: '', season: '', occasions: [],
    fitType: '', condition: 'New', notes: '', imageUrl: '', storageLocation: '', productUrl: ''
  });
  
  // Outfit Builder
  const [showOutfitBuilder, setShowOutfitBuilder] = useState(false);
  const [currentOutfit, setCurrentOutfit] = useState({
    name: '', items: []
  });
  const [savedOutfits, setSavedOutfits] = useState([]);
  const [savingOutfit, setSavingOutfit] = useState(false);
  const [deletingOutfitId, setDeletingOutfitId] = useState(null);
  const [savingCapsule, setSavingCapsule] = useState(false);
  const [deletingCapsuleId, setDeletingCapsuleId] = useState(null);
  const [savingPacking, setSavingPacking] = useState(false);
  const [deletingPackingId, setDeletingPackingId] = useState(null);
  const [movingToWardrobe, setMovingToWardrobe] = useState(null); // wishlist item id being moved
  const [outfitPreviewLoading, setOutfitPreviewLoading] = useState(false);
  const [showOutfitPickerDialog, setShowOutfitPickerDialog] = useState(false);
  const [outfitPickerCategory, setOutfitPickerCategory] = useState(null);
  const [showOutfitPreview, setShowOutfitPreview] = useState(false);
  const [previewOutfit, setPreviewOutfit] = useState(null);
  const [swapCategory, setSwapCategory] = useState(null); // For filtering alternatives
  
  // AI Suggest parameters (weather, color, event)
  const [showAiSuggestDialog, setShowAiSuggestDialog] = useState(false);
  const [aiSuggestParams, setAiSuggestParams] = useState({
    temperature: '',
    colorPreference: '',
    upcomingEvent: '',
    dressCode: '' // casual, formal, business casual
  });
  // Item detail
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemDetail, setShowItemDetail] = useState(false);
  
  // Virtual Try-On
  const [showTryOn, setShowTryOn] = useState(false);
  const [tryOnItem, setTryOnItem] = useState(null);
  
  // Style Maid AI Agent
  const [showStyleMaid, setShowStyleMaid] = useState(false);
  const [styleMaidMessages, setStyleMaidMessages] = useState([]);
  const [styleMaidInput, setStyleMaidInput] = useState('');
  const [styleMaidLoading, setStyleMaidLoading] = useState(false);
  const [styleMaidDismissed, setStyleMaidDismissed] = useState(() => localStorage.getItem('styleMaidDismissed') === 'true');
  const [guideAgentOpen, setGuideAgentOpen] = useState(false);

  // Listen for restore event from layout
  useEffect(() => {
    const handleRestore = () => setStyleMaidDismissed(false);
    window.addEventListener('restoreAgents', handleRestore);
    return () => window.removeEventListener('restoreAgents', handleRestore);
  }, []);

  // Listen for other chat agents opening - close this one
  useEffect(() => {
    const handleOtherChatOpen = (e) => {
      if (e.detail?.agent !== 'styleMaid') {
        setShowStyleMaid(false);
      }
    };
    
    window.addEventListener('chatAgentOpened', handleOtherChatOpen);
    return () => window.removeEventListener('chatAgentOpened', handleOtherChatOpen);
  }, []);

  // Broadcast when this chat opens or closes
  useEffect(() => {
    if (showStyleMaid) {
      window.dispatchEvent(new CustomEvent('chatAgentOpened', { detail: { agent: 'styleMaid', isOpen: true } }));
    } else {
      window.dispatchEvent(new CustomEvent('chatAgentClosed', { detail: { agent: 'styleMaid', isOpen: false } }));
    }
  }, [showStyleMaid]);

  // Lock scroll + reposition overlay when iOS keyboard opens
  useEffect(() => {
    if (!showStyleMaid) return;
    const prevBg = document.body.style.backgroundColor;
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100%';
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100%';
    document.body.style.backgroundColor = '#ffffff';

    const repositionOverlay = () => {
      const vv = window.visualViewport;
      const el = styleMaidOverlayRef.current;
      if (!vv || !el) return;
      el.style.top = `${vv.offsetTop}px`;
      el.style.height = `${vv.height}px`;
      el.style.bottom = 'auto';
    };
    window.visualViewport?.addEventListener('resize', repositionOverlay);
    window.visualViewport?.addEventListener('scroll', repositionOverlay);

    return () => {
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.backgroundColor = prevBg;
      window.visualViewport?.removeEventListener('resize', repositionOverlay);
      window.visualViewport?.removeEventListener('scroll', repositionOverlay);
      const el = styleMaidOverlayRef.current;
      if (el) { el.style.top = ''; el.style.height = ''; el.style.bottom = ''; }
    };
  }, [showStyleMaid]);

  // Listen for Guide Agent state changes
  useEffect(() => {
    const handleGuideState = (e) => {
      if (e.detail?.agent === 'guide') {
        setGuideAgentOpen(e.detail?.isOpen || false);
      }
    };
    window.addEventListener('chatAgentOpened', (e) => {
      if (e.detail?.agent === 'guide') setGuideAgentOpen(true);
    });
    window.addEventListener('chatAgentClosed', (e) => {
      if (e.detail?.agent === 'guide') setGuideAgentOpen(false);
    });
    return () => {
      window.removeEventListener('chatAgentOpened', handleGuideState);
      window.removeEventListener('chatAgentClosed', handleGuideState);
    };
  }, []);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [recommendationCriteria, setRecommendationCriteria] = useState({
    occasion: '', colorPreference: '', mood: '', weather: '', style: ''
  });
  
  // Wishlist / Inspiration Items (items you like from shops but don't own)
  const [wardrobeMode, setWardrobeMode] = useState('owned'); // 'owned', 'wishlist', 'capsules', 'packing', 'calendar', 'analytics', 'boards', 'journey', 'stylist'
  const [showCustomClothingType, setShowCustomClothingType] = useState(false);
  const [customClothingTypeValue, setCustomClothingTypeValue] = useState('');
  const [expandedPickerCategories, setExpandedPickerCategories] = useState({});
  const [wishlistItems, setWishlistItems] = useState([]);
  const [addWishlistDialogOpen, setAddWishlistDialogOpen] = useState(false);
  const [wishlistImage, setWishlistImage] = useState(null);
  const [wishlistForm, setWishlistForm] = useState({
    name: '', brand: '', store: '', price: '', notes: '', link: '', productUrl: '', primaryCategory: '', clothingType: ''
  });
  const [isAnalyzingWishlist, setIsAnalyzingWishlist] = useState(false);
  const [wishlistAiAnalysis, setWishlistAiAnalysis] = useState(null);
  const [editingWishlistItem, setEditingWishlistItem] = useState(null);
  const [showEditWishlistDialog, setShowEditWishlistDialog] = useState(false);
  const [editWishlistImage, setEditWishlistImage] = useState(null);
  const [addingItem, setAddingItem] = useState(false);
  const [editWishlistAiAnalysis, setEditWishlistAiAnalysis] = useState(null);
  const [isAnalyzingEditWishlist, setIsAnalyzingEditWishlist] = useState(false);
  const [editWishlistForm, setEditWishlistForm] = useState({
    name: '', brand: '', store: '', price: '', notes: '', link: '', productUrl: '', primaryCategory: '', clothingType: ''
  });
  
  // Capsule Wardrobes
  const [capsules, setCapsules] = useState([]);
  const [showCapsuleDialog, setShowCapsuleDialog] = useState(false);
  const [currentCapsule, setCurrentCapsule] = useState({ name: '', description: '', type: 'seasonal', items: [] });
  const [editingCapsule, setEditingCapsule] = useState(null);
  const [viewingCapsule, setViewingCapsule] = useState(null);
  
  // Packing Lists
  const [packingLists, setPackingLists] = useState([]);
  const [showPackingDialog, setShowPackingDialog] = useState(false);
  const [currentPackingList, setCurrentPackingList] = useState({ 
    name: '', destination: '', startDate: '', endDate: '', items: [], packedItems: [], coverImage: '' 
  });
  const [editingPackingList, setEditingPackingList] = useState(null);
  const [viewingPackingList, setViewingPackingList] = useState(null);
  const [showCapsulePickerForPacking, setShowCapsulePickerForPacking] = useState(false);
  const [selectedCapsuleForPacking, setSelectedCapsuleForPacking] = useState(null);
  const [selectedCapsuleItems, setSelectedCapsuleItems] = useState(new Set());
  const [showTripPickerForCapsule, setShowTripPickerForCapsule] = useState(false);
  const [capsuleForTripPicker, setCapsuleForTripPicker] = useState(null);
  
  // Outfit Calendar
  const [calendarView, setCalendarView] = useState('month');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [outfitCalendar, setOutfitCalendar] = useState({});
  const [showCalendarOutfitDialog, setShowCalendarOutfitDialog] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [calendarOutfitItems, setCalendarOutfitItems] = useState([]);
  const [calendarOutfitNote, setCalendarOutfitNote] = useState('');
  const [calendarOutfitTimeOfDay, setCalendarOutfitTimeOfDay] = useState('morning');
  const [calendarOutfitOccasion, setCalendarOutfitOccasion] = useState('');
  const [editingSlotId, setEditingSlotId] = useState(null); // which slot we're editing, null = new
  const [viewingCalendarOutfit, setViewingCalendarOutfit] = useState(null); // { date, dayData }
  const [viewingSlotIndex, setViewingSlotIndex] = useState(0);
  
  // Partner Wardrobe
  const [partnerDialogOpen, setPartnerDialogOpen] = useState(false);
  const [partnerDialogTab, setPartnerDialogTab] = useState('link'); // 'link', 'browse', 'suggestions'
  const [partnerDialogType, setPartnerDialogType] = useState('partner'); // 'partner' or 'friend'
  const [partnerInviteCode, setPartnerInviteCode] = useState('');
  const [partnerCodeInput, setPartnerCodeInput] = useState('');
  const [partners, setPartners] = useState([]);
  const [friends, setFriends] = useState([]);
  const [activePartner, setActivePartner] = useState(null);
  const [partnerItems, setPartnerItems] = useState([]);
  const [loadingPartnerItems, setLoadingPartnerItems] = useState(false);
  const [partnerSelectedItems, setPartnerSelectedItems] = useState([]);
  const [partnerSuggestionNote, setPartnerSuggestionNote] = useState('');
  const [partnerSuggestionOccasion, setPartnerSuggestionOccasion] = useState('');
  const [receivedSuggestions, setReceivedSuggestions] = useState([]);
  const [sentSuggestions, setSentSuggestions] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [partnerItemFilter, setPartnerItemFilter] = useState('all');
  
  // Lookbook
  const [showLookbookDialog, setShowLookbookDialog] = useState(false);
  const [lookbookItemToAdd, setLookbookItemToAdd] = useState(null);
  const [userLookbooks, setUserLookbooks] = useState([]);
  const [newLookbookName, setNewLookbookName] = useState('');
  const [loadingLookbooks, setLoadingLookbooks] = useState(false);
  
  // Edit Item
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '', primaryCategory: '', clothingType: '', brand: '', price: '',
    color: '', size: '', material: '', season: '', fitType: '', condition: '', notes: ''
  });
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItem, setEditingItem] = useState(null); // Store full item for image
  const [editImageRotation, setEditImageRotation] = useState(0);
  const [isUpdatingItem, setIsUpdatingItem] = useState(false);
  
  // Pets
  const [petsEnabled, setPetsEnabled] = useState(() => {
    return localStorage.getItem('petsEnabled') === 'true';
  });
  const [petProfiles, setPetProfiles] = useState([]);
  const [selectedPet, setSelectedPet] = useState(null);
  const [petWardrobeItems, setPetWardrobeItems] = useState([]);
  const [showAddPetDialog, setShowAddPetDialog] = useState(false);
  const [showAddPetItemDialog, setShowAddPetItemDialog] = useState(false);
  const [petForm, setPetForm] = useState({ name: '', species: 'dog', breed: '', size: 'medium', color: '' });
  const [petItemForm, setPetItemForm] = useState({ name: '', category: 'clothing', color: '', brand: '', size: '' });
  const [petItemImage, setPetItemImage] = useState(null);
  const [editingPetItemId, setEditingPetItemId] = useState(null);
  const [loadingPets, setLoadingPets] = useState(false);
  const [petPhotoPreview, setPetPhotoPreview] = useState(null);
  const [editingPetId, setEditingPetId] = useState(null);
  const [savingPet, setSavingPet] = useState(false);
  
  // Display Customization
  const [displaySettings, setDisplaySettings] = useState({
    gridSize: 'medium', // 'small', 'medium', 'large'
    showLabels: true,
    showPrices: true,
    showWearCount: true,
    sortBy: 'recent',
    hdQuality: false
  });
  const [showDisplaySettingsDialog, setShowDisplaySettingsDialog] = useState(false);
  const [hdImageModal, setHdImageModal] = useState(null);
  
  // Analytics Dashboard
  const [analyticsData, setAnalyticsData] = useState({
    wearFrequency: [],
    costPerWearTrend: [],
    categoryBreakdown: [],
    colorDistribution: [],
    seasonalAnalysis: [],
    mostWorn: [],
    leastWorn: [],
    valueAnalysis: {}
  });
  
  // Inspiration Boards
  const [inspirationBoards, setInspirationBoards] = useState([]);
  const [inspirationBoardsLoaded, setInspirationBoardsLoaded] = useState(false);
  const [showBoardDialog, setShowBoardDialog] = useState(false);
  const [currentBoard, setCurrentBoard] = useState({ name: '', description: '', inspiration: [] });
  const [viewingBoardId, setViewingBoardId] = useState(null);
  const [editingBoardId, setEditingBoardId] = useState(null);
  const [boardImageUrl, setBoardImageUrl] = useState('');
  const [boardImageNote, setBoardImageNote] = useState('');
  const [failedPinImages, setFailedPinImages] = useState({});

  // Derive viewingBoard live from inspirationBoards so it's always in sync
  const viewingBoard = viewingBoardId ? inspirationBoards.find(b => b.id === viewingBoardId) || null : null;
  
  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  // Retry reading token after short delays (covers Capacitor WebView cold-start on iPad)
  useEffect(() => {
    if (!token) {
      const t1 = setTimeout(() => { const t = localStorage.getItem('token'); if (t) setToken(t); }, 300);
      const t2 = setTimeout(() => { const t = localStorage.getItem('token'); if (t) setToken(t); }, 800);
      const t3 = setTimeout(() => { const t = localStorage.getItem('token'); if (t) setToken(t); }, 1500);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, [token]);

  // Load cached data immediately (shown while API fetches)
  const cachedRef = useRef(false);

  // Load cached data on mount
  useEffect(() => {
    cacheGet('wardrobe_data').then(cached => {
      if (cached?.data?.items?.length) {
        console.log('📦 Loaded wardrobe from cache:', cached.data.items.length, 'items');
        setItems(cached.data.items);
        if (cached.data.stats) setStats(cached.data.stats);
        // Restore grouped — prefer cached grouped, fall back to building from items
        const grouped = (cached.data.grouped && Object.values(cached.data.grouped).some(v => v?.items?.length > 0))
          ? cached.data.grouped
          : buildGroupedFromItems(cached.data.items);
        setGroupedItems(grouped);
        if (cached.data.capsules?.length) {
          setCapsules(cached.data.capsules);
        }
        if (cached.data.wishlist?.length) {
          setWishlistItems(cached.data.wishlist);
        }
        if (cached.data.packingLists?.length) {
          setPackingLists(cached.data.packingLists.map(p => ({ ...p, items: p.items || [], packedItems: p.packedItems || [] })));
        }
        setLoading(false);
        cachedRef.current = true;
      }
    });
  }, []);

  // Fetch categories on mount
  useEffect(() => {
    if (token) fetchCategories();
  }, [token]);

  // Fetch data
  const fetchCategories = async () => {
    try {
      const t = token || localStorage.getItem('token');
      if (!t) return;
      const res = await axios.get(`${API_URL}/real-wardrobe/categories`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      setCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Sort packing lists: upcoming trips first (nearest date first), then past (most recent first), then no-date
  const sortPackingLists = (lists) => {
    const today = new Date().toISOString().split('T')[0];
    return [...lists].sort((a, b) => {
      const aDate = a.startDate || a.start_date;
      const bDate = b.startDate || b.start_date;
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      const aFuture = aDate >= today;
      const bFuture = bDate >= today;
      if (aFuture && !bFuture) return -1;
      if (!aFuture && bFuture) return 1;
      if (aFuture && bFuture) return aDate.localeCompare(bDate);
      return bDate.localeCompare(aDate);
    });
  };

  const fetchWardrobe = useCallback(async () => {
    // Re-read token fresh in case the state wasn't updated yet (Capacitor cold start)
    const currentToken = token || localStorage.getItem('token');
    if (!currentToken) return; // keep loading spinner while waiting for token retry
    try {
      const params = new URLSearchParams();
      if (filters.color) params.append('color', filters.color);
      if (filters.season) params.append('season', filters.season);
      if (filters.brand) params.append('brand', filters.brand);
      if (filters.occasion) params.append('occasion', filters.occasion);
      if (filters.favorite) params.append('favorite', 'true');
      if (searchQuery) params.append('search', searchQuery);
      params.append('sortBy', filters.sortBy);
      params.append('sortOrder', filters.sortOrder);
      
      const [itemsRes, statsRes, outfitsRes, wishlistRes, capsulesRes, packingRes] = await Promise.all([
        axios.get(`${API_URL}/real-wardrobe?${params.toString()}`, { headers: { Authorization: `Bearer ${currentToken}` } }),
        axios.get(`${API_URL}/real-wardrobe/stats`, { headers: { Authorization: `Bearer ${currentToken}` } }),
        axios.get(`${API_URL}/real-wardrobe/outfits`, { headers: { Authorization: `Bearer ${currentToken}` } }).catch((e) => { console.error('Outfits fetch error:', e.message); return null; }),
        axios.get(`${API_URL}/real-wardrobe/wishlist`, { headers: { Authorization: `Bearer ${currentToken}` } }).catch((e) => { console.error('❌ Wishlist fetch error:', e.message, e.response?.data); return null; }),
        axios.get(`${API_URL}/real-wardrobe/capsules`, { headers: { Authorization: `Bearer ${currentToken}` } }).catch((e) => { console.error('Capsules fetch error:', e.message); return null; }),
        axios.get(`${API_URL}/real-wardrobe/packing-lists`, { headers: { Authorization: `Bearer ${currentToken}` } }).catch((e) => { console.error('Packing lists fetch error:', e.message); return null; })
      ]);
      
      console.log('📦 Fetched wishlist items:', wishlistRes?.data?.items?.length || 0, 'items');
      
      // Sort items by color within each category so same-color items are grouped together
      const fetchedItems = itemsRes.data.items || [];
      fetchedItems.sort(sortItemsByColor);
      setItems(fetchedItems);
      const fetchedGrouped = itemsRes.data.grouped || {};
      for (const key of Object.keys(fetchedGrouped)) {
        if (fetchedGrouped[key]?.items) fetchedGrouped[key].items.sort(sortItemsByColor);
      }
      setGroupedItems(fetchedGrouped);
      setStats(statsRes.data || {});
      if (outfitsRes) setSavedOutfits(outfitsRes.data?.outfits || []);
      if (wishlistRes) setWishlistItems(wishlistRes.data?.items || []);
      if (capsulesRes) setCapsules(capsulesRes.data?.capsules || []);
      // Ensure every packing list has safe packedItems and items arrays
      if (packingRes) {
        const safePacking = (packingRes.data?.packingLists || []).map(p => ({
          ...p,
          items: p.items || [],
          packedItems: p.packedItems || []
        }));
        // Sort: upcoming trips first (nearest date first), then past trips (most recent first), then no-date
        setPackingLists(sortPackingLists(safePacking));
      }

      // Cache for offline use — keep only essential display fields
      const tinyItem = (item) => ({
        id: item.id, name: item.name, image_url: item.image_url,
        category: item.category, primary_category: item.primary_category,
        display_category: item.display_category,
        color: item.color, brand: item.brand, times_worn: item.times_worn,
        is_favorite: item.is_favorite, clothing_type: item.clothing_type
      });
      // Build lightweight grouped cache (category metadata + tiny items)
      const tinyGrouped = {};
      for (const [key, data] of Object.entries(fetchedGrouped)) {
        tinyGrouped[key] = { name: data.name, icon: data.icon, types: data.types, items: (data.items || []).map(tinyItem), count: data.count };
      }
      // Only cache capsules if GET succeeded; otherwise preserve whatever's already cached
      const capsulesToCache = capsulesRes ? (capsulesRes.data?.capsules || []) : undefined;
      const wishlistToCache = wishlistRes ? (wishlistRes.data?.items || []) : undefined;
      const packingToCache = packingRes ? (packingRes.data?.packingLists || []).map(p => ({ ...p, items: p.items || [], packedItems: p.packedItems || [] })) : undefined;
      cacheSet('wardrobe_data', {
        items: fetchedItems.map(tinyItem),
        stats: statsRes.data || {},
        grouped: tinyGrouped,
        ...(capsulesToCache !== undefined && { capsules: capsulesToCache }),
        ...(wishlistToCache !== undefined && { wishlist: wishlistToCache }),
        ...(packingToCache !== undefined && { packingLists: packingToCache })
      });
    } catch (error) {
      console.error('Error fetching wardrobe:', error);
      // If fetch fails, try restoring from cache
      if (!cachedRef.current) {
        const cached = await cacheGet('wardrobe_data');
        if (cached?.data?.items) {
          setItems(cached.data.items);
          if (cached.data.stats) setStats(cached.data.stats);
          const grouped = cached.data.grouped || buildGroupedFromItems(cached.data.items);
          setGroupedItems(grouped);
          if (cached.data.capsules?.length) setCapsules(cached.data.capsules);
          if (cached.data.wishlist?.length) setWishlistItems(cached.data.wishlist);
          if (cached.data.packingLists?.length) setPackingLists(cached.data.packingLists);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [token, filters, searchQuery]);

  useEffect(() => { fetchWardrobe(); }, [fetchWardrobe]);

  // Refetch when page becomes visible (handles navigation back to page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 Page visible - refetching wardrobe data');
        fetchWardrobe();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchWardrobe]);

  // ========== PET FUNCTIONS ==========
  const fetchPets = useCallback(async () => {
    if (!token || !petsEnabled) return;
    setLoadingPets(true);
    try {
      const res = await axios.get(`${API_URL}/pets`, { headers: { Authorization: `Bearer ${token}` } });
      setPetProfiles(res.data.pets || []);
      if (res.data.pets?.length > 0 && !selectedPet) {
        setSelectedPet(res.data.pets[0]);
      }
    } catch (err) { console.error('❌ Error fetching pets:', err); }
    finally { setLoadingPets(false); }
  }, [token, petsEnabled, selectedPet]);

  useEffect(() => { if (petsEnabled) fetchPets(); }, [petsEnabled, fetchPets]);

  const fetchPetWardrobe = useCallback(async (petId) => {
    if (!token || !petId) return;
    try {
      const res = await axios.get(`${API_URL}/pets/${petId}/wardrobe`, { headers: { Authorization: `Bearer ${token}` } });
      setPetWardrobeItems(res.data.items || []);
    } catch (err) { console.error('❌ Error fetching pet wardrobe:', err); }
  }, [token]);

  useEffect(() => { if (selectedPet) fetchPetWardrobe(selectedPet.id); }, [selectedPet, fetchPetWardrobe]);

  const handleTogglePets = (enabled) => {
    setPetsEnabled(enabled);
    localStorage.setItem('petsEnabled', enabled ? 'true' : 'false');
    if (enabled && petProfiles.length === 0) {
      setShowAddPetDialog(true);
    }
  };

  const handleEditPetStart = (pet) => {
    setEditingPetId(pet.id);
    setPetForm({ name: pet.name, species: pet.species || 'dog', breed: pet.breed || '', size: pet.size || 'medium', color: pet.color || '' });
    setPetPhotoPreview(pet.photo_url || pet.photo_base64 || null);
    setShowAddPetDialog(true);
  };

  const handleCreatePet = async () => {
    if (!petForm.name.trim()) return;
    setSavingPet(true);
    try {
      const payload = { ...petForm };
      if (petPhotoPreview && !petPhotoPreview.startsWith('http')) {
        payload.photo_base64 = petPhotoPreview;
      }
      if (editingPetId) {
        const res = await axios.put(`${API_URL}/pets/${editingPetId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setPetProfiles(prev => prev.map(p => p.id === editingPetId ? res.data.pet : p));
        if (selectedPet?.id === editingPetId) setSelectedPet(res.data.pet);
        toast({ title: "Pet updated! 🐾", description: `${res.data.pet.name}'s profile has been updated` });
      } else {
        const res = await axios.post(`${API_URL}/pets`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setPetProfiles(prev => [...prev, res.data.pet]);
        setSelectedPet(res.data.pet);
        toast({ title: "Pet added! 🐾", description: `${res.data.pet.name} has been added to your wardrobe` });
      }
      setShowAddPetDialog(false);
      setEditingPetId(null);
      setPetForm({ name: '', species: 'dog', breed: '', size: 'medium', color: '' });
      setPetPhotoPreview(null);
      setSavingPet(false);
    } catch (err) {
      console.error('❌ Error saving pet:', err);
      setSavingPet(false);
      toast({ title: "Error", description: editingPetId ? "Failed to update pet" : "Failed to add pet", variant: "destructive" });
    }
  };

  const handleDeletePet = async (petId) => {
    if (!window.confirm('Remove this pet? Their wardrobe items will also be removed.')) return;
    try {
      await axios.delete(`${API_URL}/pets/${petId}`, { headers: { Authorization: `Bearer ${token}` } });
      setPetProfiles(prev => prev.filter(p => p.id !== petId));
      if (selectedPet?.id === petId) setSelectedPet(petProfiles.find(p => p.id !== petId) || null);
      toast({ title: "Pet removed", description: "Pet profile has been removed" });
    } catch (err) { toast({ title: "Error", description: "Failed to remove pet", variant: "destructive" }); }
  };

  const handlePetPhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPetPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleAddPetWardrobeItem = async () => {
    if (!petItemForm.name.trim() || !selectedPet) return;
    try {
      const payload = { ...petItemForm };
      if (petItemImage) {
        payload.image_url = petItemImage;
      }
      if (editingPetItemId) {
        // Update existing item
        const res = await axios.put(`${API_URL}/pets/wardrobe/${editingPetItemId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setPetWardrobeItems(prev => prev.map(i => i.id === editingPetItemId ? res.data.item : i));
        toast({ title: "Updated! 🐾", description: `${selectedPet.name}'s item updated` });
      } else {
        // Add new item
        const res = await axios.post(`${API_URL}/pets/${selectedPet.id}/wardrobe`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setPetWardrobeItems(prev => [...prev, res.data.item]);
        toast({ title: "Item added! 🐾", description: `Added to ${selectedPet.name}'s wardrobe` });
      }
      setShowAddPetItemDialog(false);
      setPetItemForm({ name: '', category: 'clothing', color: '', brand: '', size: '' });
      setPetItemImage(null);
      setEditingPetItemId(null);
    } catch (err) { toast({ title: "Error", description: "Failed to save item", variant: "destructive" }); }
  };

  const handlePetItemImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPetItemImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleDeletePetItem = async (itemId) => {
    try {
      await axios.delete(`${API_URL}/pets/wardrobe/${itemId}`, { headers: { Authorization: `Bearer ${token}` } });
      setPetWardrobeItems(prev => prev.filter(i => i.id !== itemId));
      toast({ title: "Removed", description: "Item removed from pet wardrobe" });
    } catch (err) { toast({ title: "Error", description: "Failed to remove item", variant: "destructive" }); }
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } } 
      });
      streamRef.current = stream;
      setShowCamera(true);
    } catch (error) { 
      console.error('Error accessing camera:', error); 
      alert('Could not access camera. Please check your browser permissions.'); 
    }
  };

  // Attach camera stream to video element once it mounts
  useEffect(() => {
    if (showCamera && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [showCamera]);

  const stopCamera = () => {
    if (streamRef.current) { 
      streamRef.current.getTracks().forEach(track => track.stop()); 
      streamRef.current = null; 
    }
    setShowCamera(false);
  };

  // Compress and resize image to reduce file size
  const compressImage = (dataUrl, maxWidth = 800, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Calculate new dimensions maintaining aspect ratio
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to JPEG for better compression
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = dataUrl;
    });
  };

  const capturePhoto = async () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current, canvas = canvasRef.current;
      canvas.width = video.videoWidth; 
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/png');
      // Compress the captured image
      const compressedImage = await compressImage(imageData);
      setCapturedImage(compressedImage);
      setImageRotation(0); // Reset rotation on new capture
      stopCamera();
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        // Compress the uploaded image
        const compressedImage = await compressImage(e.target.result);
        setCapturedImage(compressedImage);
        setImageRotation(0); // Reset rotation on new upload
      };
      reader.readAsDataURL(file);
    }
  };

  // Rotate image by specified degrees
  const rotateImage = (degrees) => {
    setImageRotation((prev) => (prev + degrees) % 360);
  };

  // Apply rotation to image and return rotated data URL
  const applyRotationToImage = (imageDataUrl, rotation) => {
    if (rotation === 0) return imageDataUrl;
    
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Adjust canvas size based on rotation
        if (rotation === 90 || rotation === 270) {
          canvas.width = img.height;
          canvas.height = img.width;
        } else {
          canvas.width = img.width;
          canvas.height = img.height;
        }
        
        // Apply rotation
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = imageDataUrl;
    });
  };

  // Background removal using backend AI API
  const removeBackground = async () => {
    if (!capturedImage) {
      toast({
        title: "No image",
        description: "Please capture or upload an image first",
        variant: "destructive"
      });
      return;
    }
    setIsProcessing(true);
    
    try {
      // Use backend AI-powered background removal (Replicate fottoai/remove-bg-2)
      const response = await axios.post(`${API_URL}/real-wardrobe/remove-background`, {
        imageBase64: capturedImage.split(',')[1]
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (response.data.success && response.data.processedImage) {
        setProcessedImage(response.data.processedImage);
        toast({
          title: "✨ Background Removed",
          description: "AI-powered background removal complete",
        });
      } else {
        toast({
          title: "Background Removal Failed",
          description: response.data.error || "The AI service couldn't process this image. Try a different photo.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Background removal error:', error);
      const rawErr = error.response?.data?.error || error.message || 'Unknown error';
      const errorMsg = typeof rawErr === 'object' ? (rawErr.message || 'Unknown error') : rawErr;
      toast({
        title: "Background Removal Failed",
        description: errorMsg.includes('Usage limit') 
          ? "You've reached your background removal limit. Tap here to upgrade your plan."
          : "Could not remove background. Please try again or use a different photo.",
        variant: "destructive",
        ...(errorMsg.includes('Usage limit') && { action: <button onClick={() => navigate('/account')} className="text-xs underline font-semibold">Upgrade</button> })
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Remove background for any wardrobe/wishlist item by ID
  const [removingBgItemId, setRemovingBgItemId] = useState(null);
  const removeBackgroundForItem = async (item, isWishlist = false) => {
    if (!item.image_url) {
      toast({ title: 'No image', description: 'This item has no image to process.', variant: 'destructive' });
      return;
    }
    setRemovingBgItemId(item.id);
    try {
      // Fetch image and convert to base64
      const imgResponse = await fetch(item.image_url);
      const blob = await imgResponse.blob();
      const base64 = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(blob);
      });
      
      const response = await axios.post(`${API_URL}/real-wardrobe/remove-background`, {
        imageBase64: base64
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (response.data.success && response.data.processedImage) {
        const newImageUrl = response.data.processedImage;
        if (isWishlist) {
          await axios.put(`${API_URL}/real-wardrobe/wishlist/${item.id}`, {
            ...item, image_url: newImageUrl
          }, { headers: { Authorization: `Bearer ${token}` } });
          setWishlistItems(prev => prev.map(i => i.id === item.id ? { ...i, image_url: newImageUrl } : i));
        } else {
          await axios.put(`${API_URL}/real-wardrobe/items/${item.id}`, {
            image_url: newImageUrl
          }, { headers: { Authorization: `Bearer ${token}` } });
          setItems(prev => prev.map(i => i.id === item.id ? { ...i, image_url: newImageUrl } : i));
        }
        toast({ title: '✨ Background Removed', description: `${item.name} updated with clean background` });
      } else {
        toast({ title: 'Failed', description: 'Could not remove background. Try a different photo.', variant: 'destructive' });
      }
    } catch (error) {
      console.error('Item BG removal error:', error);
      toast({ title: 'Failed', description: 'Could not remove background.', variant: 'destructive' });
    } finally {
      setRemovingBgItemId(null);
    }
  };

  // Fallback client-side background removal
  const removeBackgroundFallback = () => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width; 
        canvas.height = img.height;
        const ctx = canvas.getContext('2d'); 
        ctx.drawImage(img, 0, 0);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Sample corner pixels for background color
        const corners = [
          { x: 5, y: 5 }, { x: canvas.width - 5, y: 5 }, 
          { x: 5, y: canvas.height - 5 }, { x: canvas.width - 5, y: canvas.height - 5 }
        ];
        let bgR = 0, bgG = 0, bgB = 0;
        corners.forEach(c => { 
          const idx = (c.y * canvas.width + c.x) * 4; 
          bgR += data[idx]; bgG += data[idx + 1]; bgB += data[idx + 2]; 
        });
        bgR /= 4; bgG /= 4; bgB /= 4;
        
        // Remove similar colors with improved threshold
        for (let i = 0; i < data.length; i += 4) {
          const dist = Math.sqrt(
            Math.pow(data[i] - bgR, 2) +
            Math.pow(data[i + 1] - bgG, 2) + 
            Math.pow(data[i + 2] - bgB, 2)
          );
          if (dist < 70) data[i + 3] = 0;
        }
        
        ctx.putImageData(imageData, 0, 0);
        setProcessedImage(canvas.toDataURL('image/png'));
        resolve();
      };
      img.src = capturedImage;
    });
  };

  // AI Analysis
  const analyzeWithAI = async () => {
    if (!capturedImage) {
      toast({
        title: "No image",
        description: "Please capture or upload an image first",
        variant: "destructive"
      });
      return;
    }
    setIsAnalyzing(true);
    
    try {
      const response = await axios.post(`${API_URL}/real-wardrobe/analyze`, {
        imageBase64: capturedImage.split(',')[1]
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (response.data.success && response.data.analysis) {
        const analysis = response.data.analysis;
        setAiAnalysis(analysis);
        
        // Auto-fill form with AI results
        setNewItem(prev => ({
          ...prev,
          name: analysis.name || prev.name,
          primaryCategory: analysis.primaryCategory || prev.primaryCategory,
          clothingType: analysis.clothingType || prev.clothingType,
          color: analysis.color || prev.color,
          material: analysis.material || prev.material,
          season: analysis.suggestedSeason || prev.season,
          occasions: analysis.suggestedOccasions || prev.occasions,
          fitType: analysis.fitType || prev.fitType,
          brand: analysis.estimatedBrand || analysis.detectedStore || prev.brand,
          price: analysis.detectedPrice || prev.price,
          size: analysis.detectedSize || prev.size
        }));
        
        const extras = [];
        if (analysis.detectedStore) extras.push(`Store: ${analysis.detectedStore}`);
        if (analysis.detectedPrice) extras.push(`Price: ${analysis.detectedPrice}`);
        toast({
          title: "✨ AI Detection Complete",
          description: `Detected: ${analysis.name || 'Clothing item'}${extras.length ? ' · ' + extras.join(' · ') : ''}`,
        });
      } else {
        const errorData = response.data.error;
        const errorMessage = typeof errorData === 'object' ? errorData.message : errorData;
        toast({
          title: "AI Detection",
          description: errorMessage || "Could not analyze the image. Please fill in the details manually.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('AI analysis error:', error);
      toast({
        title: "Analysis Failed",
        description: "Could not analyze the image. Please check your connection and try again.",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Bulk upload handlers
  const handleBulkFileSelect = async (e) => {
    const files = Array.from(e.target.files).slice(0, 15);
    const processFile = (file) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        // Compress to max 1200px JPEG 0.7 to prevent iOS memory crash
        const compressed = await compressImage(ev.target.result, 1200, 0.7);
        const base64 = compressed.split(',')[1];
        resolve({ file, preview: compressed, base64 });
      };
      reader.readAsDataURL(file);
    });
    const newImages = await Promise.all(files.map(processFile));
    setBulkImages(prev => [...prev, ...newImages].slice(0, 15));
  };

  const removeBulkImage = (index) => {
    setBulkImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleBulkUpload = async () => {
    if (bulkImages.length === 0) return;
    setBulkProcessing(true);
    setBulkProgress({ current: 0, total: bulkImages.length, status: 'Processing with AI... this may take a minute' });
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(`${API_URL}/real-wardrobe/bulk-add`, {
        images: bulkImages.map(img => img.base64)
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 600000, // 10 min timeout for bulk processing
        transformResponse: [(data) => {
          // Server sends keep-alive spaces during processing — trim before parsing
          try { return JSON.parse(typeof data === 'string' ? data.trim() : data); }
          catch (e) { return data; }
        }]
      });
      setBulkResults(resp.data);
      setBulkProgress({ current: resp.data.added, total: bulkImages.length, status: 'Complete!' });
      toast({ title: '✅ Bulk Upload Complete', description: resp.data.message });
      fetchWardrobe();
    } catch (err) {
      console.error('Bulk upload error:', err);
      const msg = err.response?.data?.reason || err.response?.data?.error || err.message;
      if (err.response?.status === 403) {
        toast({ title: '👑 Upgrade Required', description: msg, variant: 'destructive', action: <button onClick={() => navigate('/account')} className="text-xs underline font-semibold">Upgrade</button> });
      } else {
        toast({ title: '❌ Bulk Upload Failed', description: msg, variant: 'destructive' });
      }
    } finally {
      setBulkProcessing(false);
    }
  };

  const resetBulkUpload = () => {
    setBulkImages([]);
    setBulkResults(null);
    setBulkProgress({ current: 0, total: 0, status: '' });
  };

  // Add item
  const handleAddItem = async () => {
    console.log('🔵 ADD ITEM CLICKED - Starting add to wardrobe');
    
    if (!newItem.name) {
      toast({
        title: "Name required",
        description: "Please enter a name for your item",
        variant: "destructive"
      });
      return;
    }
    if (!newItem.primaryCategory) {
      toast({
        title: "Category required",
        description: "Please select a category for your item",
        variant: "destructive"
      });
      return;
    }
    
    setAddingItem(true);
    
    // Declare optimistic item ID outside try block so catch can access it
    let optimisticItemId = null;
    
    try {
      // Apply rotation to images if needed
      let finalOriginalImage = capturedImage || newItem.imageUrl;
      let finalProcessedImage = processedImage;
      
      if (imageRotation !== 0) {
        if (capturedImage) {
          finalOriginalImage = await applyRotationToImage(capturedImage, imageRotation);
        }
        if (processedImage) {
          finalProcessedImage = await applyRotationToImage(processedImage, imageRotation);
        }
      }
      
      const payload = {
        ...newItem,
        price: parseFloat(newItem.price) || 0,
        imageUrl: finalProcessedImage || finalOriginalImage,
        processedImageUrl: finalProcessedImage || null,
        originalImageUrl: finalOriginalImage,
        aiDetectedInfo: aiAnalysis
      };
      
      // Create optimistic item for instant UI feedback
      optimisticItemId = Date.now();
      const optimisticItem = {
        id: optimisticItemId,
        ...payload,
        // Add snake_case fields to match DB item shape (display code reads these)
        primary_category: payload.primaryCategory,
        clothing_type: payload.clothingType,
        purchase_price: payload.price,
        image_url: payload.imageUrl,
        original_image_url: payload.originalImageUrl,
        processed_image_url: payload.processedImageUrl,
        is_favorite: false,
        times_worn: 0,
        created_at: new Date().toISOString(),
      };
      
      // Show success immediately and close dialog
      toast({
        title: "✅ Adding to Wardrobe",
        description: `${newItem.name} is being saved...`,
      });
      
      // Add to UI optimistically — both items array and groupedItems
      setItems(prev => [optimisticItem, ...prev]);
      setGroupedItems(prev => {
        const cat = (payload.primaryCategory || '').toLowerCase();
        if (cat && prev[cat]) {
          return { ...prev, [cat]: { ...prev[cat], items: [optimisticItem, ...prev[cat].items], count: prev[cat].count + 1 } };
        }
        return prev;
      });
      
      // Close dialog and reset form immediately for snappy UX
      resetAddForm();
      setAddDialogOpen(false);
      setAddingItem(false);
      
      // Send to backend in background
      const response = await axios.post(`${API_URL}/real-wardrobe`, payload, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      console.log('✅ API Response:', response.data);
      
      // Replace optimistic item with real one from server
      const realItem = response.data.item;
      setItems(prev => prev.map(item => 
        item.id === optimisticItemId ? realItem : item
      ));
      setGroupedItems(prev => {
        const cat = (realItem.primary_category || payload.primaryCategory || '').toLowerCase();
        if (cat && prev[cat]) {
          return { ...prev, [cat]: { ...prev[cat], items: prev[cat].items.map(item => item.id === optimisticItemId ? realItem : item) } };
        }
        return prev;
      });
      
      toast({
        title: "✅ Saved!",
        description: `${newItem.name} is now in your wardrobe`,
      });
      
    } catch (error) {
      console.error('❌ ADD ITEM ERROR:', error);
      setAddingItem(false);
      
      // Remove optimistic item on error (use the saved ID)
      if (optimisticItemId) {
        setItems(prev => prev.filter(item => item.id !== optimisticItemId));
      }
      
      const errorData = error.response?.data?.error;
      const errorMessage = typeof errorData === 'object' ? errorData.message : errorData;
      toast({
        title: "Failed to Add Item",
        description: String(errorMessage || "Could not add item to wardrobe. Please try again."),
        variant: "destructive"
      });
    }
  };

  const resetAddForm = () => {
    setNewItem({
      name: '', primaryCategory: '', clothingType: '', brand: '', price: '',
      color: '', size: '', material: '', season: '', occasions: [],
      fitType: '', condition: 'New', notes: '', imageUrl: '', storageLocation: '', productUrl: ''
    });
    setCapturedImage(null);
    setProcessedImage(null);
    setAiAnalysis(null);
    setImageRotation(0);
    stopCamera();
  };

  // Item actions
  const handleLogWear = async (itemId) => {
    // Optimistic: increment times_worn in UI immediately
    const updateWorn = (item) => item.id === itemId ? { ...item, times_worn: (item.times_worn || 0) + 1, last_worn: new Date().toISOString() } : item;
    setItems(prev => prev.map(updateWorn));
    setGroupedItems(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated)) {
        if (updated[key]?.items) updated[key] = { ...updated[key], items: updated[key].items.map(updateWorn) };
      }
      return updated;
    });
    try {
      await axios.post(`${API_URL}/real-wardrobe/${itemId}/wear`, {}, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
    } catch (e) { console.error(e); fetchWardrobe(); }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this item from your wardrobe?')) return;
    // Optimistic: remove from UI immediately
    const prevItems = items;
    setItems(prev => prev.filter(i => i.id !== itemId));
    setGroupedItems(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated)) {
        if (updated[key]?.items) {
          const filtered = updated[key].items.filter(i => i.id !== itemId);
          updated[key] = { ...updated[key], items: filtered, count: filtered.length };
        }
      }
      return updated;
    });
    // Close detail view if this item was selected
    if (selectedItem?.id === itemId) setSelectedItem(null);
    toast({ title: '🗑️ Item removed' });
    try {
      await axios.delete(`${API_URL}/real-wardrobe/${itemId}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
    } catch (e) {
      console.error(e);
      // Revert on failure
      setItems(prevItems);
      fetchWardrobe();
      toast({ title: 'Delete failed', description: 'Could not delete item.', variant: 'destructive' });
    }
  };

  const openEditItem = (item) => {
    setEditingItemId(item.id);
    setEditingItem({
      ...item,
      image: item.image || item.image_url || item.processed_image_url || item.original_image_url
    });
    setEditImageRotation(0);
    setEditForm({
      name: item.name || '',
      primaryCategory: item.primary_category || item.category || '',
      clothingType: item.clothing_type || item.subcategory || '',
      brand: item.brand || '',
      price: item.purchase_price || item.price || '',
      color: item.color || '',
      size: item.size || '',
      material: item.material || '',
      season: item.season || '',
      fitType: item.fit_type || '',
      condition: item.condition || '',
      notes: item.notes || '',
      storageLocation: item.storage_location || ''
    });
    setShowEditDialog(true);
  };

  const handleUpdateItem = async () => {
    if (!editingItemId || isUpdatingItem) return;
    setIsUpdatingItem(true);
    try {
      // Prepare update data
      const updateData = {
        name: editForm.name,
        primary_category: editForm.primaryCategory,
        clothing_type: editForm.clothingType,
        brand: editForm.brand,
        purchase_price: editForm.price ? parseFloat(editForm.price) : null,
        color: editForm.color,
        size: editForm.size,
        material: editForm.material,
        season: editForm.season,
        fit_type: editForm.fitType,
        condition: editForm.condition,
        notes: editForm.notes,
        storage_location: editForm.storageLocation
      };
      
      // If rotation was applied, update the image
      if (editImageRotation !== 0 && editingItem?.image) {
        const rotatedImage = await applyRotationToImage(editingItem.image, editImageRotation);
        updateData.imageUrl = rotatedImage;
      }
      
      // Close dialog immediately for snappy UX
      const savedId = editingItemId;
      setShowEditDialog(false);
      setEditingItemId(null);
      setEditingItem(null);
      setEditImageRotation(0);

      // Optimistic: update item in UI immediately
      const updateInList = (item) => item.id === savedId ? {
        ...item, name: updateData.name, primary_category: updateData.primary_category,
        clothing_type: updateData.clothing_type, brand: updateData.brand,
        purchase_price: updateData.purchase_price, color: updateData.color,
        size: updateData.size, material: updateData.material, season: updateData.season,
        fit_type: updateData.fit_type, condition: updateData.condition, notes: updateData.notes,
        ...(updateData.imageUrl ? { image_url: updateData.imageUrl } : {})
      } : item;
      setItems(prev => prev.map(updateInList));
      setGroupedItems(prev => {
        const updated = { ...prev };
        for (const key of Object.keys(updated)) {
          if (updated[key]?.items) updated[key] = { ...updated[key], items: updated[key].items.map(updateInList) };
        }
        return updated;
      });

      await axios.put(`${API_URL}/real-wardrobe/${savedId}`, updateData, 
        { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Item updated', description: 'Your item details have been saved.' });
    } catch (e) {
      console.error(e);
      toast({ title: 'Update failed', description: 'Could not update item.', variant: 'destructive' });
      fetchWardrobe();
    } finally {
      setIsUpdatingItem(false);
    }
  };

  const handleToggleFavorite = async (itemId) => {
    // Helper to toggle is_favorite in groupedItems
    const toggleInGrouped = (prev) => {
      const updated = { ...prev };
      for (const key of Object.keys(updated)) {
        if (updated[key]?.items) {
          updated[key] = { ...updated[key], items: updated[key].items.map(i =>
            i.id === itemId ? { ...i, is_favorite: !i.is_favorite } : i
          )};
        }
      }
      return updated;
    };
    // Optimistic update — toggle immediately in UI
    setItems(prev => prev.map(i => 
      i.id === itemId ? { ...i, is_favorite: !i.is_favorite } : i
    ));
    setGroupedItems(toggleInGrouped);
    // Also update selectedItem if it's the one being toggled
    setSelectedItem(prev => prev && prev.id === itemId ? { ...prev, is_favorite: !prev.is_favorite } : prev);
    try {
      await axios.post(`${API_URL}/real-wardrobe/${itemId}/favorite`, {}, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
    } catch (e) { 
      console.error('Favorite toggle failed:', e);
      // Revert on failure
      setItems(prev => prev.map(i => 
        i.id === itemId ? { ...i, is_favorite: !i.is_favorite } : i
      ));
      setGroupedItems(toggleInGrouped);
      setSelectedItem(prev => prev && prev.id === itemId ? { ...prev, is_favorite: !prev.is_favorite } : prev);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0 || !window.confirm(`Delete ${selectedItems.length} items?`)) return;
    try {
      await axios.post(`${API_URL}/real-wardrobe/bulk-delete`, { ids: selectedItems }, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setSelectedItems([]);
      setSelectionMode(false);
      fetchWardrobe();
    } catch (e) { console.error(e); }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  // Outfit Builder
  const getItemOutfitCategory = (item) => {
    const cat = (item.primary_category || item.category || '').toLowerCase();
    const clothingType = (item.clothing_type || '').toLowerCase();
    if (cat === 'tops' || cat === 'top') return 'top';
    if (cat === 'bottoms' || cat === 'bottom') return 'bottom';
    if (cat === 'dresses' || cat === 'dress') return 'dress';
    if (cat === 'outerwear' || clothingType.includes('jacket') || clothingType.includes('coat')) return 'outerwear';
    if (cat === 'footwear' || cat === 'shoes' || clothingType.includes('shoe') || clothingType.includes('boot')) return 'shoes';
    if (clothingType.includes('bag') || item.name?.toLowerCase().includes('bag')) return 'bag';
    if (cat === 'accessories' || cat === 'accessory') return 'accessories';
    return 'top';
  };

  const addToOutfit = (item) => {
    setCurrentOutfit(p => {
      if (p.items.find(i => i.id === item.id)) return p;
      return { ...p, items: [...p.items, item] };
    });
  };

  const removeFromOutfit = (itemId) => {
    setCurrentOutfit(p => ({ ...p, items: p.items.filter(i => i.id !== itemId) }));
  };

  const isInCurrentOutfit = (itemId) => currentOutfit.items.some(i => i.id === itemId);

  const getOutfitItemsByCategory = (category) => {
    return currentOutfit.items.filter(item => getItemOutfitCategory(item) === category);
  };

  const openOutfitPicker = (category) => {
    setOutfitPickerCategory(category);
    setShowOutfitPickerDialog(true);
  };

  const saveOutfit = async () => {
    if (!currentOutfit.name || currentOutfit.items.length === 0 || savingOutfit) return;
    
    setSavingOutfit(true);
    try {
      const topItem = currentOutfit.items.find(i => getItemOutfitCategory(i) === 'top');
      const bottomItem = currentOutfit.items.find(i => getItemOutfitCategory(i) === 'bottom');
      const dressItem = currentOutfit.items.find(i => getItemOutfitCategory(i) === 'dress');
      const outerwearItem = currentOutfit.items.find(i => getItemOutfitCategory(i) === 'outerwear');
      const shoesItem = currentOutfit.items.find(i => getItemOutfitCategory(i) === 'shoes');
      const bagItem = currentOutfit.items.find(i => getItemOutfitCategory(i) === 'bag');
      const accItems = currentOutfit.items.filter(i => getItemOutfitCategory(i) === 'accessories');

      await axios.post(`${API_URL}/real-wardrobe/outfits`, {
        name: currentOutfit.name,
        topItemId: topItem?.id || null,
        bottomItemId: bottomItem?.id || null,
        dressItemId: dressItem?.id || null,
        outerwearItemId: outerwearItem?.id || null,
        shoesItemId: shoesItem?.id || null,
        bagItemId: bagItem?.id || null,
        accessoryIds: accItems.map(a => a.id)
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setCurrentOutfit({ name: '', items: [] });
      toast({ title: 'Outfit saved! ✨', description: `${currentOutfit.name} saved — find it in My Wardrobe → Outfit Builder tab & Saved ❤️ page` });
      fetchWardrobe();
    } catch (e) { console.error(e); toast({ title: 'Error', description: 'Failed to save outfit', variant: 'destructive' }); } finally { setSavingOutfit(false); }
  };

  const deleteOutfit = async (id) => {
    if (deletingOutfitId) return;
    setDeletingOutfitId(id);
    try {
      await axios.delete(`${API_URL}/real-wardrobe/outfits/${id}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      fetchWardrobe();
    } catch (e) { console.error(e); } finally { setDeletingOutfitId(null); }
  };

  // Generate outfit preview using virtual try-on
  const generateOutfitPreview = async (outfitId) => {
    // This would integrate with the virtual try-on service
    setOutfitPreviewLoading(true);
    try {
      // Get user's model photo
      const userModelRes = await axios.get(`${API_URL}/tryon/model`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      if (!userModelRes.data.hasModel) {
        alert('Please upload a model photo in Virtual Try-On first');
        return;
      }

      // Find the outfit
      const outfit = savedOutfits.find(o => o.id === outfitId);
      if (!outfit) return;

      // Use the top or dress for try-on
      const garmentImage = outfit.top_image || outfit.dress_image;
      if (!garmentImage) {
        alert('No top or dress to try on');
        return;
      }

      // Call virtual try-on
      const tryonRes = await axios.post(`${API_URL}/tryon`, {
        garmentImageUrl: garmentImage
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (tryonRes.data.success) {
        // Update outfit with preview
        await axios.put(`${API_URL}/real-wardrobe/outfits/${outfitId}/preview`, {
          previewImageUrl: tryonRes.data.resultImageUrl
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        fetchWardrobe();
      }
    } catch (error) {
      console.error('Error generating preview:', error);
    } finally {
      setOutfitPreviewLoading(false);
    }
  };

  // Get items by category for swapping
  const getItemsByCategory = (category) => {
    const cat = category.toLowerCase();
    return items.filter(item => {
      const itemCat = (item.primary_category || item.category || '').toLowerCase();
      const clothingType = (item.clothing_type || '').toLowerCase();
      
      if (cat === 'top' || cat === 'tops') return itemCat === 'tops' || itemCat === 'top';
      if (cat === 'bottom' || cat === 'bottoms') return itemCat === 'bottoms' || itemCat === 'bottom';
      if (cat === 'dress' || cat === 'dresses') return itemCat === 'dresses' || itemCat === 'dress';
      if (cat === 'outerwear') return itemCat === 'outerwear' || clothingType.includes('jacket') || clothingType.includes('coat');
      if (cat === 'shoes') return itemCat === 'footwear' || itemCat === 'shoes' || clothingType.includes('shoe') || clothingType.includes('boot');
      if (cat === 'bag') return clothingType.includes('bag') || item.name?.toLowerCase().includes('bag');
      return false;
    });
  };

  // Open outfit preview with a saved or current outfit
  const openOutfitPreview = (outfit) => {
    setPreviewOutfit(outfit);
    setShowOutfitPreview(true);
  };



  // Toggle favorite on saved outfit
  const toggleOutfitFavorite = async (outfitId) => {
    try {
      await axios.post(`${API_URL}/real-wardrobe/outfits/${outfitId}/favorite`, {}, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      fetchWardrobe();
      toast({ title: "Updated!", description: "Outfit favorite status updated" });
    } catch (e) { 
      console.error(e);
      // Update locally if API doesn't exist yet
      setSavedOutfits(prev => prev.map(o => 
        o.id === outfitId ? { ...o, is_favorite: !o.is_favorite } : o
      ));
    }
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const clearFilters = () => {
    setFilters({
      color: '', season: '', brand: '', occasion: '', 
      sortBy: 'created_at', sortOrder: 'DESC', favorite: false
    });
    setSearchQuery('');
  };

  // Open Virtual Try-On with a wardrobe item
  const canTryOn = (item) => {
    const cat = (item.primary_category || item.category || '').toLowerCase();
    return ['tops', 'bottoms', 'dresses', 'outerwear', 'sportswear'].includes(cat) && !!item.image_url;
  };

  const openTryOn = (item) => {
    const garmentData = {
      id: `wardrobe-${item.id}`,
      name: item.name,
      image: item.image_url || item.processed_image_url || item.original_image_url,
      category: item.primary_category || item.category?.toLowerCase() || 'tops',
      displayCategory: (item.primary_category || item.category || 'CLOTHING').toUpperCase(),
      color: item.color || 'Unknown',
      fromWardrobe: true
    };
    setTryOnItem(garmentData);
    setShowTryOn(true);
  };

  // ========== STYLE MAID AI AGENT ==========
  
  // Send message to Style Maid — accepts optional direct message for quick actions
  const sendStyleMaidMessage = async (e, directMessage) => {
    e?.preventDefault();
    const messageText = directMessage || styleMaidInput.trim();
    if (!messageText || styleMaidLoading) return;
    
    const userMessage = { role: 'user', content: messageText };
    setStyleMaidMessages(prev => [...prev, userMessage]);
    setStyleMaidInput('');
    setStyleMaidLoading(true);
    
    try {
      const res = await axios.post(`${API_URL}/style-maid/chat`, {
        message: messageText,
        history: styleMaidMessages
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (res.data.success) {
        setStyleMaidMessages(prev => [...prev, { 
          role: 'assistant', 
          content: res.data.response 
        }]);
      }
    } catch (error) {
      console.error('Style Maid error:', error);
      let errorContent;
      if (error.response?.status === 401) {
        errorContent = "Please log in to use Style Maid! 👗";
        setStyleMaidMessages(prev => [...prev, { role: 'assistant', content: errorContent }]);
      } else if (error.response?.status === 403) {
        errorContent = "You've reached your chat limit for this month. Upgrade your plan for more AI chats! ✨";
        setStyleMaidMessages(prev => [...prev, { 
          role: 'assistant', 
          content: errorContent,
          upgradeNeeded: true
        }]);
      } else {
        errorContent = "I'm having trouble right now. Please try again! 💫";
        setStyleMaidMessages(prev => [...prev, { 
          role: 'assistant', 
          content: errorContent
        }]);
      }
    } finally {
      setStyleMaidLoading(false);
    }
  };

  // Get AI outfit recommendations
  const getAiRecommendations = async () => {
    setStyleMaidLoading(true);
    try {
      const res = await axios.post(`${API_URL}/style-maid/recommend`, {
        ...recommendationCriteria,
        count: 3
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (res.data.success) {
        setAiRecommendations(res.data.recommendations || []);
        setShowRecommendations(true);
      }
    } catch (error) {
      console.error('AI recommendations error:', error);
      toast({ title: "Error", description: "Couldn't get recommendations", variant: "destructive" });
    } finally {
      setStyleMaidLoading(false);
    }
  };

  // Quick AI suggestion for outfit builder
  const getQuickAiSuggestion = async () => {
    setStyleMaidLoading(true);
    try {
      // Build query params with weather/color/event
      const params = new URLSearchParams();
      if (aiSuggestParams.temperature) params.append('temperature', aiSuggestParams.temperature);
      if (aiSuggestParams.colorPreference) params.append('colorPreference', aiSuggestParams.colorPreference);
      if (aiSuggestParams.upcomingEvent) params.append('upcomingEvent', aiSuggestParams.upcomingEvent);
      if (aiSuggestParams.dressCode) params.append('dressCode', aiSuggestParams.dressCode);
      
      const res = await axios.get(`${API_URL}/style-maid/quick-suggest${params.toString() ? '?' + params.toString() : ''}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      if (res.data.success && res.data.suggestion) {
        const suggestion = res.data.suggestion;
        const matchedItems = [];
        
        suggestion.items?.forEach(item => {
          const fullItem = items.find(i => i.id === item.id);
          if (fullItem && !matchedItems.find(m => m.id === fullItem.id)) {
            matchedItems.push(fullItem);
          }
        });
        
        setCurrentOutfit({ name: suggestion.name || 'AI Suggested Look', items: matchedItems });
        setShowOutfitBuilder(true);
        setWardrobeMode('builder');
        setShowAiSuggestDialog(false);
        toast({ title: "✨ AI Suggestion", description: suggestion.name || "Here's a look I put together!" });
      }
    } catch (error) {
      console.error('Quick suggest error:', error);
      if (error.response?.status === 403) {
        const data = error.response.data;
        const reason = typeof data.reason === 'string' ? data.reason : `You've used all ${data.limit || ''} AI suggests this month. Upgrade for more!`;
        toast({ title: '✨ AI Suggest limit reached', description: String(reason), action: <button onClick={() => navigate('/account')} className="text-xs underline font-semibold">Upgrade</button> });
      }
    } finally {
      setStyleMaidLoading(false);
    }
  };

  // Apply AI recommendation to outfit builder
  const applyRecommendation = (recommendation) => {
    const matchedItems = [];
    
    recommendation.items?.forEach(item => {
      // Try to find the full item in wardrobe, otherwise use the recommendation item directly
      const fullItem = items.find(i => i.id === item.id) || item;
      if (!matchedItems.find(m => m.id === fullItem.id)) {
        matchedItems.push(fullItem);
      }
    });
    
    setCurrentOutfit({ name: recommendation.name || '', items: matchedItems });
    setShowRecommendations(false);
    setShowOutfitBuilder(true);
    toast({ title: "Applied!", description: `"${recommendation.name}" is ready to customize` });
  };

  // ========== CAPSULE WARDROBE FUNCTIONS ==========
  
  const saveCapsule = async () => {
    if (!currentCapsule.name) {
      toast({ title: "Error", description: "Please enter a capsule name", variant: "destructive" });
      return;
    }
    if (savingCapsule) return;
    
    setSavingCapsule(true);
    const itemIds = currentCapsule.items.map(i => i.id);
    
    try {
      if (editingCapsule !== null) {
        const capsuleId = capsules[editingCapsule]?.id;
        if (capsuleId) {
          const res = await axios.put(`${API_URL}/real-wardrobe/capsules/${capsuleId}`, {
            name: currentCapsule.name,
            description: currentCapsule.description,
            type: currentCapsule.type,
            itemIds
          }, { headers: { Authorization: `Bearer ${token}` } });
          setCapsules(prev => prev.map((c, i) => i === editingCapsule ? res.data.capsule : c));
        }
      } else {
        const res = await axios.post(`${API_URL}/real-wardrobe/capsules`, {
          name: currentCapsule.name,
          description: currentCapsule.description,
          type: currentCapsule.type,
          itemIds
        }, { headers: { Authorization: `Bearer ${token}` } });
        setCapsules(prev => [...prev, res.data.capsule]);
      }
      toast({ title: "Capsule saved ✨", description: `Find "${currentCapsule.name}" in My Wardrobe → Capsules tab` });
      // Persist capsules to IndexedDB cache immediately so they survive refresh
      setCapsules(latest => { cacheGet('wardrobe_data').then(c => { if (c?.data) cacheSet('wardrobe_data', { ...c.data, capsules: latest }); }); return latest; });
    } catch (e) {
      console.error('Capsule save error:', e);
      toast({ 
        title: "Failed to Save Capsule", 
        description: "Could not save capsule. Please try again.",
        variant: "destructive"
      });
      return;
    }
    setCurrentCapsule({ name: '', description: '', type: 'seasonal', items: [] });
    setEditingCapsule(null);
    setShowCapsuleDialog(false);
    setSavingCapsule(false);
  };

  const deleteCapsule = async (id) => {
    if (deletingCapsuleId) return;
    setDeletingCapsuleId(id);
    try {
      await axios.delete(`${API_URL}/real-wardrobe/capsules/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { console.error('Capsule delete error:', e); }
    setCapsules(prev => prev.filter(c => c.id !== id));
    // Persist deletion to cache
    setCapsules(latest => { cacheGet('wardrobe_data').then(c => { if (c?.data) cacheSet('wardrobe_data', { ...c.data, capsules: latest }); }); return latest; });
    toast({ title: "Deleted", description: "Capsule removed" });
    setDeletingCapsuleId(null);
  };

  const addItemToCapsule = (item) => {
    if (!currentCapsule.items.find(i => i.id === item.id)) {
      setCurrentCapsule(prev => ({ ...prev, items: [...prev.items, item] }));
    }
  };

  // ========== WISHLIST FUNCTIONS ==========

  const moveWishlistItemToWardrobe = async (wishlistItem) => {
    if (movingToWardrobe) return;
    setMovingToWardrobe(wishlistItem.id);
    console.log('BOUGHT BUTTON CLICKED - Starting move to wardrobe');
    try {
      // Infer category from item name if no category was saved (e.g. item added without AI Detect)
      let inferredCategory = wishlistItem.primary_category || '';
      let inferredType = wishlistItem.clothing_type || wishlistItem.secondary_category || '';
      if (!inferredCategory && wishlistItem.name) {
        const n = wishlistItem.name.toLowerCase();
        if (/jacket|coat|blazer|cardigan|hoodie|parka|puffer|vest|gilet/i.test(n)) { inferredCategory = 'outerwear'; inferredType = inferredType || 'jacket'; }
        else if (/dress|gown|jumpsuit|romper/i.test(n)) { inferredCategory = 'dresses'; inferredType = inferredType || 'dress'; }
        else if (/jeans|pants|trousers|shorts|skirt|leggings/i.test(n)) { inferredCategory = 'bottoms'; inferredType = inferredType || 'pants'; }
        else if (/shoe|sneaker|boot|sandal|heel|loafer|trainer|slipper|mule/i.test(n)) { inferredCategory = 'footwear'; inferredType = inferredType || 'shoes'; }
        else if (/bag|purse|wallet|clutch|tote|backpack|handbag/i.test(n)) { inferredCategory = 'accessories'; inferredType = inferredType || 'bags'; }
        else if (/hat|scarf|belt|watch|sunglasses|glasses|jewelry|necklace|bracelet|earring|ring|tie/i.test(n)) { inferredCategory = 'accessories'; inferredType = inferredType || 'accessories'; }
        else if (/bikini|swimsuit|swim|trunks/i.test(n)) { inferredCategory = 'swimwear'; inferredType = inferredType || 'swimsuit'; }
        else if (/sports|athletic|gym|running|yoga|leotard/i.test(n)) { inferredCategory = 'sportswear'; inferredType = inferredType || 'athletic'; }
        else if (/shirt|top|blouse|t-shirt|tee|tank|sweater|polo|crop/i.test(n)) { inferredCategory = 'tops'; inferredType = inferredType || 'shirt'; }
      }

      // Create wardrobe item payload - field names must match backend destructuring
      const itemImage = wishlistItem.image_url || '';
      
      const wardrobePayload = {
        name: wishlistItem.name,
        brand: wishlistItem.brand || '',
        imageUrl: itemImage || undefined,
        primaryCategory: inferredCategory || 'tops',
        clothingType: inferredType || 'shirt',
        color: wishlistItem.color || 'multi',
        price: parseFloat(wishlistItem.price) || 0,
        notes: wishlistItem.notes || '',
        condition: 'New',
        purchaseDate: new Date().toISOString().split('T')[0]
      };

      console.log('Sending to API:', wardrobePayload);

      // Add to wardrobe
      const response = await axios.post(
        `${API_URL}/real-wardrobe`, 
        wardrobePayload, 
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('API Response:', response.data);

      if (response.data && response.data.item) {
        console.log('Item added successfully, now deleting from wishlist');
        
        // Remove from wishlist
        await axios.delete(
          `${API_URL}/real-wardrobe/wishlist/${wishlistItem.id}`, 
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log('Deleted from wishlist, updating UI');

        // Update local state
        setWishlistItems(prev => prev.filter(i => i.id !== wishlistItem.id));
        setItems(prev => [...prev, response.data.item]);

        console.log('UI updated successfully');

        toast({ 
          title: "🎉 Item Purchased!", 
          description: `${wishlistItem.name} has been added to your wardrobe` 
        });
      } else {
        console.error('No item in response');
        throw new Error('Failed to add item to wardrobe');
      }
    } catch (error) {
      console.error('❌ MOVE TO WARDROBE ERROR:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Ensure we always show a string, never an object
      const errorMsg = typeof error.response?.data?.error === 'string' 
        ? error.response.data.error
        : typeof error.response?.data?.message === 'string'
        ? error.response.data.message
        : error.message || "Failed to move item to wardrobe";
      
      toast({ 
        title: "Error", 
        description: String(errorMsg),
        variant: "destructive" 
      });
    } finally {
      setMovingToWardrobe(null);
    }
  };

  // ========== PACKING LIST FUNCTIONS ==========
  
  const savePackingList = async () => {
    if (!currentPackingList.name) {
      toast({ title: "Error", description: "Please enter a trip name", variant: "destructive" });
      return;
    }
    if (savingPacking) return;
    
    setSavingPacking(true);
    const itemIds = currentPackingList.items.map(i => i.id);
    
    try {
      if (editingPackingList !== null) {
        const listId = packingLists[editingPackingList]?.id;
        if (listId) {
          const res = await axios.put(`${API_URL}/real-wardrobe/packing-lists/${listId}`, {
            name: currentPackingList.name,
            destination: currentPackingList.destination,
            startDate: currentPackingList.startDate,
            endDate: currentPackingList.endDate,
            itemIds,
            packedItems: currentPackingList.packedItems,
            coverImage: currentPackingList.coverImage || undefined
          }, { headers: { Authorization: `Bearer ${token}` } });
          setPackingLists(prev => sortPackingLists(prev.map((p, i) => i === editingPackingList ? res.data.packingList : p)));
          if (viewingPackingList?.id === listId) setViewingPackingList(res.data.packingList);
        }
      } else {
        const res = await axios.post(`${API_URL}/real-wardrobe/packing-lists`, {
          name: currentPackingList.name,
          destination: currentPackingList.destination,
          startDate: currentPackingList.startDate,
          endDate: currentPackingList.endDate,
          itemIds,
          packedItems: currentPackingList.packedItems,
          coverImage: currentPackingList.coverImage || undefined
        }, { headers: { Authorization: `Bearer ${token}` } });
        setPackingLists(prev => sortPackingLists([...prev, res.data.packingList]));
      }
      toast({ title: "Success!", description: "Packing list saved" });
    } catch (e) {
      console.error('Packing list save error:', e);
      toast({ title: "Failed to Save", description: "Could not save packing list. Please try again.", variant: "destructive" });
      return;
    }
    setCurrentPackingList({ name: '', destination: '', startDate: '', endDate: '', items: [], packedItems: [], coverImage: '' });
    setEditingPackingList(null);
    setShowPackingDialog(false);
    setSavingPacking(false);
  };

  const deletePackingList = async (id) => {
    if (deletingPackingId) return;
    setDeletingPackingId(id);
    try {
      await axios.delete(`${API_URL}/real-wardrobe/packing-lists/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { console.error('Packing list delete error:', e); }
    setPackingLists(prev => prev.filter(p => p.id !== id));
    toast({ title: "Deleted", description: "Packing list removed" });
    setDeletingPackingId(null);
  };

  const updatePackingCover = async (listId, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const list = packingLists.find(p => p.id === listId) || viewingPackingList;
        if (!list) return;
        const res = await axios.put(`${API_URL}/real-wardrobe/packing-lists/${listId}`, {
          name: list.name,
          destination: list.destination,
          startDate: list.startDate || list.start_date,
          endDate: list.endDate || list.end_date,
          packedItems: list.packedItems || list.packed_items || [],
          coverImage: ev.target.result
        }, { headers: { Authorization: `Bearer ${token}` } });
        const updated = res.data.packingList;
        setPackingLists(prev => sortPackingLists(prev.map(p => p.id === listId ? updated : p)));
        if (viewingPackingList?.id === listId) setViewingPackingList(updated);
        toast({ title: "Cover photo updated!" });
      } catch (err) {
        console.error('Cover update error:', err);
        toast({ title: "Failed to update cover", variant: "destructive" });
      }
    };
    reader.readAsDataURL(file);
  };

  const togglePackedItem = async (listId, itemId) => {
    const list = packingLists.find(l => l.id === listId);
    if (!list) return;
    const newPackedItems = list.packedItems.includes(itemId)
      ? list.packedItems.filter(id => id !== itemId)
      : [...list.packedItems, itemId];
    
    setPackingLists(prev => prev.map(l => 
      l.id === listId ? { ...l, packedItems: newPackedItems } : l
    ));
    
    try {
      await axios.patch(`${API_URL}/real-wardrobe/packing-lists/${listId}/packed`, {
        packedItems: newPackedItems
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { console.error('Toggle packed error:', e); }
  };

  // ========== CALENDAR FUNCTIONS ==========
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty days for padding
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add days of month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const formatDateKey = (date) => date.toISOString().split('T')[0];

  const logOutfitToCalendar = (date, outfit) => {
    const key = formatDateKey(date);
    setOutfitCalendar(prev => ({
      ...prev,
      [key]: outfit
    }));
    setShowCalendarOutfitDialog(false);
    toast({ title: "Logged!", description: `Outfit logged for ${date.toLocaleDateString()}` });
  };

  // Time-of-day config
  const TIME_OF_DAY_OPTIONS = [
    { value: 'morning', label: 'Morning', icon: Sunrise, color: 'from-amber-400 to-yellow-300', textColor: 'text-amber-600', bgColor: 'bg-amber-50' },
    { value: 'afternoon', label: 'Afternoon', icon: Sun, color: 'from-orange-400 to-amber-300', textColor: 'text-orange-600', bgColor: 'bg-orange-50' },
    { value: 'evening', label: 'Evening', icon: Sunset, color: 'from-rose-400 to-orange-300', textColor: 'text-rose-600', bgColor: 'bg-rose-50' },
    { value: 'night', label: 'Night', icon: Moon, color: 'from-indigo-500 to-purple-400', textColor: 'text-indigo-600', bgColor: 'bg-indigo-50' },
    { value: 'allday', label: 'All Day', icon: Clock, color: 'from-olive-500 to-olive-400', textColor: 'text-olive-600', bgColor: 'bg-olive-50' },
  ];

  const getTimeOption = (value) => TIME_OF_DAY_OPTIONS.find(o => o.value === value) || TIME_OF_DAY_OPTIONS[4];

  const openCalendarDate = (day, slotToEdit = null) => {
    setSelectedCalendarDate(day);
    if (slotToEdit) {
      // Editing an existing slot
      setEditingSlotId(slotToEdit.id);
      setCalendarOutfitItems(slotToEdit.items || []);
      setCalendarOutfitNote(slotToEdit.note || '');
      setCalendarOutfitTimeOfDay(slotToEdit.timeOfDay || 'morning');
      setCalendarOutfitOccasion(slotToEdit.occasion || '');
    } else {
      // Adding a new slot
      setEditingSlotId(null);
      setCalendarOutfitItems([]);
      setCalendarOutfitNote('');
      setCalendarOutfitTimeOfDay('morning');
      setCalendarOutfitOccasion('');
    }
    setShowCalendarOutfitDialog(true);
  };

  const saveCalendarOutfit = () => {
    if (!selectedCalendarDate) return;
    if (calendarOutfitItems.length === 0) {
      toast({ title: "Error", description: "Select at least one item", variant: "destructive" });
      return;
    }
    const key = formatDateKey(selectedCalendarDate);
    const newSlot = {
      id: editingSlotId || `slot-${Date.now()}`,
      timeOfDay: calendarOutfitTimeOfDay,
      occasion: calendarOutfitOccasion,
      items: calendarOutfitItems,
      note: calendarOutfitNote,
      savedAt: new Date().toISOString()
    };

    setOutfitCalendar(prev => {
      const dayData = prev[key] || { slots: [] };
      // Migrate old format (items at top level) to new slots format
      let existingSlots = dayData.slots || [];
      if (!dayData.slots && dayData.items) {
        existingSlots = [{ id: 'migrated-1', timeOfDay: 'allday', occasion: '', items: dayData.items, note: dayData.note || '', savedAt: dayData.savedAt || new Date().toISOString() }];
      }
      
      let updatedSlots;
      if (editingSlotId) {
        updatedSlots = existingSlots.map(s => s.id === editingSlotId ? newSlot : s);
      } else {
        updatedSlots = [...existingSlots, newSlot];
      }
      // Sort slots by time of day order
      const timeOrder = ['morning', 'afternoon', 'evening', 'night', 'allday'];
      updatedSlots.sort((a, b) => timeOrder.indexOf(a.timeOfDay) - timeOrder.indexOf(b.timeOfDay));
      
      return { ...prev, [key]: { slots: updatedSlots } };
    });
    setShowCalendarOutfitDialog(false);
    setSelectedCalendarDate(null);
    setEditingSlotId(null);
    toast({ title: "Saved!", description: `${getTimeOption(calendarOutfitTimeOfDay).label} outfit logged for ${selectedCalendarDate.toLocaleDateString()}` });
  };

  const removeCalendarSlot = (dateKey, slotId) => {
    setOutfitCalendar(prev => {
      const dayData = prev[dateKey];
      if (!dayData) return prev;
      const slots = (dayData.slots || []).filter(s => s.id !== slotId);
      if (slots.length === 0) {
        const updated = { ...prev };
        delete updated[dateKey];
        return updated;
      }
      return { ...prev, [dateKey]: { slots } };
    });
    toast({ title: "Removed", description: "Outfit removed from calendar" });
  };

  const removeCalendarOutfit = () => {
    if (!selectedCalendarDate) return;
    const key = formatDateKey(selectedCalendarDate);
    setOutfitCalendar(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
    setShowCalendarOutfitDialog(false);
    setSelectedCalendarDate(null);
    toast({ title: "Removed", description: "All outfits removed from this date" });
  };

  // Helper to get all slots for a date (handles old format migration)
  const getDaySlots = (dayData) => {
    if (!dayData) return [];
    if (dayData.slots) return dayData.slots;
    // Migrate old single-outfit format
    if (dayData.items) {
      return [{ id: 'migrated-1', timeOfDay: 'allday', occasion: '', items: dayData.items, note: dayData.note || '', savedAt: dayData.savedAt || '' }];
    }
    return [];
  };

  // Get total items count for a day across all slots
  const getDayItemCount = (dayData) => {
    const slots = getDaySlots(dayData);
    return slots.reduce((sum, s) => sum + (s.items?.length || 0), 0);
  };

  // ========== PARTNER & FRIENDS WARDROBE FUNCTIONS ==========

  const fetchPartners = async (type = 'partner') => {
    setLoadingPartners(true);
    try {
      const res = await axios.get(`${API_URL}/partner-wardrobe/partners?type=${type}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (type === 'friend') {
        setFriends(res.data.partners || []);
      } else {
        setPartners(res.data.partners || []);
      }
    } catch (err) {
      console.error('Failed to fetch partners:', err);
    } finally {
      setLoadingPartners(false);
    }
  };

  const generatePartnerCode = async (type = 'partner') => {
    try {
      const res = await axios.post(`${API_URL}/partner-wardrobe/generate-code`, { type }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartnerInviteCode(res.data.code);
      const label = type === 'friend' ? 'friend' : 'partner';
      toast({ title: "Code generated!", description: `Share this code with your ${label}` });
    } catch (err) {
      toast({ title: "Error", description: "Failed to generate code", variant: "destructive" });
    }
  };

  const acceptPartnerCode = async (type = 'partner') => {
    if (!partnerCodeInput.trim() || partnerCodeInput.trim().length !== 6) {
      toast({ title: "Invalid code", description: "Enter a 6-character invite code", variant: "destructive" });
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/partner-wardrobe/accept-code`, {
        code: partnerCodeInput.trim().toUpperCase()
      }, { headers: { Authorization: `Bearer ${token}` } });
      const label = type === 'friend' ? 'friend' : 'partner';
      const emoji = type === 'friend' ? '🤝' : '💕';
      toast({ title: `Linked! ${emoji}`, description: `You're now connected with ${res.data.partnership.partnerName}` });
      setPartnerCodeInput('');
      fetchPartners(type);
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.error || "Invalid or expired code", variant: "destructive" });
    }
  };

  const fetchPartnerItems = async (partnerId) => {
    setLoadingPartnerItems(true);
    try {
      const res = await axios.get(`${API_URL}/partner-wardrobe/partner/${partnerId}/items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartnerItems(res.data.items || []);
    } catch (err) {
      toast({ title: "Error", description: "Failed to load partner's wardrobe", variant: "destructive" });
    } finally {
      setLoadingPartnerItems(false);
    }
  };

  const togglePartnerItemSelection = (itemId) => {
    setPartnerSelectedItems(prev => 
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const sendOutfitSuggestion = async () => {
    if (!activePartner || partnerSelectedItems.length === 0) {
      toast({ title: "Select items", description: "Pick at least one item to suggest", variant: "destructive" });
      return;
    }
    try {
      await axios.post(`${API_URL}/partner-wardrobe/suggestions`, {
        partnerId: activePartner.linked_user_id,
        itemIds: partnerSelectedItems,
        note: partnerSuggestionNote,
        occasion: partnerSuggestionOccasion
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Sent! 💌", description: `Outfit suggestion sent to ${activePartner.display_name}` });
      setPartnerSelectedItems([]);
      setPartnerSuggestionNote('');
      setPartnerSuggestionOccasion('');
    } catch (err) {
      toast({ title: "Error", description: "Failed to send suggestion", variant: "destructive" });
    }
  };

  const fetchReceivedSuggestions = async () => {
    try {
      const res = await axios.get(`${API_URL}/partner-wardrobe/suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceivedSuggestions(res.data.suggestions || []);
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
    }
  };

  const fetchSentSuggestions = async () => {
    try {
      const res = await axios.get(`${API_URL}/partner-wardrobe/suggestions/sent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSentSuggestions(res.data.suggestions || []);
    } catch (err) {
      console.error('Failed to fetch sent suggestions:', err);
    }
  };

  const respondToSuggestion = async (suggestionId, status) => {
    try {
      await axios.put(`${API_URL}/partner-wardrobe/suggestions/${suggestionId}`, {
        status
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ 
        title: status === 'accepted' ? "Outfit accepted! ✅" : "Suggestion declined",
        description: status === 'accepted' ? "Great choice!" : "Suggestion removed"
      });
      fetchReceivedSuggestions();
    } catch (err) {
      toast({ title: "Error", description: "Failed to update suggestion", variant: "destructive" });
    }
  };

  const unlinkPartner = async (partnerId, type = 'partner') => {
    try {
      await axios.delete(`${API_URL}/partner-wardrobe/partner/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const label = type === 'friend' ? 'Friend' : 'Partner';
      toast({ title: "Unlinked", description: `${label} access removed` });
      setActivePartner(null);
      setPartnerItems([]);
      fetchPartners(type);
    } catch (err) {
      toast({ title: "Error", description: "Failed to unlink", variant: "destructive" });
    }
  };

  const openPartnerWardrobe = (partner, dialogType = 'partner') => {
    setActivePartner(partner);
    setPartnerDialogType(dialogType);
    setPartnerDialogTab('browse');
    setPartnerSelectedItems([]);
    setPartnerItemFilter('all');
    fetchPartnerItems(partner.linked_user_id);
  };

  // ========== LOOKBOOK FUNCTIONS ==========
  
  const fetchUserLookbooks = async () => {
    setLoadingLookbooks(true);
    try {
      const res = await axios.get(`${API_URL}/lookbooks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserLookbooks(res.data.lookbooks || res.data || []);
    } catch (err) {
      console.error('Failed to fetch lookbooks:', err);
      setUserLookbooks([]);
    } finally {
      setLoadingLookbooks(false);
    }
  };

  const openAddToLookbook = (item) => {
    setLookbookItemToAdd(item);
    setShowLookbookDialog(true);
    fetchUserLookbooks();
  };

  const createLookbookAndAddItem = async () => {
    if (!newLookbookName.trim() || !lookbookItemToAdd) return;
    try {
      const createRes = await axios.post(`${API_URL}/lookbooks`, {
        name: newLookbookName.trim(),
        style: 'casual'
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      const lookbookId = createRes.data.lookbook.id;
      await axios.post(`${API_URL}/lookbooks/${lookbookId}/items/from-wardrobe`, {
        wardrobeItemId: lookbookItemToAdd.id
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast({ title: "Added to Stylebook!", description: `"${lookbookItemToAdd.name}" added to "${newLookbookName}"` });
      setNewLookbookName('');
      setShowLookbookDialog(false);
      setLookbookItemToAdd(null);
    } catch (err) {
      const errData = err.response?.data?.error;
      const errMsg = typeof errData === 'object' ? (errData.message || 'Failed to add to stylebook') : (errData || 'Failed to add to stylebook');
      toast({ title: "Error", description: errMsg, variant: "destructive" });
    }
  };

  const addItemToExistingLookbook = async (lookbookId, lookbookName) => {
    if (!lookbookItemToAdd) return;
    try {
      await axios.post(`${API_URL}/lookbooks/${lookbookId}/items/from-wardrobe`, {
        wardrobeItemId: lookbookItemToAdd.id
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast({ title: "Added to Stylebook!", description: `"${lookbookItemToAdd.name}" added to "${lookbookName}"` });
      setShowLookbookDialog(false);
      setLookbookItemToAdd(null);
    } catch (err) {
      const errData = err.response?.data?.error;
      const errMsg = typeof errData === 'object' ? (errData.message || 'Failed to add to stylebook') : (errData || 'Failed to add to stylebook');
      toast({ title: "Error", description: errMsg, variant: "destructive" });
    }
  };



  // ========== ANALYTICS FUNCTIONS ==========
  
  const calculateAnalytics = useCallback(() => {
    if (!items.length) return;
    
    // Most worn items
    const mostWorn = [...items]
      .filter(i => i.times_worn > 0)
      .sort((a, b) => (b.times_worn || 0) - (a.times_worn || 0))
      .slice(0, 5);
    
    // Least worn items
    const leastWorn = [...items]
      .sort((a, b) => (a.times_worn || 0) - (b.times_worn || 0))
      .slice(0, 5);
    
    // Category breakdown
    const categoryBreakdown = Object.entries(groupedItems).map(([cat, data]) => ({
      category: data.name,
      count: data.items.length,
      value: data.items.reduce((sum, i) => sum + parseFloat(i.purchase_price || 0), 0)
    }));
    
    // Color distribution
    const colorCounts = {};
    items.forEach(item => {
      const color = item.color || 'Unknown';
      colorCounts[color] = (colorCounts[color] || 0) + 1;
    });
    const colorDistribution = Object.entries(colorCounts)
      .map(([color, count]) => ({ color, count }))
      .sort((a, b) => b.count - a.count);
    
    // Value analysis
    const totalValue = items.reduce((sum, i) => sum + parseFloat(i.purchase_price || 0), 0);
    const avgItemValue = items.length ? totalValue / items.length : 0;
    const totalWears = items.reduce((sum, i) => sum + (i.times_worn || 0), 0);
    const avgCostPerWear = totalWears > 0 ? totalValue / totalWears : totalValue;
    
    setAnalyticsData({
      mostWorn,
      leastWorn,
      categoryBreakdown,
      colorDistribution,
      valueAnalysis: { totalValue, avgItemValue, totalWears, avgCostPerWear },
      wearFrequency: items.map(i => ({ name: i.name, wears: i.times_worn || 0 })).slice(0, 10),
      costPerWearTrend: items.map(i => ({ 
        name: i.name, 
        cpw: i.times_worn > 0 ? (i.purchase_price / i.times_worn) : i.purchase_price 
      })).slice(0, 10),
      seasonalAnalysis: []
    });
  }, [items, groupedItems]);

  useEffect(() => {
    if (wardrobeMode === 'analytics') {
      calculateAnalytics();
    }
  }, [wardrobeMode, calculateAnalytics]);

  // Fetch partner/friends data when switching to those tabs
  useEffect(() => {
    if (wardrobeMode === 'partner' && token) {
      fetchPartners('partner');
      fetchReceivedSuggestions();
    }
    if (wardrobeMode === 'friends' && token) {
      fetchPartners('friend');
      fetchReceivedSuggestions();
    }
  }, [wardrobeMode]);

  // ========== INSPIRATION BOARDS FUNCTIONS ==========

  // Load inspiration boards from API (with one-time localStorage migration)
  useEffect(() => {
    const loadBoards = async () => {
      if (!token || inspirationBoardsLoaded) return;
      try {
        // Check for localStorage data to migrate
        const localData = localStorage.getItem('inspirationBoards');
        if (localData) {
          try {
            const localBoards = JSON.parse(localData);
            if (localBoards.length > 0) {
              console.log(`🔄 Migrating ${localBoards.length} inspiration boards to cloud...`);
              await axios.post(`${API_URL}/inspiration-boards/migrate`, { boards: localBoards }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              console.log('✅ Inspiration boards migrated to cloud');
            }
          } catch (migErr) {
            console.warn('⚠️ Migration failed, data preserved locally:', migErr.message);
          }
          localStorage.removeItem('inspirationBoards');
        }

        // Fetch from API
        const res = await axios.get(`${API_URL}/inspiration-boards`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInspirationBoards(res.data.boards || []);
      } catch (err) {
        console.error('Failed to load inspiration boards:', err);
      } finally {
        setInspirationBoardsLoaded(true);
      }
    };
    loadBoards();
  }, [token, inspirationBoardsLoaded]);
  
  const saveBoard = async () => {
    if (!currentBoard.name) {
      toast({ title: "Error", description: "Please enter a board name", variant: "destructive" });
      return;
    }
    try {
      if (editingBoardId) {
        // Update existing board via API
        await axios.put(`${API_URL}/inspiration-boards/${editingBoardId}`, {
          name: currentBoard.name,
          description: currentBoard.description || ''
        }, { headers: { Authorization: `Bearer ${token}` } });
        setInspirationBoards(prev => prev.map(b => 
          b.id === editingBoardId ? { ...b, name: currentBoard.name, description: currentBoard.description || '' } : b
        ));
        setEditingBoardId(null);
        toast({ title: "Updated!", description: "Board details saved" });
      } else {
        // Create new board via API
        const res = await axios.post(`${API_URL}/inspiration-boards`, {
          name: currentBoard.name,
          description: currentBoard.description || ''
        }, { headers: { Authorization: `Bearer ${token}` } });
        setInspirationBoards(prev => [...prev, res.data.board]);
        toast({ title: "Success!", description: "Inspiration board created" });
      }
    } catch (err) {
      console.error('Failed to save board:', err);
      toast({ title: "Error", description: "Failed to save board", variant: "destructive" });
    }
    setCurrentBoard({ name: '', description: '', inspiration: [] });
    setShowBoardDialog(false);
  };

  const deleteBoard = async (boardId) => {
    try {
      await axios.delete(`${API_URL}/inspiration-boards/${boardId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspirationBoards(prev => prev.filter(b => b.id !== boardId));
      if (viewingBoardId === boardId) setViewingBoardId(null);
      toast({ title: "Deleted", description: "Board removed" });
    } catch (err) {
      console.error('Failed to delete board:', err);
      toast({ title: "Error", description: "Failed to delete board", variant: "destructive" });
    }
  };

  const addImageToBoard = async (boardId) => {
    if (!boardImageUrl.trim()) {
      toast({ title: "Error", description: "Please enter an image URL or upload an image", variant: "destructive" });
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/inspiration-boards/${boardId}/pins`, {
        imageUrl: boardImageUrl.trim(),
        note: boardImageNote.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      setInspirationBoards(prev => prev.map(b => 
        b.id === boardId 
          ? { ...b, inspiration: [...(b.inspiration || []), res.data.pin] }
          : b
      ));
      setBoardImageUrl('');
      setBoardImageNote('');
      toast({ title: "Added!", description: "Pin added to board" });
    } catch (err) {
      console.error('Failed to add pin:', err);
      toast({ title: "Error", description: "Failed to add pin", variant: "destructive" });
    }
  };

  const addUploadedImageToBoard = (boardId, file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const res = await axios.post(`${API_URL}/inspiration-boards/${boardId}/pins`, {
          imageUrl: e.target.result,
          note: boardImageNote.trim() || file.name
        }, { headers: { Authorization: `Bearer ${token}` } });
        setInspirationBoards(prev => prev.map(b => 
          b.id === boardId 
            ? { ...b, inspiration: [...(b.inspiration || []), res.data.pin] }
            : b
        ));
        setBoardImageUrl('');
        setBoardImageNote('');
        toast({ title: "Added!", description: "Image pinned to board" });
      } catch (err) {
        console.error('Failed to upload pin:', err);
        toast({ title: "Error", description: "Failed to upload image", variant: "destructive" });
      }
    };
    reader.readAsDataURL(file);
  };

  const removePinFromBoard = async (boardId, pinId) => {
    try {
      await axios.delete(`${API_URL}/inspiration-boards/${boardId}/pins/${pinId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspirationBoards(prev => prev.map(b => 
        b.id === boardId 
          ? { ...b, inspiration: b.inspiration.filter(p => p.id !== pinId) }
          : b
      ));
      toast({ title: "Removed", description: "Pin removed from board" });
    } catch (err) {
      console.error('Failed to remove pin:', err);
      toast({ title: "Error", description: "Failed to remove pin", variant: "destructive" });
    }
  };

  const addToBoard = async (boardId, inspiration) => {
    try {
      const res = await axios.post(`${API_URL}/inspiration-boards/${boardId}/pins`, {
        imageUrl: inspiration.imageUrl,
        note: inspiration.note || ''
      }, { headers: { Authorization: `Bearer ${token}` } });
      setInspirationBoards(prev => prev.map(b => 
        b.id === boardId 
          ? { ...b, inspiration: [...(b.inspiration || []), res.data.pin] }
          : b
      ));
    } catch (err) {
      console.error('Failed to add to board:', err);
    }
  };

  const totalValue = (items || []).reduce((sum, item) => sum + parseFloat(item.purchase_price || item.price || 0), 0);
  const activeFiltersCount = Object.values(filters).filter(v => v && v !== 'created_at' && v !== 'DESC').length;

  // Not logged in
  if (!token) {
    return (
      <div className="min-h-screen py-20 px-6 flex items-center justify-center">
        <div className="text-center glass-card p-8 rounded-2xl max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center mx-auto mb-6">
            <Shirt className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-light mb-3">
            Your <span className="italic bg-gradient-to-r from-suede-600 to-suede-500 bg-clip-text text-transparent">Virtual Wardrobe</span>
          </h2>
          <p className="text-gray-500 mb-6">
            Photograph your real clothes, organize them by category, and create perfect outfit combinations
          </p>
          <a href="/login">
            <Button className="bg-gradient-to-r from-suede-600 to-suede-500 text-white rounded-2xl px-8 py-3">
              Sign In to Start
            </Button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-6 bg-[#faf9f7] overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Magazine Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-10 gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.45em] text-stone-400 mb-3 font-medium">Your Personal</p>
            <h1 className="text-5xl md:text-6xl font-extralight mb-3 text-stone-800" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
              Virtual <span className="italic text-stone-500">Wardrobe</span>
            </h1>
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-stone-400">
              <span>{items.length} pieces</span>
              <span className="w-1 h-1 rounded-full bg-stone-300" />
              <span>{cs()}{totalValue.toLocaleString()} value</span>
              <span className="w-1 h-1 rounded-full bg-stone-300" />
              <span>{savedOutfits.length} outfits</span>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {/* Contextual Action Buttons — show different tools per tab */}
            
            {/* Partner / Friends Wardrobe */}
            {['owned', 'favorites'].includes(wardrobeMode) && (
            <Button 
              variant="outline" 
              onClick={() => { 
                setPartnerDialogType('partner');
                setPartnerDialogOpen(true); 
                fetchPartners('partner'); 
                fetchReceivedSuggestions(); 
              }} 
              className="rounded-full border-stone-200 text-stone-500 hover:text-stone-700 hover:border-stone-300 h-10 w-10 p-0"
              title="Partner Wardrobe"
            >
              <HeartHandshake className="w-4 h-4" />
            </Button>
            )}
            
            {/* Select — My Clothes, Favorites, Capsules, Packing */}
            {['owned', 'favorites', 'capsules', 'packing'].includes(wardrobeMode) && (
            <Button 
              variant={selectionMode ? "default" : "outline"} 
              onClick={() => { setSelectionMode(!selectionMode); setSelectedItems([]); }} 
              className={selectionMode ? "bg-stone-800 text-white rounded-full" : "rounded-full border-stone-200 text-stone-500 hover:text-stone-700"}
            >
              <Check className="w-4 h-4 mr-2" />
              {selectionMode ? `${selectedItems.length} Selected` : 'Select'}
            </Button>
            )}
            
            {/* Add Clothing — My Clothes only */}
            {wardrobeMode === 'owned' && (
            <Dialog open={addDialogOpen} onOpenChange={(open) => { setAddDialogOpen(open); if (!open) resetAddForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-stone-800 hover:bg-stone-900 text-white rounded-full px-6">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Clothing
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-light flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center">
                      <Plus className="w-5 h-5 text-white" />
                    </div>
                    Add to Your Wardrobe
                  </DialogTitle>
                  <DialogDescription>
                    Photograph or upload an image of your clothing item. Our AI will help identify and categorize it.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-6 py-4">
                  {/* Image Capture Section */}
                  {!showCamera && !capturedImage && (
                    <>
                      {/* Photo Tips Banner */}
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <Lightbulb className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-amber-800 mb-1">Tips for Best Results</h4>
                            <ul className="text-sm text-amber-700 space-y-1">
                              <li className="flex items-center gap-2">
                                <Check className="w-3 h-3 text-amber-600" />
                                <span><strong>Hang your clothing</strong> on a hanger or lay it flat</span>
                              </li>
                              <li className="flex items-center gap-2">
                                <Check className="w-3 h-3 text-amber-600" />
                                <span><strong>Good lighting</strong> – natural daylight works best</span>
                              </li>
                              <li className="flex items-center gap-2">
                                <Check className="w-3 h-3 text-amber-600" />
                                <span><strong>Plain background</strong> helps AI identify colors accurately</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                      <Button 
                        variant="outline" 
                        className="h-36 border-dashed border-2 rounded-2xl flex-col" 
                        onClick={startCamera}
                      >
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center mb-2">
                          <Camera className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-sm font-medium">Take Photo</span>
                        <span className="text-xs text-gray-400">Use your camera</span>
                      </Button>
                      
                      <label className="h-36 border-dashed border-2 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-suede-400 transition-colors">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center mb-2">
                          <Upload className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-sm font-medium">Upload Image</span>
                        <span className="text-xs text-gray-400">From your device</span>
                        <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                      </label>
                    </div>
                    </>
                  )}
                  
                  {/* Camera View */}
                  {showCamera && !capturedImage && (
                    <div className="relative rounded-2xl overflow-hidden">
                      <video ref={videoRef} autoPlay playsInline className="w-full rounded-xl" />
                      <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
                        <Button onClick={capturePhoto} className="bg-white text-gray-900 rounded-full px-8">
                          <Camera className="w-5 h-5 mr-2" />
                          Capture
                        </Button>
                        <Button variant="outline" onClick={stopCamera} className="bg-white/80 rounded-full">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Captured Image */}
                  {capturedImage && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500 mb-2 flex items-center gap-1">
                            <ImageIcon className="w-4 h-4" /> Original
                          </p>
                          <div className="rounded-xl w-full aspect-[3/4] bg-gray-50 flex items-center justify-center overflow-hidden">
                            <img 
                              src={capturedImage} 
                              alt="Captured" 
                              className="max-w-full max-h-full object-contain transition-transform duration-300" 
                              style={{ transform: `rotate(${imageRotation}deg)` }}
                            />
                          </div>
                        </div>
                        {processedImage && (
                          <div>
                            <p className="text-sm text-gray-500 mb-2 flex items-center gap-1">
                              <Sparkles className="w-4 h-4" /> Background Removed
                            </p>
                            <div 
                              className="rounded-xl overflow-hidden aspect-[3/4] flex items-center justify-center" 
                              style={{ background: 'repeating-conic-gradient(#e5e5e5 0% 25%, white 0% 50%) 50% / 20px 20px' }}
                            >
                              <img 
                                src={processedImage} 
                                alt="Processed" 
                                className="max-w-full max-h-full object-contain transition-transform duration-300" 
                                style={{ transform: `rotate(${imageRotation}deg)` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          onClick={removeBackground} 
                          disabled={isProcessing} 
                          className="flex-1 rounded-2xl"
                        >
                          <Wand2 className="w-4 h-4 mr-2" />
                          {isProcessing ? 'Processing...' : 'Remove Background'}
                        </Button>
                        <Button 
                          variant="outline" 
                          onClick={analyzeWithAI} 
                          disabled={isAnalyzing} 
                          className="flex-1 rounded-2xl border-suede-300 text-suede-600"
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          {isAnalyzing ? 'Analyzing...' : 'AI Detect'}
                        </Button>
                        <Button variant="ghost" onClick={() => { setCapturedImage(null); setProcessedImage(null); setAiAnalysis(null); setImageRotation(0); }}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-[11px] text-stone-400 text-center -mt-1">
                        💡 Note: Use AI detect first if you want to identify your product.
                      </p>
                      
                      {/* Rotation Controls */}
                      <div className="bg-gradient-to-r from-stone-50 to-mud-50 rounded-xl p-4 border border-stone-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <RotateCcw className="w-4 h-4 text-stone-600" />
                            <span className="text-sm font-medium text-stone-700">Rotate Image</span>
                          </div>
                          {imageRotation !== 0 && (
                            <span className="text-xs text-stone-500">{imageRotation}°</span>
                          )}
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => rotateImage(90)}
                            className="flex-col h-auto py-2"
                          >
                            <RotateCcw className="w-4 h-4 mb-1" />
                            <span className="text-xs">90°</span>
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => rotateImage(180)}
                            className="flex-col h-auto py-2"
                          >
                            <RotateCcw className="w-4 h-4 mb-1" />
                            <span className="text-xs">180°</span>
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => rotateImage(270)}
                            className="flex-col h-auto py-2"
                          >
                            <RotateCcw className="w-4 h-4 mb-1" />
                            <span className="text-xs">270°</span>
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setImageRotation(0)}
                            disabled={imageRotation === 0}
                            className="flex-col h-auto py-2"
                          >
                            <RotateCcw className="w-4 h-4 mb-1" />
                            <span className="text-xs">Reset</span>
                          </Button>
                        </div>
                      </div>
                      
                      {/* AI Analysis Results */}
                      {aiAnalysis && (
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }} 
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-gradient-to-r from-suede-50 to-terracotta-50 rounded-2xl p-4 border border-suede-200"
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-suede-600" />
                            <span className="font-medium text-suede-700">AI Detected</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div><span className="text-gray-500">Item:</span> <span className="font-medium">{aiAnalysis.name}</span></div>
                            <div><span className="text-gray-500">Color:</span> <span className="font-medium">{aiAnalysis.color}</span></div>
                            <div><span className="text-gray-500">Material:</span> <span className="font-medium">{aiAnalysis.material}</span></div>
                            <div><span className="text-gray-500">Style:</span> <span className="font-medium">{aiAnalysis.style}</span></div>
                          </div>
                          {aiAnalysis.styleTags && (
                            <div className="flex flex-wrap gap-1 mt-3">
                              {aiAnalysis.styleTags.map((tag, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                              ))}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </div>
                  )}
                  
                  <canvas ref={canvasRef} className="hidden" />
                  
                  {/* Or paste URL */}
                  {!capturedImage && (
                    <div>
                      <Label className="text-gray-500">Or paste image URL</Label>
                      <Input 
                        placeholder="https://example.com/image.jpg" 
                        className="mt-1 rounded-2xl" 
                        value={newItem.imageUrl} 
                        onChange={(e) => setNewItem({...newItem, imageUrl: e.target.value})} 
                      />
                    </div>
                  )}
                  
                  {/* Item Details Form */}
                  <div className="border-t pt-6 space-y-4">
                    <div>
                      <Label>Item Name *</Label>
                      <Input 
                        placeholder="e.g., Black Leather Jacket" 
                        className="mt-1 rounded-2xl" 
                        value={newItem.name} 
                        onChange={(e) => setNewItem({...newItem, name: e.target.value})} 
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Category *</Label>
                        <Select 
                          value={newItem.primaryCategory} 
                          onValueChange={(v) => setNewItem({...newItem, primaryCategory: v, clothingType: ''})}
                        >
                          <SelectTrigger className="mt-1 rounded-2xl"><SelectValue placeholder="Select category" /></SelectTrigger>
                          <SelectContent>
                            {categories && Object.entries(categories.categories).map(([key, cat]) => (
                              <SelectItem key={key} value={key}>
                                <span className="flex items-center gap-2">
                                  {cat.icon} {cat.name}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Type</Label>
                        {!showCustomClothingType ? (
                          <Select 
                            value={newItem.clothingType} 
                            onValueChange={(v) => { if (v === '__custom__') { setShowCustomClothingType(true); setCustomClothingTypeValue(''); } else { setNewItem({...newItem, clothingType: v}); } }}
                            disabled={!newItem.primaryCategory}
                          >
                            <SelectTrigger className="mt-1 rounded-2xl"><SelectValue placeholder="Select type" /></SelectTrigger>
                            <SelectContent>
                              {categories && newItem.primaryCategory && 
                                categories.categories[newItem.primaryCategory]?.types.map(t => (
                                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))
                              }
                              <SelectItem value="__custom__">✏️ Custom type...</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="flex gap-2 mt-1">
                            <Input
                              placeholder="Type your custom clothing type..."
                              className="rounded-2xl text-sm flex-1"
                              value={customClothingTypeValue}
                              onChange={(e) => setCustomClothingTypeValue(e.target.value)}
                              autoFocus
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-xl px-3 text-xs"
                              onClick={() => { if (customClothingTypeValue.trim()) { setNewItem({...newItem, clothingType: customClothingTypeValue.trim()}); } setShowCustomClothingType(false); setCustomClothingTypeValue(''); }}
                            >
                              {customClothingTypeValue.trim() ? 'Set' : 'Cancel'}
                            </Button>
                          </div>
                        )}
                        {!showCustomClothingType && newItem.primaryCategory && !categories?.categories?.[newItem.primaryCategory]?.types?.find(t => t.value === newItem.clothingType) && newItem.clothingType !== '' && (
                          <div className="mt-1.5 text-xs text-suede-600 bg-suede-50 rounded-lg px-2 py-1 flex items-center justify-between">
                            <span>Custom: {newItem.clothingType}</span>
                            <button onClick={() => { setShowCustomClothingType(true); setCustomClothingTypeValue(newItem.clothingType); }} className="text-suede-500 hover:text-suede-700 ml-2">✏️</button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Price</Label>
                        <div className="flex gap-1.5 mt-1">
                          <select
                            value={getCurrency()}
                            onChange={(e) => { setGlobalCurrency(e.target.value); }}
                            className="w-16 shrink-0 rounded-xl border border-stone-200 bg-stone-50 text-xs text-center py-2 focus:outline-none focus:ring-2 focus:ring-suede-500/30"
                          >
                            {getSupportedCurrencies().map(c => (
                              <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                            ))}
                          </select>
                          <Input 
                            type="number" 
                            placeholder="0" 
                            className="rounded-2xl" 
                            value={newItem.price} 
                            onChange={(e) => setNewItem({...newItem, price: e.target.value})} 
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Color</Label>
                        <Select value={newItem.color} onValueChange={(v) => setNewItem({...newItem, color: v})}>
                          <SelectTrigger className="mt-1 rounded-2xl"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            {categories?.colors.map(c => (
                              <SelectItem key={c.value} value={c.value}>
                                <span className="flex items-center gap-2">
                                  <span 
                                    className="w-4 h-4 rounded-full border" 
                                    style={{ background: c.hex }}
                                  />
                                  {c.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Size</Label>
                        <Input 
                          placeholder="M, 8, etc" 
                          className="mt-1 rounded-2xl" 
                          value={newItem.size} 
                          onChange={(e) => setNewItem({...newItem, size: e.target.value})} 
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Brand</Label>
                        <Input 
                          placeholder="e.g., Zara" 
                          className="mt-1 rounded-2xl" 
                          value={newItem.brand} 
                          onChange={(e) => setNewItem({...newItem, brand: e.target.value})} 
                        />
                      </div>
                      <div>
                        <Label>Season</Label>
                        <Select value={newItem.season} onValueChange={(v) => setNewItem({...newItem, season: v})}>
                          <SelectTrigger className="mt-1 rounded-2xl"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            {categories?.seasons.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Material</Label>
                        <Input 
                          placeholder="e.g., Cotton, Leather" 
                          className="mt-1 rounded-2xl" 
                          value={newItem.material} 
                          onChange={(e) => setNewItem({...newItem, material: e.target.value})} 
                        />
                      </div>
                      <div>
                        <Label>Fit</Label>
                        <Select value={newItem.fitType} onValueChange={(v) => setNewItem({...newItem, fitType: v})}>
                          <SelectTrigger className="mt-1 rounded-2xl"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            {categories?.fitTypes.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-stone-400" />
                        Storage Location
                        <span className="text-xs text-stone-400 font-normal">(optional)</span>
                      </Label>
                      <Input 
                        placeholder="e.g., Top shelf, Bedroom closet, Drawer 2" 
                        className="mt-1 rounded-2xl" 
                        value={newItem.storageLocation} 
                        onChange={(e) => setNewItem({...newItem, storageLocation: e.target.value})} 
                      />
                    </div>

                    <div>
                      <Label>Notes</Label>
                      <Textarea 
                        placeholder="Any additional notes about this item..." 
                        className="mt-1 rounded-2xl resize-none" 
                        rows={2}
                        value={newItem.notes} 
                        onChange={(e) => setNewItem({...newItem, notes: e.target.value})} 
                      />
                    </div>
                    
                    <div>
                      <Label className="flex items-center gap-1.5">
                        <ExternalLink className="w-3.5 h-3.5 text-stone-400" />
                        Product Link
                        <span className="text-xs text-stone-400 font-normal">(optional)</span>
                      </Label>
                      <Input 
                        placeholder="https://store.com/product..." 
                        className="mt-1 rounded-2xl" 
                        value={newItem.productUrl} 
                        onChange={(e) => setNewItem({...newItem, productUrl: e.target.value})} 
                      />
                    </div>
                    
                    <Button 
                      className="w-full bg-gradient-to-r from-suede-600 to-suede-500 text-white rounded-2xl py-6 active:scale-95 transition-transform" 
                      onClick={handleAddItem} 
                      disabled={!newItem.name || !newItem.primaryCategory || addingItem}
                    >
                      {addingItem ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <Plus className="w-5 h-5 mr-2" />
                          Add to Wardrobe
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full border-terracotta-200 text-terracotta-600 hover:bg-terracotta-50 rounded-2xl py-5" 
                      onClick={async () => {
                        if (newItem.name) {
                          try {
                            const res = await axios.post(`${API_URL}/real-wardrobe/wishlist`, {
                              name: newItem.name,
                              brand: newItem.brand || null,
                              store: null,
                              price: newItem.price || null,
                              link: null,
                              notes: newItem.notes || null,
                              image_url: capturedImage || null,
                              productUrl: newItem.productUrl || null
                            }, { headers: { Authorization: `Bearer ${token}` } });
                            setWishlistItems(prev => [res.data.item, ...prev]);
                            toast({ title: 'Added to Wishlist ❤️', description: `${newItem.name} saved to your wishlist` });
                          } catch (e) {
                            console.error('Wishlist save error:', e);
                            toast({ 
                              title: 'Failed to Save', 
                              description: 'Could not add to wishlist. Please try again.',
                              variant: 'destructive'
                            });
                            return;
                          }
                          setNewItem({ name: '', primaryCategory: '', clothingType: '', brand: '', price: '', color: '', size: '', material: '', season: '', occasions: [], fitType: '', condition: 'New', notes: '', imageUrl: '', storageLocation: '' });
                          setCapturedImage(null);
                          setAddDialogOpen(false);
                          toast({ title: 'Added to Wishlist ❤️', description: `${newItem.name} saved to your wishlist` });
                        }
                      }}
                      disabled={!newItem.name}
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Add to Wishlist Instead
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            )}
            
            {/* Bulk Add — My Clothes only */}
            {wardrobeMode === 'owned' && (
            <Dialog open={bulkDialogOpen} onOpenChange={(open) => {
              setBulkDialogOpen(open);
              if (!open) resetBulkUpload();
              if (open) {
                const token = localStorage.getItem('token');
                axios.get(`${API_URL}/subscription/usage`, { headers: { Authorization: `Bearer ${token}` } })
                  .then(r => setBulkAddsRemaining(r.data?.usage?.bulk_uploads))
                  .catch(() => {});
              }
            }}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-700 hover:to-amber-700 text-white px-5">
                  <Zap className="w-4 h-4 mr-2" />
                  Bulk Add
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-light flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-400 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    Bulk Add to Wardrobe
                  </DialogTitle>
                  <DialogDescription>
                    Upload up to 15 clothing photos. AI will automatically detect, remove backgrounds, and add them all to your wardrobe.
                  </DialogDescription>
                  {bulkAddsRemaining && (
                    <div className="mt-2 flex items-center gap-2 text-xs font-medium">
                      <div className={`px-2.5 py-1 rounded-full ${bulkAddsRemaining.remaining > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>
                        {bulkAddsRemaining.remaining > 0
                          ? `${bulkAddsRemaining.remaining} of ${bulkAddsRemaining.limit} bulk adds remaining`
                          : 'No bulk adds remaining — upgrade or purchase a top-up'}
                      </div>
                    </div>
                  )}
                </DialogHeader>

                <div className="space-y-4 py-4">
                  {!bulkProcessing && !bulkResults && (
                    <>
                      {/* Upload Area */}
                      <div
                        onClick={() => bulkInputRef.current?.click()}
                        className="border-2 border-dashed border-stone-300 rounded-xl p-8 text-center cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-all"
                      >
                        <Upload className="w-10 h-10 text-stone-400 mx-auto mb-3" />
                        <p className="text-stone-600 font-medium">Tap to select photos</p>
                        <p className="text-xs text-stone-400 mt-1">Up to 15 clothing items • JPG, PNG</p>
                        <input
                          ref={bulkInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleBulkFileSelect}
                          className="hidden"
                        />
                      </div>

                      {/* Preview Grid */}
                      {bulkImages.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-stone-700 mb-2">{bulkImages.length}/15 photos selected</p>
                          <div className="grid grid-cols-4 gap-2">
                            {bulkImages.map((img, idx) => (
                              <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-stone-100">
                                <img src={img.preview} alt={`Item ${idx + 1}`} className="w-full h-full object-cover" />
                                <button
                                  onClick={(e) => { e.stopPropagation(); removeBulkImage(idx); }}
                                  className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center"
                                >
                                  <X className="w-3 h-3 text-white" />
                                </button>
                              </div>
                            ))}
                            {bulkImages.length < 15 && (
                              <div
                                onClick={() => bulkInputRef.current?.click()}
                                className="aspect-square rounded-lg border-2 border-dashed border-stone-200 flex items-center justify-center cursor-pointer hover:border-amber-400 transition-colors"
                              >
                                <Plus className="w-6 h-6 text-stone-400" />
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Action */}
                      {bulkImages.length > 0 && (
                        <Button
                          onClick={handleBulkUpload}
                          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl py-3"
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          Process & Add {bulkImages.length} {bulkImages.length === 1 ? 'Item' : 'Items'}
                        </Button>
                      )}
                    </>
                  )}

                  {/* Processing State */}
                  {bulkProcessing && (
                    <div className="text-center py-8">
                      <RefreshCw className="w-10 h-10 text-amber-500 animate-spin mx-auto mb-4" />
                      <p className="text-lg font-medium text-stone-700">Processing your items...</p>
                      <p className="text-sm text-stone-500 mt-1">AI detecting + removing backgrounds</p>
                      <p className="text-xs text-stone-400 mt-3">This may take a few minutes for {bulkImages.length} items</p>
                      <div className="mt-4 w-full bg-stone-100 rounded-full h-2">
                        <div className="bg-gradient-to-r from-amber-500 to-orange-500 h-2 rounded-full animate-pulse" style={{ width: '60%' }} />
                      </div>
                    </div>
                  )}

                  {/* Results */}
                  {bulkResults && (
                    <div className="space-y-4">
                      <div className="text-center py-4">
                        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                          <Check className="w-8 h-8 text-green-600" />
                        </div>
                        <p className="text-lg font-medium text-stone-700">{bulkResults.message}</p>
                        {bulkResults.failed > 0 && (
                          <p className="text-sm text-red-500 mt-1">{bulkResults.failed} items could not be processed</p>
                        )}
                      </div>
                      {bulkResults.items && bulkResults.items.length > 0 && (
                        <div className="grid grid-cols-4 gap-2">
                          {bulkResults.items.map((item, idx) => (
                            <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-stone-100">
                              <img src={item.image_url || item.processed_image_url} alt={item.name} className="w-full h-full object-cover" />
                            </div>
                          ))}
                        </div>
                      )}
                      <Button
                        onClick={() => { setBulkDialogOpen(false); resetBulkUpload(); }}
                        className="w-full bg-stone-800 text-white rounded-xl"
                      >
                        Done
                      </Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            )}

            {/* Outfit Builder — My Clothes only */}
            {wardrobeMode === 'owned' && (
            <Button 
              onClick={() => { setWardrobeMode('builder'); setShowOutfitBuilder(true); }}
              className="bg-gradient-to-r from-suede-500 to-suede-400 hover:from-suede-600 hover:to-suede-500 text-white rounded-full px-5"
            >
              <Layers className="w-4 h-4 mr-2" />
              Outfit Builder
            </Button>
            )}

            {/* Quick-access buttons — always visible on owned tab */}
            {wardrobeMode === 'owned' && (
            <>
              <Button 
                onClick={() => setWardrobeMode('wishlist')}
                className="rounded-full bg-gradient-to-r from-terracotta-600 to-terracotta-500 hover:from-terracotta-700 hover:to-terracotta-600 text-white px-4"
              >
                <Heart className="w-4 h-4 mr-1.5" />
                Wishlist
                {wishlistItems.length > 0 && <span className="ml-1.5 text-[10px] bg-white/20 text-white px-1.5 py-0.5 rounded-full">{wishlistItems.length}</span>}
              </Button>
              <Button 
                onClick={() => navigate('/outfit-calendar')}
                className="rounded-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-4"
              >
                <Calendar className="w-4 h-4 mr-1.5" />
                Calendar
              </Button>
              <Button 
                onClick={() => setWardrobeMode('packing')}
                className="rounded-full bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white px-4"
              >
                <Luggage className="w-4 h-4 mr-1.5" />
                Packing
                {packingLists.length > 0 && <span className="ml-1.5 text-[10px] bg-white/20 text-white px-1.5 py-0.5 rounded-full">{packingLists.length}</span>}
              </Button>
              <Button 
                onClick={() => setWardrobeMode('partner')}
                className="rounded-full bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white px-4"
              >
                <HeartHandshake className="w-4 h-4 mr-1.5" />
                Partner
                {partners.filter(p => p.type === 'partner' || !p.type).length > 0 && <span className="ml-1.5 text-[10px] bg-white/20 text-white px-1.5 py-0.5 rounded-full">{partners.filter(p => p.type === 'partner' || !p.type).length}</span>}
              </Button>
              <Button 
                onClick={() => setWardrobeMode('friends')}
                className="rounded-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-4"
              >
                <Users className="w-4 h-4 mr-1.5" />
                Friends
                {friends.length > 0 && <span className="ml-1.5 text-[10px] bg-white/20 text-white px-1.5 py-0.5 rounded-full">{friends.length}</span>}
              </Button>
            </>
            )}

            {/* Add to Wishlist — Wishlist tab */}
            {wardrobeMode === 'wishlist' && (
            <Dialog open={addWishlistDialogOpen} onOpenChange={setAddWishlistDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-terracotta-200 text-terracotta-600 hover:bg-terracotta-50 rounded-2xl">
                  <Heart className="w-4 h-4 mr-2" />
                  Add to Wishlist
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
                <DialogHeader>
                  <DialogTitle className="text-xl font-medium flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-terracotta-500 to-terracotta-400 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-white" />
                    </div>
                    Add to Wishlist
                  </DialogTitle>
                  <DialogDescription>
                    Save items you've spotted in shops or online that you'd love to own.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Image Upload */}
                  {!wishlistImage ? (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <label className="flex-1 flex items-center justify-center gap-2 py-4 rounded-xl border-2 border-dashed border-stone-200 cursor-pointer hover:bg-stone-50 hover:border-stone-300 transition-colors text-sm text-stone-600">
                          <Upload className="w-5 h-5" />
                          Upload Photo
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => setWishlistImage(reader.result);
                              reader.readAsDataURL(file);
                            }
                          }} />
                        </label>
                        <label className="flex-1 flex items-center justify-center gap-2 py-4 rounded-xl border-2 border-dashed border-terracotta-200 cursor-pointer hover:bg-terracotta-50 hover:border-terracotta-400 transition-colors text-sm text-terracotta-600">
                          <Camera className="w-5 h-5" />
                          Take Photo
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => setWishlistImage(reader.result);
                              reader.readAsDataURL(file);
                            }
                          }} />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="relative">
                        <img src={wishlistImage} alt="Wishlist item" className="w-full max-h-64 object-contain rounded-xl bg-stone-50" />
                        {(isAnalyzingWishlist || isProcessing) && (
                          <div className="absolute inset-0 bg-white/70 rounded-xl flex items-center justify-center">
                            <div className="flex flex-col items-center gap-2">
                              <Loader2 className="w-8 h-8 text-suede-500 animate-spin" />
                              <span className="text-xs text-stone-500 font-medium">{isProcessing ? 'Removing background...' : 'Analyzing...'}</span>
                            </div>
                          </div>
                        )}
                        <button 
                          onClick={() => { setWishlistImage(null); setWishlistAiAnalysis(null); }}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Remove Background + AI Detect Buttons */}
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          onClick={async () => {
                            if (!wishlistImage) return;
                            setIsProcessing(true);
                            try {
                              const isDataUrl = wishlistImage.startsWith('data:');
                              const response = await axios.post(`${API_URL}/real-wardrobe/remove-background`, 
                                isDataUrl ? { imageBase64: wishlistImage.split(',')[1] } : { imageUrl: wishlistImage },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              if (response.data.success && response.data.processedImage) {
                                setWishlistImage(response.data.processedImage);
                                toast({ title: '✨ Background Removed', description: 'Clean cutout ready!' });
                              } else {
                                toast({ title: 'Failed', description: 'Could not remove background.', variant: 'destructive' });
                              }
                            } catch (err) {
                              console.error('Wishlist BG removal error:', err);
                              toast({ title: 'Failed', description: 'Could not remove background.', variant: 'destructive' });
                            } finally {
                              setIsProcessing(false);
                            }
                          }} 
                          disabled={isProcessing || isAnalyzingWishlist} 
                          className="flex-1 rounded-2xl border-stone-300 text-stone-600"
                        >
                          <Wand2 className="w-4 h-4 mr-1.5" />
                          {isProcessing ? 'Removing...' : 'Remove BG'}
                        </Button>
                        <Button 
                          variant="outline" 
                          onClick={async () => {
                            if (!wishlistImage) return;
                            setIsAnalyzingWishlist(true);
                            try {
                              const isDataUrl = wishlistImage.startsWith('data:');
                              const response = await axios.post(`${API_URL}/real-wardrobe/analyze`, 
                                isDataUrl ? { imageBase64: wishlistImage.split(',')[1], source: 'wishlist' } : { imageUrl: wishlistImage, source: 'wishlist' },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              if (response.data.success && response.data.analysis) {
                                const analysis = response.data.analysis;
                                setWishlistAiAnalysis(analysis);
                                setWishlistForm(prev => ({
                                  ...prev,
                                  name: analysis.name || prev.name,
                                  brand: analysis.estimatedBrand || analysis.detectedStore || prev.brand,
                                  price: analysis.detectedPrice || prev.price,
                                  store: analysis.detectedStore || prev.store,
                                  primaryCategory: analysis.primaryCategory || prev.primaryCategory,
                                  clothingType: analysis.clothingType || prev.clothingType,
                                }));
                                toast({ title: '✨ AI Detection Complete', description: `Detected: ${analysis.name || 'Item'}` });
                              } else {
                                toast({ title: 'AI Detection', description: 'Could not analyze. Please fill in manually.', variant: 'destructive' });
                              }
                            } catch (err) {
                              console.error('Wishlist AI analysis error:', err);
                              toast({ title: 'Analysis Failed', description: 'Could not analyze the image.', variant: 'destructive' });
                            } finally {
                              setIsAnalyzingWishlist(false);
                            }
                          }} 
                          disabled={isAnalyzingWishlist || isProcessing} 
                          className="flex-1 rounded-2xl border-suede-300 text-suede-600"
                        >
                          <Sparkles className="w-4 h-4 mr-1.5" />
                          {isAnalyzingWishlist ? 'Detecting...' : 'AI Detect'}
                        </Button>
                      </div>
                      <p className="text-[11px] text-stone-400 text-center">💡 Note: Use AI detect first if you want to identify your product.</p>
                      
                      {/* AI Analysis Results */}
                      {wishlistAiAnalysis && (
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }} 
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-gradient-to-r from-suede-50 to-terracotta-50 rounded-2xl p-4 border border-suede-200"
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-suede-600" />
                            <span className="font-medium text-suede-700">AI Detected</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div><span className="text-gray-500">Item:</span> <span className="font-medium">{wishlistAiAnalysis.name}</span></div>
                            <div><span className="text-gray-500">Color:</span> <span className="font-medium">{wishlistAiAnalysis.color}</span></div>
                            <div><span className="text-gray-500">Material:</span> <span className="font-medium">{wishlistAiAnalysis.material}</span></div>
                            <div><span className="text-gray-500">Style:</span> <span className="font-medium">{wishlistAiAnalysis.style}</span></div>
                          </div>
                          {wishlistAiAnalysis.styleTags && (
                            <div className="flex flex-wrap gap-1 mt-3">
                              {wishlistAiAnalysis.styleTags.map((tag, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                              ))}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-stone-500">Item Name</Label>
                      <Input 
                        placeholder="e.g., Linen Blazer"
                        value={wishlistForm.name}
                        onChange={(e) => setWishlistForm(p => ({ ...p, name: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-stone-500">Brand</Label>
                      <Input 
                        placeholder="e.g., Zara"
                        value={wishlistForm.brand}
                        onChange={(e) => setWishlistForm(p => ({ ...p, brand: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-stone-500">Store</Label>
                      <Input 
                        placeholder="Where you saw it"
                        value={wishlistForm.store}
                        onChange={(e) => setWishlistForm(p => ({ ...p, store: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-stone-500">Price</Label>
                      <div className="flex gap-1.5 mt-1">
                        <select
                          value={getCurrency()}
                          onChange={(e) => { setGlobalCurrency(e.target.value); }}
                          className="w-16 shrink-0 rounded-xl border border-stone-200 bg-stone-50 text-xs text-center py-2 focus:outline-none focus:ring-2 focus:ring-suede-500/30"
                        >
                          {getSupportedCurrencies().map(c => (
                            <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                          ))}
                        </select>
                        <Input 
                          placeholder="0.00"
                          value={wishlistForm.price}
                          onChange={(e) => setWishlistForm(p => ({ ...p, price: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-stone-500">Link (optional)</Label>
                    <Input 
                      placeholder="https://..."
                      value={wishlistForm.link}
                      onChange={(e) => setWishlistForm(p => ({ ...p, link: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs text-stone-500">Notes</Label>
                    <Textarea 
                      placeholder="Why you love it, outfit ideas..."
                      value={wishlistForm.notes}
                      onChange={(e) => setWishlistForm(p => ({ ...p, notes: e.target.value }))}
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                  
                  <Button 
                    onClick={async () => {
                      if (wishlistForm.name) {
                        const savedForm = { ...wishlistForm };
                        const savedImage = wishlistImage;
                        // Close dialog immediately for snappy UX
                        setWishlistForm({ name: '', brand: '', store: '', price: '', notes: '', link: '', primaryCategory: '', clothingType: '' });
                        setWishlistImage(null);
                        setWishlistAiAnalysis(null);
                        setAddWishlistDialogOpen(false);
                        setWardrobeMode('wishlist');
                        toast({ title: 'Added to Wishlist ❤️', description: `${savedForm.name} saved — find it in My Wardrobe → Wishlist tab` });
                        console.log('💝 Adding to wishlist:', savedForm);
                        try {
                          const res = await axios.post(`${API_URL}/real-wardrobe/wishlist`, {
                            name: savedForm.name,
                            brand: savedForm.brand || null,
                            store: savedForm.store || null,
                            price: savedForm.price || null,
                            link: savedForm.link || null,
                            notes: savedForm.notes || null,
                            image_url: savedImage || null,
                            productUrl: savedForm.productUrl || null,
                            primary_category: savedForm.primaryCategory || null,
                            clothing_type: savedForm.clothingType || null
                          }, { headers: { Authorization: `Bearer ${token}` } });
                          console.log('✅ Wishlist API response:', res.data);
                          setWishlistItems(prev => [res.data.item, ...prev]);
                        } catch (e) {
                          console.error('❌ Wishlist save error:', e);
                          console.error('❌ Error details:', {
                            message: e.message,
                            response: e.response?.data,
                            status: e.response?.status
                          });
                          toast({ 
                            title: '❌ Failed to Save', 
                            description: 'Could not add to wishlist. Please check your connection and try again.',
                            variant: 'destructive'
                          });
                          return;
                        }
                      }
                    }}
                    className="w-full bg-gradient-to-r from-terracotta-500 to-terracotta-400 text-white rounded-xl"
                    disabled={!wishlistForm.name}
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Save to Wishlist
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            )}

            {/* Create Capsule — Capsules tab */}
            {wardrobeMode === 'capsules' && (
              <Button onClick={() => { setCurrentCapsule({ name: '', type: 'seasonal', description: '', items: [] }); setShowCapsuleDialog(true); }} className="bg-stone-800 hover:bg-stone-900 text-white rounded-full px-6">
                <Plus className="w-4 h-4 mr-2" />
                New Capsule
              </Button>
            )}

            {/* Create Packing List — Packing tab */}
            {wardrobeMode === 'packing' && (
              <Button onClick={() => { setCurrentPackingList({ name: '', destination: '', startDate: '', endDate: '', items: [], packedItems: [], coverImage: '' }); setShowPackingDialog(true); }} className="bg-stone-800 hover:bg-stone-900 text-white rounded-full px-6">
                <Plus className="w-4 h-4 mr-2" />
                New Packing List
              </Button>
            )}

            {/* Calendar — Calendar tab */}
            {wardrobeMode === 'calendar' && (
              <Button variant="outline" onClick={() => navigate('/style-studio')} className="rounded-full border-stone-200 text-stone-500 hover:text-stone-700 hover:border-stone-300">
                <Layers className="w-4 h-4 mr-2" />
                Style Studio
              </Button>
            )}

            {/* Boards — Boards tab */}
            {wardrobeMode === 'boards' && (
              <Button onClick={() => { setCurrentBoard({ name: '', description: '', inspiration: [] }); setShowBoardDialog(true); }} className="bg-stone-800 hover:bg-stone-900 text-white rounded-full px-6">
                <Plus className="w-4 h-4 mr-2" />
                New Board
              </Button>
            )}
          </div>
        </div>

        {/* Stats Strip */}
        {(wardrobeMode === 'owned' || wardrobeMode === 'builder') && (
        <div className="flex items-center justify-center gap-8 md:gap-16 py-6 mb-8 border-y border-stone-200">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
            <p className="text-3xl font-extralight text-stone-800" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>{stats.total_items || 0}</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mt-1">Pieces</p>
          </motion.div>
          <div className="w-px h-10 bg-stone-200" />
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="text-center">
            <p className="text-3xl font-extralight text-stone-800" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>{Object.keys(groupedItems).filter(k => groupedItems[k]?.items?.length > 0).length}</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mt-1">Categories</p>
          </motion.div>
          <div className="w-px h-10 bg-stone-200" />
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-center">
            <p className="text-3xl font-extralight text-stone-800" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>{stats.outfitCount || savedOutfits.length}</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mt-1">Outfits</p>
          </motion.div>
        </div>
        )}

        {/* Feature Navigation Tabs */}
        <div className="mb-10 overflow-x-auto">
          <div className="flex gap-1.5 min-w-max pb-2">
            {[
              { id: 'owned', icon: Shirt, label: 'My Clothes' },
              { id: 'favorites', icon: Star, label: 'Favorites', count: items.filter(i => i.is_favorite).length },
              { id: 'capsules', icon: Archive, label: 'Capsules', count: capsules.length },
              { id: 'analytics', icon: PieChart, label: 'Analytics' },
              { id: 'boards', icon: Bookmark, label: 'Boards', count: inspirationBoards.length },
              ...(petsEnabled ? [{ id: 'pets', icon: Dog, label: 'Pets', count: petProfiles.length }] : []),
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setWardrobeMode(tab.id);
                  if (tab.id === 'builder') {
                    setShowOutfitBuilder(true);
                  }
                }}
                className={`px-4 py-2 rounded-full text-xs font-medium uppercase tracking-[0.15em] transition-all flex items-center gap-2 whitespace-nowrap ${
                  wardrobeMode === tab.id
                    ? 'bg-stone-800 text-white'
                    : 'bg-transparent text-stone-400 hover:text-stone-700 hover:bg-stone-100'
                }`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
                {tab.count > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    wardrobeMode === tab.id ? 'bg-white/20' : 'bg-stone-100'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
            {!petsEnabled && (
              <button
                onClick={() => handleTogglePets(true)}
                className="px-3 py-2 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 whitespace-nowrap text-stone-300 hover:text-stone-500 hover:bg-stone-50 border border-dashed border-stone-200 hover:border-stone-300"
                title="Add pet wardrobe section"
              >
                <Dog className="w-3.5 h-3.5" />
                <Plus className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Wishlist View */}
        {wardrobeMode === 'wishlist' && (
          <div className="mb-8">
            {/* Wishlist Header */}
            <div className="bg-gradient-to-r from-terracotta-50 to-terracotta-50 rounded-2xl p-6 mb-6 border border-terracotta-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-terracotta-500 to-terracotta-400 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-stone-800">My Wishlist</h2>
                    <p className="text-sm text-stone-500">{wishlistItems.length} items you're dreaming about</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Wishlist Items Grid */}
            {wishlistItems.length === 0 ? (
              <div className="text-center py-16 bg-white/50 rounded-2xl border border-dashed border-terracotta-200">
                <Heart className="w-16 h-16 text-terracotta-200 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-stone-600 mb-2">Your Wishlist is Empty</h3>
                <p className="text-stone-400 mb-6 max-w-md mx-auto">
                  Spotted something you love while shopping? Add it here to save for later or use for outfit inspiration.
                </p>
                <Button 
                  onClick={() => setAddWishlistDialogOpen(true)}
                  className="bg-gradient-to-r from-terracotta-500 to-terracotta-400 text-white rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Item
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {wishlistItems.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm border border-terracotta-100 hover:shadow-lg transition-shadow group"
                  >
                    <div className="relative aspect-square bg-stone-100">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Heart className="w-12 h-12 text-terracotta-200" />
                        </div>
                      )}
                      {/* Processing overlay */}
                      {removingBgItemId === item.id && (
                        <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-10">
                          <div className="text-center">
                            <Loader2 className="w-8 h-8 animate-spin text-suede-500 mx-auto mb-1" />
                            <span className="text-xs text-stone-600">Removing BG...</span>
                          </div>
                        </div>
                      )}
                      {/* Top-right: store link */}
                      {item.link && (
                        <a 
                          href={item.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow hover:bg-white transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5 text-terracotta-500" />
                        </a>
                      )}
                      {/* Bottom action bar */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent p-2 pt-6 flex items-end justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {/* View */}
                        <button
                          onClick={() => { setSelectedItem(item); setShowItemDetail(true); }}
                          className="bg-white/90 backdrop-blur-sm p-1.5 rounded-lg shadow hover:bg-white transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-3.5 h-3.5 text-stone-600" />
                        </button>
                        {/* Virtual Try-On */}
                        {canTryOn(item) && (
                          <button
                            onClick={() => openTryOn(item)}
                            className="bg-white/90 backdrop-blur-sm p-1.5 rounded-lg shadow hover:bg-amber-50 transition-colors"
                            title="Virtual Try-On"
                          >
                            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                          </button>
                        )}
                        {/* Edit */}
                        <button 
                          onClick={() => {
                            setEditingWishlistItem(item);
                            setEditWishlistForm({ name: item.name || '', brand: item.brand || '', store: item.store || '', price: item.price || '', notes: item.notes || '', link: item.link || '', productUrl: item.product_url || '' });
                            setEditWishlistImage(item.image_url || null);
                            setEditWishlistAiAnalysis(null);
                            setShowEditWishlistDialog(true);
                          }}
                          className="bg-white/90 backdrop-blur-sm p-1.5 rounded-lg shadow hover:bg-suede-50 transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5 text-suede-500" />
                        </button>
                        {/* Delete */}
                        <button 
                          onClick={async () => {
                            try {
                              await axios.delete(`${API_URL}/real-wardrobe/wishlist/${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
                            } catch (e) { console.error('Wishlist delete error:', e); }
                            setWishlistItems(prev => prev.filter(i => i.id !== item.id));
                          }}
                          className="bg-white/90 backdrop-blur-sm p-1.5 rounded-lg shadow hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-stone-800 truncate flex-1">{item.name}</h3>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('🛍️ BOUGHT BUTTON TAPPED for item:', item.name);
                            moveWishlistItemToWardrobe(item);
                          }}
                          disabled={movingToWardrobe === item.id}
                          className="ml-2 bg-green-500 text-white p-2 rounded-lg shadow-sm hover:bg-green-600 transition-colors flex-shrink-0 disabled:opacity-50"
                          title="Bought - Move to Wardrobe"
                        >
                          {movingToWardrobe === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-stone-500">{item.brand || item.store}</span>
                        {item.price && <span className="text-sm font-medium text-terracotta-600">{cs()}{item.price}</span>}
                      </div>
                      {item.notes && (
                        <p className="text-xs text-stone-400 mt-2 line-clamp-2">{item.notes}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Edit Wishlist Item Dialog */}
        <Dialog open={showEditWishlistDialog} onOpenChange={setShowEditWishlistDialog}>
          <DialogContent className="max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto rounded-none sm:rounded-3xl top-0 sm:top-[50%] translate-y-0 sm:translate-y-[-50%] h-[100dvh] sm:h-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-medium flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center">
                  <Pencil className="w-5 h-5 text-white" />
                </div>
                Edit Wishlist Item
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Image */}
              {!editWishlistImage ? (
                <label className="block border-2 border-dashed border-suede-200 rounded-xl p-8 text-center cursor-pointer hover:border-suede-400 hover:bg-suede-50/50 transition-colors">
                  <Camera className="w-10 h-10 text-suede-300 mx-auto mb-2" />
                  <span className="text-sm font-medium text-stone-600">Upload a photo</span>
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => setEditWishlistImage(reader.result);
                        reader.readAsDataURL(file);
                      }
                    }} 
                  />
                </label>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <img src={editWishlistImage} alt="Item" className="w-full max-h-[50vh] sm:max-h-[40vh] object-contain rounded-xl bg-stone-50" />
                    <button 
                      onClick={() => { setEditWishlistImage(null); setEditWishlistAiAnalysis(null); }}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* AI Detect Button */}
                  <Button 
                    variant="outline" 
                    onClick={async () => {
                      if (!editWishlistImage) return;
                      setIsAnalyzingEditWishlist(true);
                      try {
                        const imgData = editWishlistImage.startsWith('data:') ? editWishlistImage.split(',')[1] : null;
                        if (!imgData) {
                          toast({ title: 'AI Detection', description: 'Please upload a new photo to use AI Detect.', variant: 'destructive' });
                          setIsAnalyzingEditWishlist(false);
                          return;
                        }
                        const response = await axios.post(`${API_URL}/real-wardrobe/analyze`, {
                          imageBase64: imgData,
                          source: 'wishlist'
                        }, { headers: { Authorization: `Bearer ${token}` } });
                        if (response.data.success && response.data.analysis) {
                          const analysis = response.data.analysis;
                          setEditWishlistAiAnalysis(analysis);
                          setEditWishlistForm(prev => ({
                            ...prev,
                            name: analysis.name || prev.name,
                            brand: analysis.estimatedBrand || prev.brand,
                          }));
                          toast({ title: '✨ AI Detection Complete', description: `Detected: ${analysis.name || 'Item'}` });
                        } else {
                          toast({ title: 'AI Detection', description: 'Could not analyze. Please fill in details manually.', variant: 'destructive' });
                        }
                      } catch (err) {
                        console.error('Edit wishlist AI analysis error:', err);
                        toast({ title: 'Analysis Failed', description: 'Could not analyze the image.', variant: 'destructive' });
                      } finally {
                        setIsAnalyzingEditWishlist(false);
                      }
                    }} 
                    disabled={isAnalyzingEditWishlist} 
                    className="w-full rounded-2xl border-suede-300 text-suede-600"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {isAnalyzingEditWishlist ? 'Analyzing...' : '✨ AI Detect'}
                  </Button>
                  
                  {/* AI Results */}
                  {editWishlistAiAnalysis && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }} 
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gradient-to-r from-suede-50 to-terracotta-50 rounded-2xl p-4 border border-suede-200"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-5 h-5 text-suede-600" />
                        <span className="font-medium text-suede-700">AI Detected</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-500">Item:</span> <span className="font-medium">{editWishlistAiAnalysis.name}</span></div>
                        <div><span className="text-gray-500">Color:</span> <span className="font-medium">{editWishlistAiAnalysis.color}</span></div>
                        <div><span className="text-gray-500">Material:</span> <span className="font-medium">{editWishlistAiAnalysis.material}</span></div>
                        <div><span className="text-gray-500">Style:</span> <span className="font-medium">{editWishlistAiAnalysis.style}</span></div>
                      </div>
                      {editWishlistAiAnalysis.styleTags && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {editWishlistAiAnalysis.styleTags.map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-stone-500">Item Name</Label>
                  <Input 
                    placeholder="e.g., Linen Blazer"
                    value={editWishlistForm.name}
                    onChange={(e) => setEditWishlistForm(p => ({ ...p, name: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Brand</Label>
                  <Input 
                    placeholder="e.g., Zara"
                    value={editWishlistForm.brand}
                    onChange={(e) => setEditWishlistForm(p => ({ ...p, brand: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-stone-500">Store</Label>
                  <Input 
                    placeholder="Where you saw it"
                    value={editWishlistForm.store}
                    onChange={(e) => setEditWishlistForm(p => ({ ...p, store: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Price</Label>
                  <div className="flex gap-1.5 mt-1">
                    <select
                      value={getCurrency()}
                      onChange={(e) => { setGlobalCurrency(e.target.value); }}
                      className="w-16 shrink-0 rounded-xl border border-stone-200 bg-stone-50 text-xs text-center py-2 focus:outline-none focus:ring-2 focus:ring-suede-500/30"
                    >
                      {getSupportedCurrencies().map(c => (
                        <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                      ))}
                    </select>
                    <Input 
                      placeholder="0.00"
                      value={editWishlistForm.price}
                      onChange={(e) => setEditWishlistForm(p => ({ ...p, price: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-stone-500">Link (optional)</Label>
                <Input 
                  placeholder="https://..."
                  value={editWishlistForm.link}
                  onChange={(e) => setEditWishlistForm(p => ({ ...p, link: e.target.value }))}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="text-xs text-stone-500">Notes</Label>
                <Textarea 
                  placeholder="Why you love it, outfit ideas..."
                  value={editWishlistForm.notes}
                  onChange={(e) => setEditWishlistForm(p => ({ ...p, notes: e.target.value }))}
                  className="mt-1"
                  rows={2}
                />
              </div>
              
              <Button 
                onClick={async () => {
                  if (editWishlistForm.name && editingWishlistItem) {
                    try {
                      await axios.put(`${API_URL}/real-wardrobe/wishlist/${editingWishlistItem.id}`, {
                        ...editWishlistForm,
                        image_url: editWishlistImage || null
                      }, { headers: { Authorization: `Bearer ${token}` } });
                    } catch (e) { console.error('Wishlist update error:', e); }
                    setWishlistItems(prev => prev.map(item => 
                      item.id === editingWishlistItem.id 
                        ? { ...item, ...editWishlistForm, image_url: editWishlistImage }
                        : item
                    ));
                    setShowEditWishlistDialog(false);
                    setEditingWishlistItem(null);
                    toast({ title: 'Updated ✅', description: `${editWishlistForm.name} updated in your Wishlist` });
                  }
                }}
                className="w-full bg-gradient-to-r from-suede-500 to-suede-400 text-white rounded-xl"
                disabled={!editWishlistForm.name}
              >
                <Pencil className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* AI Suggest Dialog - Weather/Color/Event inputs */}
        <Dialog open={showAiSuggestDialog} onOpenChange={setShowAiSuggestDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-suede-600" />
                AI Outfit Suggestion
              </DialogTitle>
              <DialogDescription>
                Tell me about your day and I'll suggest the perfect outfit from your wardrobe!
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Temperature Options */}
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 flex items-center gap-2">
                  <Thermometer className="w-4 h-4 text-suede-500" />
                  Weather
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Warm', value: 'warm' },
                    { label: 'A bit cold', value: 'cold' },
                    { label: 'Very cold', value: 'very-cold' }
                  ].map((temp) => (
                    <button
                      key={temp.value}
                      type="button"
                      onClick={() => setAiSuggestParams(p => ({ ...p, temperature: p.temperature === temp.value ? '' : temp.value }))}
                      className={cn(
                        "py-2 px-3 rounded-lg text-sm font-medium transition-all border",
                        aiSuggestParams.temperature === temp.value
                          ? "bg-suede-500 text-white border-suede-600"
                          : "bg-white text-stone-600 border-stone-200 hover:border-suede-300"
                      )}
                    >
                      {temp.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dress Code Filter */}
              <div>
                <Label className="text-sm font-medium text-stone-700 mb-1.5 flex items-center gap-2">
                  👔 Dress Code
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  {['Casual', 'Formal'].map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setAiSuggestParams(p => ({ ...p, dressCode: p.dressCode === code.toLowerCase() ? '' : code.toLowerCase() }))}
                      className={cn(
                        "py-2.5 px-4 rounded-lg text-sm font-medium transition-all border",
                        aiSuggestParams.dressCode === code.toLowerCase()
                          ? "bg-suede-500 text-white border-suede-600"
                          : "bg-white text-stone-600 border-stone-200 hover:border-suede-300"
                      )}
                    >
                      {code === 'Casual' ? '😎 Casual' : '✨ Formal'}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-stone-400 mt-1">
                  Casual = everyday, relaxed looks · Formal = polished, elegant outfits
                </p>
              </div>

              <div className="bg-suede-50 rounded-xl p-3 border border-suede-100">
                <p className="text-xs text-suede-700">
                  💡 <strong>Tip:</strong> You can leave fields blank for a general suggestion, or fill them in for a more tailored outfit!
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setAiSuggestParams({ temperature: '', colorPreference: '', upcomingEvent: '', dressCode: '' });
                  setShowAiSuggestDialog(false);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={getQuickAiSuggestion}
                disabled={styleMaidLoading}
                className="flex-1 bg-gradient-to-r from-suede-500 to-terracotta-500 text-white"
              >
                {styleMaidLoading ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                      className="inline-block"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                    </motion.div>
                    Thinking...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Suggest Outfit
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ========== CAPSULE WARDROBES VIEW ========== */}
        {wardrobeMode === 'capsules' && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-suede-50 to-suede-50 rounded-2xl p-6 mb-6 border border-suede-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {viewingCapsule && (
                    <Button variant="ghost" size="sm" className="mr-1" onClick={() => setViewingCapsule(null)}>
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                  )}
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center">
                    <Archive className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-stone-800">
                      {viewingCapsule ? viewingCapsule.name : 'Capsule Wardrobes'}
                    </h2>
                    <p className="text-sm text-stone-500">
                      {viewingCapsule 
                        ? `${viewingCapsule.items.length} items · ${viewingCapsule.type}${viewingCapsule.description ? ` · ${viewingCapsule.description}` : ''}`
                        : 'Create unlimited seasonal, travel, or themed capsules'
                      }
                    </p>
                  </div>
                </div>
                {!viewingCapsule ? (
                  <Button 
                    onClick={() => { setCurrentCapsule({ name: '', description: '', type: 'seasonal', items: [] }); setEditingCapsule(null); setShowCapsuleDialog(true); }}
                    className="bg-gradient-to-r from-suede-500 to-suede-400 text-white rounded-xl"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Capsule
                  </Button>
                ) : (
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        // Add capsule items to a packing list
                        if (packingLists.length === 0) {
                          // Create new packing list from capsule
                          const newList = {
                            id: Date.now(),
                            name: `${viewingCapsule.name} Trip`,
                            destination: '',
                            startDate: '',
                            endDate: '',
                            items: [...viewingCapsule.items],
                            packedItems: [],
                            createdAt: new Date().toISOString(),
                            fromCapsule: viewingCapsule.name
                          };
                          setPackingLists(prev => [...prev, newList]);
                          toast({ title: '🧳 Packing List Created', description: `${viewingCapsule.items.length} items added from "${viewingCapsule.name}"` });
                          setWardrobeMode('packing');
                          setViewingPackingList(newList);
                          setViewingCapsule(null);
                        } else {
                          // Show trip picker dialog
                          setCapsuleForTripPicker(viewingCapsule);
                          setShowTripPickerForCapsule(true);
                        }
                      }}
                      className="rounded-xl border-mud-200 text-mud-600 hover:bg-mud-50"
                    >
                      <Luggage className="w-4 h-4 mr-1" />
                      Pack This
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { 
                        const idx = capsules.findIndex(c => c.id === viewingCapsule.id);
                        setCurrentCapsule(viewingCapsule); 
                        setEditingCapsule(idx); 
                        setShowCapsuleDialog(true); 
                      }}
                      className="rounded-xl border-suede-200"
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit Capsule
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { deleteCapsule(viewingCapsule.id); setViewingCapsule(null); }}
                      disabled={deletingCapsuleId === viewingCapsule.id}
                      className="rounded-xl border-red-200 text-red-500 hover:bg-red-50"
                    >
                      {deletingCapsuleId === viewingCapsule.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Capsule List View (no capsule selected) */}
            {!viewingCapsule && (
              <>
                {capsules.length === 0 ? (
                  <div className="text-center py-16 bg-white/50 rounded-2xl border border-dashed border-suede-200">
                    <Archive className="w-16 h-16 text-suede-200 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-stone-600 mb-2">No Capsule Wardrobes Yet</h3>
                    <p className="text-stone-400 mb-6 max-w-md mx-auto">
                      Create a capsule for seasons, vacations, or minimalist challenges. Mix and match your favorites!
                    </p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {capsules.map((capsule) => (
                      <motion.div 
                        key={capsule.id} 
                        className="bg-white rounded-2xl p-5 shadow-sm border border-suede-100 hover:shadow-lg transition-shadow cursor-pointer"
                        onClick={() => setViewingCapsule(capsule)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-stone-800">{capsule.name}</h3>
                            <Badge variant="outline" className="text-xs mt-1">{capsule.type}</Badge>
                          </div>
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="ghost" onClick={() => { setCurrentCapsule(capsule); setEditingCapsule(capsules.indexOf(capsule)); setShowCapsuleDialog(true); }}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500" disabled={deletingCapsuleId === capsule.id} onClick={() => deleteCapsule(capsule.id)}>
                              {deletingCapsuleId === capsule.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                        {capsule.description && <p className="text-sm text-stone-500 mb-3">{capsule.description}</p>}
                        <div className="flex flex-wrap gap-1 mb-3">
                          {capsule.items.slice(0, 6).map((item, i) => (
                            <div key={i} className="w-10 h-10 rounded-lg overflow-hidden bg-stone-100">
                              {item.image_url ? <img src={item.image_url} alt="" className="w-full h-full object-cover" /> : <span className="text-lg">👕</span>}
                            </div>
                          ))}
                          {capsule.items.length > 6 && (
                            <div className="w-10 h-10 rounded-lg bg-suede-100 flex items-center justify-center text-xs font-medium text-suede-600">
                              +{capsule.items.length - 6}
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-stone-400">{capsule.items.length} items · Click to view</p>
                      </motion.div>
                    ))}
                  </div>
                )}
              </>
            )}
            
            {/* Capsule Detail View (categorized) */}
            {viewingCapsule && (
              <div className="space-y-6">
                {viewingCapsule.items.length === 0 ? (
                  <div className="text-center py-16 bg-white/50 rounded-2xl border border-dashed border-suede-200">
                    <Shirt className="w-16 h-16 text-suede-200 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-stone-600 mb-2">No Items in This Capsule</h3>
                    <p className="text-stone-400 mb-6">Edit this capsule to add items from your wardrobe.</p>
                    <Button 
                      onClick={() => { 
                        const idx = capsules.findIndex(c => c.id === viewingCapsule.id);
                        setCurrentCapsule(viewingCapsule); 
                        setEditingCapsule(idx); 
                        setShowCapsuleDialog(true); 
                      }}
                      className="bg-gradient-to-r from-suede-500 to-suede-400 text-white rounded-xl"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Items
                    </Button>
                  </div>
                ) : (() => {
                  // Group capsule items by primary_category
                  const grouped = {};
                  viewingCapsule.items.forEach(item => {
                    const cat = item.primary_category || 'other';
                    if (!grouped[cat]) grouped[cat] = [];
                    grouped[cat].push(item);
                  });
                  // Sort items by color within each category
                  for (const k of Object.keys(grouped)) grouped[k].sort(sortItemsByColor);
                  const categoryOrder = ['tops', 'bottoms', 'dresses', 'outerwear', 'sportswear', 'footwear', 'accessories', 'swimwear', 'other'];
                  const sortedCategories = Object.keys(grouped).sort((a, b) => {
                    const ai = categoryOrder.indexOf(a);
                    const bi = categoryOrder.indexOf(b);
                    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                  });
                  
                  return sortedCategories.map(catKey => {
                    const catItems = grouped[catKey];
                    const IconComp = CATEGORY_ICONS[catKey] || Shirt;
                    const catLabel = categories?.categories?.[catKey]?.name || catKey.charAt(0).toUpperCase() + catKey.slice(1);
                    
                    return (
                      <div key={catKey} className="bg-white/80 rounded-2xl border border-stone-100 overflow-hidden">
                        {/* Category Header */}
                        <div className="flex items-center justify-between px-5 py-3 bg-stone-50/80 border-b border-stone-100">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center text-white">
                              {typeof IconComp === 'function' ? <IconComp className="w-4 h-4" /> : <Shirt className="w-4 h-4" />}
                            </div>
                            <span className="font-semibold text-stone-700">{catLabel}</span>
                            <Badge variant="secondary" className="text-xs">{catItems.length}</Badge>
                          </div>
                        </div>
                        
                        {/* Items Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
                          {catItems.map((item) => (
                            <motion.div
                              key={item.id}
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              className="bg-white rounded-xl overflow-hidden shadow-sm border border-stone-100 hover:shadow-md transition-shadow group relative"
                            >
                              <div className="relative aspect-[3/4] bg-stone-50">
                                {item.image_url ? (
                                  <img src={item.processed_image_url || item.image_url} alt={item.name} className="w-full h-full object-contain" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Shirt className="w-10 h-10 text-stone-200" />
                                  </div>
                                )}
                                {/* Hover overlay with actions */}
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                  <button 
                                    onClick={() => { setSelectedItem(item); setShowItemDetail(true); }}
                                    className="bg-white/90 p-2 rounded-full hover:bg-white transition-colors"
                                    title="View"
                                  >
                                    <Eye className="w-4 h-4 text-stone-700" />
                                  </button>
                                  <button 
                                    onClick={() => {
                                      setEditingItemId(item.id);
                                      setEditForm({
                                        name: item.name || '', primaryCategory: item.primary_category || '', clothingType: item.clothing_type || '',
                                        brand: item.brand || '', price: item.purchase_price || '', color: item.color || '',
                                        size: item.size || '', material: item.material || '', season: item.season || '',
                                        fitType: item.fit_type || '', condition: item.condition || '', notes: item.notes || ''
                                      });
                                      setShowEditDialog(true);
                                    }}
                                    className="bg-white/90 p-2 rounded-full hover:bg-white transition-colors"
                                    title="Edit"
                                  >
                                    <Pencil className="w-4 h-4 text-suede-600" />
                                  </button>
                                  <button 
                                    onClick={() => {
                                      // Remove item from this capsule
                                      const updatedCapsule = { ...viewingCapsule, items: viewingCapsule.items.filter(i => i.id !== item.id) };
                                      setCapsules(prev => prev.map(c => c.id === viewingCapsule.id ? updatedCapsule : c));
                                      setViewingCapsule(updatedCapsule);
                                      toast({ title: 'Removed', description: `${item.name} removed from capsule` });
                                    }}
                                    className="bg-white/90 p-2 rounded-full hover:bg-red-50 transition-colors"
                                    title="Remove from capsule"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-400" />
                                  </button>
                                </div>
                              </div>
                              <div className="p-3">
                                <h4 className="font-medium text-sm text-stone-800 truncate">{item.name}</h4>
                                <div className="flex items-center justify-between mt-1">
                                  <span className="text-xs text-stone-400">{item.clothing_type || item.primary_category}</span>
                                  {item.color && (
                                    <div className="flex items-center gap-1">
                                      <div className="w-3 h-3 rounded-full border border-stone-200" style={{ backgroundColor: item.color?.toLowerCase() }} />
                                      <span className="text-xs text-stone-400">{item.color}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        )}

        {/* ========== PACKING LISTS VIEW ========== */}
        {wardrobeMode === 'packing' && (
          <div className="mb-8">
            {(() => {
              const coverUrl = viewingPackingList?.coverImageUrl || viewingPackingList?.cover_image_url;
              const hasCover = viewingPackingList && coverUrl;
              return (
                <div className="rounded-2xl mb-6 overflow-hidden border border-mud-100 bg-white">
                  {/* Cover photo section */}
                  {hasCover && (
                    <div className="relative h-[180px] sm:h-[220px]">
                      <img src={coverUrl} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
                      <div className="absolute top-3 left-3 z-10">
                        {viewingPackingList && (
                          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 backdrop-blur-sm bg-black/20 rounded-full" onClick={() => setViewingPackingList(null)}>
                            <ArrowLeft className="w-5 h-5" />
                          </Button>
                        )}
                      </div>
                      <div className="absolute top-3 right-3 z-10">
                        <label className="inline-flex items-center gap-1 cursor-pointer rounded-full px-3 py-1.5 text-xs font-medium text-white bg-black/30 backdrop-blur-sm hover:bg-black/50 transition-colors">
                          <Camera className="w-3.5 h-3.5" />
                          Change
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => { updatePackingCover(viewingPackingList.id, e.target.files?.[0]); e.target.value = ''; }} />
                        </label>
                      </div>
                      <div className="absolute bottom-3 left-4 right-4 z-10">
                        <h2 className="text-xl sm:text-2xl font-bold text-white drop-shadow-lg">
                          {viewingPackingList.name}
                        </h2>
                        <p className="text-sm text-white/90 drop-shadow-md mt-0.5">
                          {(viewingPackingList.items || []).length} items{viewingPackingList.destination ? ` · ${viewingPackingList.destination}` : ''}{viewingPackingList.startDate ? ` · ${new Date(viewingPackingList.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} → ${new Date(viewingPackingList.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                        </p>
                      </div>
                    </div>
                  )}
                  {/* Info bar (no cover) */}
                  {!hasCover && (
                    <div className="p-4 sm:p-6 bg-gradient-to-r from-mud-50 to-mud-50">
                      <div className="flex items-center gap-3 min-w-0">
                        {viewingPackingList && (
                          <Button variant="ghost" size="sm" className="mr-1 flex-shrink-0" onClick={() => setViewingPackingList(null)}>
                            <ArrowLeft className="w-5 h-5" />
                          </Button>
                        )}
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-mud-500 to-mud-400">
                          <Luggage className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div className="min-w-0">
                          <h2 className="text-lg sm:text-xl font-semibold truncate text-stone-800">
                            {viewingPackingList ? viewingPackingList.name : 'Packing Lists'}
                          </h2>
                          <p className="text-sm text-stone-500">
                            {viewingPackingList 
                              ? `${(viewingPackingList.items || []).length} items${viewingPackingList.destination ? ` · ${viewingPackingList.destination}` : ''}${viewingPackingList.startDate ? ` · ${new Date(viewingPackingList.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} → ${new Date(viewingPackingList.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}`
                              : 'Plan your travel wardrobe and track what\'s packed'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Action buttons — always visible below */}
                  <div className="px-4 py-3 flex flex-wrap gap-2 border-t border-mud-100 bg-white">
                    {!viewingPackingList ? (
                      <Button 
                        onClick={() => { setCurrentPackingList({ name: '', destination: '', startDate: '', endDate: '', items: [], packedItems: [], coverImage: '' }); setEditingPackingList(null); setShowPackingDialog(true); }}
                        className="bg-gradient-to-r from-mud-500 to-mud-400 text-white rounded-xl"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        New Trip
                      </Button>
                    ) : (
                      <>
                        {capsules.length > 0 && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedCapsuleForPacking(null);
                              setSelectedCapsuleItems(new Set());
                              setShowCapsulePickerForPacking(true);
                            }}
                            className="rounded-xl border-suede-200 text-suede-600 hover:bg-suede-50"
                          >
                            <Archive className="w-4 h-4 mr-1" />
                            Add from Capsule
                          </Button>
                        )}
                        {!hasCover && (
                          <label className="inline-flex items-center gap-1 cursor-pointer rounded-xl border border-mud-200 px-3 h-9 text-sm font-medium text-mud-600 hover:bg-mud-50 transition-colors">
                            <Camera className="w-4 h-4" />
                            Add Photo
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => { updatePackingCover(viewingPackingList.id, e.target.files?.[0]); e.target.value = ''; }} />
                          </label>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => { 
                            const idx = packingLists.findIndex(p => p.id === viewingPackingList.id);
                            setCurrentPackingList({ ...viewingPackingList, coverImage: viewingPackingList.coverImageUrl || viewingPackingList.cover_image_url || '' }); 
                            setEditingPackingList(idx); 
                            setShowPackingDialog(true); 
                          }}
                          className="rounded-xl border-mud-200"
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit Trip
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => { deletePackingList(viewingPackingList.id); setViewingPackingList(null); }}
                          disabled={deletingPackingId === viewingPackingList.id}
                          className="rounded-xl border-red-200 text-red-500 hover:bg-red-50"
                        >
                          {deletingPackingId === viewingPackingList.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
            
            {/* Packing List View (no list selected) */}
            {!viewingPackingList && (
              <>
                {packingLists.length === 0 ? (
                  <div className="text-center py-16 bg-white/50 rounded-2xl border border-dashed border-mud-200">
                    <Plane className="w-16 h-16 text-mud-200 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-stone-600 mb-2">No Packing Lists Yet</h3>
                    <p className="text-stone-400 mb-6 max-w-md mx-auto">
                      Planning a trip? Create a packing list to ensure you bring everything you need.
                    </p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {packingLists.map((list) => {
                      const listCover = list.coverImageUrl || list.cover_image_url;
                      return (
                        <motion.div 
                          key={list.id} 
                          className={`rounded-2xl shadow-sm hover:shadow-lg transition-shadow cursor-pointer overflow-hidden relative ${!listCover ? 'bg-white border border-mud-100' : ''}`}
                          onClick={() => setViewingPackingList(list)}
                        >
                          {/* Cover image background */}
                          {listCover && (
                            <>
                              <img src={listCover} alt="" className="absolute inset-0 w-full h-full object-cover" />
                              <div className="absolute inset-0 backdrop-blur-[2px] bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
                            </>
                          )}
                          <div className={`relative z-10 p-5 ${listCover ? 'text-white' : ''}`}>
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h3 className={`font-semibold ${listCover ? 'text-white drop-shadow-md' : 'text-stone-800'}`}>{list.name}</h3>
                                {list.destination && (
                                  <div className={`flex items-center gap-1 text-sm mt-1 ${listCover ? 'text-white/80' : 'text-mud-600'}`}>
                                    <MapPin className="w-3 h-3" />
                                    {list.destination}
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                <label className={`inline-flex items-center justify-center w-9 h-9 cursor-pointer rounded-md transition-colors ${listCover ? 'text-white hover:bg-white/20' : 'hover:bg-stone-100'}`}>
                                  <Camera className="w-4 h-4" />
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { updatePackingCover(list.id, e.target.files?.[0]); e.target.value = ''; }} />
                                </label>
                                <Button size="sm" variant="ghost" className={listCover ? 'text-white hover:bg-white/20' : ''} onClick={() => { 
                                  const idx = packingLists.indexOf(list);
                                  setCurrentPackingList({ ...list, coverImage: list.coverImageUrl || list.cover_image_url || '' }); 
                                  setEditingPackingList(idx); 
                                  setShowPackingDialog(true); 
                                }}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className={listCover ? 'text-red-300 hover:bg-red-500/20' : 'text-red-500'} disabled={deletingPackingId === list.id} onClick={() => deletePackingList(list.id)}>
                                  {deletingPackingId === list.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                </Button>
                              </div>
                            </div>
                            {list.startDate && <p className={`text-xs mb-3 ${listCover ? 'text-white/70' : 'text-stone-400'}`}>{new Date(list.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} → {new Date(list.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>}
                            <div className="flex flex-wrap gap-1 mb-3">
                              {list.items.slice(0, 6).map((item, i) => (
                                <div key={i} className="w-10 h-10 rounded-lg overflow-hidden bg-white/20 backdrop-blur-sm border border-white/10">
                                  {item.image_url ? <img src={item.image_url} alt="" className="w-full h-full object-cover" /> : <span className="text-lg">👕</span>}
                                </div>
                              ))}
                              {list.items.length > 6 && (
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-medium ${listCover ? 'bg-white/20 text-white' : 'bg-mud-100 text-mud-600'}`}>
                                  +{list.items.length - 6}
                                </div>
                              )}
                            </div>
                            <div className="mt-2">
                              <Progress value={((list.packedItems || []).length / Math.max((list.items || []).length, 1)) * 100} className="h-2" />
                              <p className={`text-xs mt-1 ${listCover ? 'text-white/70' : 'text-stone-400'}`}>{(list.packedItems || []).length}/{(list.items || []).length} packed · Click to view</p>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
            
            {/* Packing List Detail View (categorized with checklist) */}
            {viewingPackingList && (
              <div className="space-y-6">
                {/* Packing Progress Summary */}
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-mud-100">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-stone-700">Packing Progress</h3>
                    <span className="text-sm font-medium text-mud-600">
                      {(viewingPackingList.packedItems || []).length}/{(viewingPackingList.items || []).length} packed
                    </span>
                  </div>
                  <Progress value={((viewingPackingList.packedItems || []).length / Math.max((viewingPackingList.items || []).length, 1)) * 100} className="h-3" />
                </div>

                {viewingPackingList.items.length === 0 ? (
                  <div className="text-center py-16 bg-white/50 rounded-2xl border border-dashed border-mud-200">
                    <Shirt className="w-16 h-16 text-mud-200 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-stone-600 mb-2">No Items in This Trip</h3>
                    <p className="text-stone-400 mb-6">Edit this trip to add items from your wardrobe.</p>
                    <Button 
                      onClick={() => { 
                        const idx = packingLists.findIndex(p => p.id === viewingPackingList.id);
                        setCurrentPackingList(viewingPackingList); 
                        setEditingPackingList(idx); 
                        setShowPackingDialog(true); 
                      }}
                      className="bg-gradient-to-r from-mud-500 to-mud-400 text-white rounded-xl"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Items
                    </Button>
                  </div>
                ) : (() => {
                  // Group packing list items by primary_category
                  const grouped = {};
                  viewingPackingList.items.forEach(item => {
                    const cat = item.primary_category || 'other';
                    if (!grouped[cat]) grouped[cat] = [];
                    grouped[cat].push(item);
                  });
                  // Sort items by color within each category
                  for (const k of Object.keys(grouped)) grouped[k].sort(sortItemsByColor);
                  const categoryOrder = ['tops', 'bottoms', 'dresses', 'outerwear', 'sportswear', 'footwear', 'accessories', 'swimwear', 'other'];
                  const sortedCategories = Object.keys(grouped).sort((a, b) => {
                    const ai = categoryOrder.indexOf(a);
                    const bi = categoryOrder.indexOf(b);
                    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                  });
                  
                  return sortedCategories.map(catKey => {
                    const catItems = grouped[catKey];
                    const IconComp = CATEGORY_ICONS[catKey] || Shirt;
                    const catLabel = categories?.categories?.[catKey]?.name || catKey.charAt(0).toUpperCase() + catKey.slice(1);
                    const packedInCategory = catItems.filter(item => (viewingPackingList.packedItems || []).includes(item.id)).length;
                    
                    return (
                      <div key={catKey} className="bg-white/80 rounded-2xl border border-stone-100 overflow-hidden">
                        {/* Category Header */}
                        <div className="flex items-center justify-between px-5 py-3 bg-stone-50/80 border-b border-stone-100">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-mud-500 to-mud-400 flex items-center justify-center text-white">
                              {typeof IconComp === 'function' ? <IconComp className="w-4 h-4" /> : <Shirt className="w-4 h-4" />}
                            </div>
                            <span className="font-semibold text-stone-700">{catLabel}</span>
                            <Badge variant="secondary" className="text-xs">{catItems.length}</Badge>
                          </div>
                          <span className="text-xs text-stone-400">{packedInCategory}/{catItems.length} packed</span>
                        </div>
                        
                        {/* Items Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
                          {catItems.map((item) => {
                            const isPacked = (viewingPackingList.packedItems || []).includes(item.id);
                            return (
                              <motion.div
                                key={item.id}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className={`bg-white rounded-xl overflow-hidden shadow-sm border transition-shadow group relative ${
                                  isPacked ? 'border-green-200 bg-green-50/30' : 'border-stone-100 hover:shadow-md'
                                }`}
                              >
                                <div className={`relative aspect-[3/4] bg-stone-50 ${isPacked ? 'opacity-60' : ''}`}>
                                  {item.image_url ? (
                                    <img src={item.processed_image_url || item.image_url} alt={item.name} className="w-full h-full object-contain" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Shirt className="w-10 h-10 text-stone-200" />
                                    </div>
                                  )}
                                  {/* Packed checkmark overlay */}
                                  {isPacked && (
                                    <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-green-500 flex items-center justify-center shadow-md">
                                      <Check className="w-4 h-4 text-white" />
                                    </div>
                                  )}
                                  {/* Hover overlay with actions */}
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                    <button 
                                      onClick={() => { setSelectedItem(item); setShowItemDetail(true); }}
                                      className="bg-white/90 p-2 rounded-full hover:bg-white transition-colors"
                                      title="View"
                                    >
                                      <Eye className="w-4 h-4 text-stone-700" />
                                    </button>
                                    <button 
                                      onClick={() => {
                                        setEditingItemId(item.id);
                                        setEditForm({
                                          name: item.name || '', primaryCategory: item.primary_category || '', clothingType: item.clothing_type || '',
                                          brand: item.brand || '', price: item.purchase_price || '', color: item.color || '',
                                          size: item.size || '', material: item.material || '', season: item.season || '',
                                          fitType: item.fit_type || '', condition: item.condition || '', notes: item.notes || ''
                                        });
                                        setShowEditDialog(true);
                                      }}
                                      className="bg-white/90 p-2 rounded-full hover:bg-white transition-colors"
                                      title="Edit"
                                    >
                                      <Pencil className="w-4 h-4 text-mud-600" />
                                    </button>
                                    <button 
                                      onClick={() => {
                                        // Remove item from this packing list
                                        const updatedList = { ...viewingPackingList, items: (viewingPackingList.items || []).filter(i => i.id !== item.id), packedItems: (viewingPackingList.packedItems || []).filter(id => id !== item.id) };
                                        setPackingLists(prev => prev.map(p => p.id === viewingPackingList.id ? updatedList : p));
                                        setViewingPackingList(updatedList);
                                        toast({ title: 'Removed', description: `${item.name} removed from packing list` });
                                      }}
                                      className="bg-white/90 p-2 rounded-full hover:bg-red-50 transition-colors"
                                      title="Remove from list"
                                    >
                                      <Trash2 className="w-4 h-4 text-red-400" />
                                    </button>
                                  </div>
                                </div>
                                <div className="p-3">
                                  <div className="flex items-center gap-2">
                                    <label className="flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                      <input 
                                        type="checkbox" 
                                        checked={isPacked}
                                        onChange={() => {
                                          togglePackedItem(viewingPackingList.id, item.id);
                                          // Sync viewingPackingList state
                                          setViewingPackingList(prev => ({
                                            ...prev,
                                            packedItems: isPacked 
                                              ? prev.packedItems.filter(id => id !== item.id)
                                              : [...prev.packedItems, item.id]
                                          }));
                                        }}
                                        className="w-4 h-4 rounded border-mud-300 text-mud-500 mr-2"
                                      />
                                    </label>
                                    <h4 className={`font-medium text-sm truncate ${isPacked ? 'line-through text-stone-400' : 'text-stone-800'}`}>{item.name}</h4>
                                  </div>
                                  <div className="flex items-center justify-between mt-1">
                                    <span className="text-xs text-stone-400">{item.clothing_type || item.primary_category}</span>
                                    {item.color && (
                                      <div className="flex items-center gap-1">
                                        <div className="w-3 h-3 rounded-full border border-stone-200" style={{ backgroundColor: item.color?.toLowerCase() }} />
                                        <span className="text-xs text-stone-400">{item.color}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        )}

        {/* ========== OUTFIT CALENDAR VIEW ========== */}
        {wardrobeMode === 'calendar' && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-olive-50 to-olive-50 rounded-2xl p-6 mb-6 border border-olive-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {viewingCalendarOutfit && (
                    <Button variant="ghost" size="sm" className="mr-1" onClick={() => setViewingCalendarOutfit(null)}>
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                  )}
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-olive-500 to-olive-400 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-stone-800">
                      {viewingCalendarOutfit 
                        ? viewingCalendarOutfit.date.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric' })
                        : 'Outfit Calendar'
                      }
                    </h2>
                    <p className="text-sm text-stone-500">
                      {viewingCalendarOutfit 
                        ? `${getDaySlots(viewingCalendarOutfit.dayData).length} outfit${getDaySlots(viewingCalendarOutfit.dayData).length !== 1 ? 's' : ''} · ${getDayItemCount(viewingCalendarOutfit.dayData)} pieces`
                        : 'Tap a date to add or edit your outfits'
                      }
                    </p>
                  </div>
                </div>
                {!viewingCalendarOutfit ? (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1))}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-4 py-2 font-medium">
                      {selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </span>
                    <Button variant="outline" onClick={() => setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1))}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openCalendarDate(viewingCalendarOutfit.date)}
                      className="rounded-xl border-olive-200"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Outfit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const key = formatDateKey(viewingCalendarOutfit.date);
                        setOutfitCalendar(prev => {
                          const updated = { ...prev };
                          delete updated[key];
                          return updated;
                        });
                        setViewingCalendarOutfit(null);
                        toast({ title: 'Removed', description: 'All outfits removed from this date' });
                      }}
                      className="rounded-xl border-red-200 text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Clear Day
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Calendar Grid View */}
            {!viewingCalendarOutfit && (
              <>
                <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm border border-olive-100">
                  <div className="grid grid-cols-7 gap-1 md:gap-2 mb-3">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="text-center text-xs font-medium text-stone-500 py-2">{day}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1 md:gap-2">
                    {getDaysInMonth(selectedDate).map((day, i) => {
                      const dayData = day ? outfitCalendar[formatDateKey(day)] : null;
                      const isToday = day?.toDateString() === new Date().toDateString();
                      const daySlots = getDaySlots(dayData);
                      const hasOutfit = daySlots.length > 0;
                      return (
                        <motion.button
                          key={i}
                          whileHover={{ scale: day ? 1.03 : 1 }}
                          whileTap={{ scale: day ? 0.97 : 1 }}
                          onClick={() => {
                            if (!day) return;
                            if (hasOutfit) {
                              setViewingCalendarOutfit({ date: day, dayData });
                              setViewingSlotIndex(0);
                            } else {
                              openCalendarDate(day);
                            }
                          }}
                          className={`min-h-[70px] md:min-h-[90px] rounded-xl flex flex-col items-center justify-start pt-1.5 pb-1 px-0.5 text-sm transition-all relative overflow-hidden ${
                            day
                              ? hasOutfit
                                ? 'bg-olive-50 text-olive-700 border-2 border-olive-300 hover:border-olive-400 hover:shadow-md'
                                : 'bg-stone-50 hover:bg-olive-50 text-stone-700 border border-stone-100 hover:border-olive-200'
                              : 'bg-transparent'
                          } ${isToday ? 'ring-2 ring-olive-500 ring-offset-1' : ''}`}
                        >
                          {day && (
                            <>
                              <span className={`font-semibold text-xs z-10 mb-0.5 ${hasOutfit ? 'text-olive-700' : ''}`}>{day.getDate()}</span>
                              {hasOutfit ? (
                                <div className="flex flex-col items-center gap-0.5 w-full px-0.5">
                                  <div className="flex items-center gap-0.5 flex-wrap justify-center">
                                    {daySlots.slice(0, 3).map((slot, idx) => {
                                      const timeOpt = getTimeOption(slot.timeOfDay);
                                      const TimeIcon = timeOpt.icon;
                                      return <TimeIcon key={idx} className={`w-2.5 h-2.5 md:w-3 md:h-3 ${timeOpt.textColor}`} />;
                                    })}
                                  </div>
                                  <span className="text-[9px] md:text-[10px] font-semibold text-olive-600 leading-tight text-center">
                                    {daySlots.length} outfit{daySlots.length !== 1 ? 's' : ''}
                                  </span>
                                  <span className="text-[8px] text-olive-400 leading-tight">
                                    {getDayItemCount(dayData)} piece{getDayItemCount(dayData) !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex-1 flex items-center justify-center">
                                  <Plus className="w-4 h-4 text-stone-300" />
                                </div>
                              )}
                            </>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>

                {/* Saved Outfits Below Calendar */}
                {(() => {
                  const allOutfits = Object.entries(outfitCalendar)
                    .sort(([a], [b]) => new Date(a) - new Date(b));
                  if (allOutfits.length === 0) return null;
                  
                  const today = new Date();
                  const todayStr = formatDateKey(today);
                  const upcoming = allOutfits.filter(([key]) => key >= todayStr);
                  const past = allOutfits.filter(([key]) => key < todayStr).reverse();
                  
                  const renderOutfitCard = ([dateKey, dayData]) => {
                    const date = new Date(dateKey + 'T00:00:00');
                    const slots = getDaySlots(dayData);
                    const totalItems = getDayItemCount(dayData);
                    return (
                      <motion.button
                        key={dateKey}
                        whileHover={{ scale: 1.01 }}
                        onClick={() => { setViewingCalendarOutfit({ date, dayData }); setViewingSlotIndex(0); }}
                        className="w-full bg-white rounded-2xl p-4 shadow-sm border border-olive-100 hover:shadow-md hover:border-olive-200 transition-all text-left"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="text-center min-w-[48px] bg-olive-50 rounded-xl p-2">
                              <p className="text-[10px] text-olive-500 font-medium uppercase">{date.toLocaleString('default', { weekday: 'short' })}</p>
                              <p className="text-xl font-bold text-olive-700">{date.getDate()}</p>
                              <p className="text-[10px] text-olive-400">{date.toLocaleString('default', { month: 'short' })}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-stone-700">{date.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                              <div className="flex items-center gap-2 flex-wrap mt-1">
                                {slots.map((slot, idx) => {
                                  const timeOpt = getTimeOption(slot.timeOfDay);
                                  const TimeIcon = timeOpt.icon;
                                  return (
                                    <span key={idx} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${timeOpt.bgColor} ${timeOpt.textColor} font-medium`}>
                                      <TimeIcon className="w-2.5 h-2.5" />
                                      {timeOpt.label}{slot.occasion ? ` · ${slot.occasion}` : ''}
                                    </span>
                                  );
                                })}
                                <span className="text-xs text-stone-400">{totalItems} pieces</span>
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-stone-300" />
                        </div>
                        {/* Show first slot's items as preview */}
                        {slots[0] && (
                          <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                            {(slots[0].items || []).slice(0, 6).map((item, idx) => (
                              <div key={idx} className="aspect-[3/4] rounded-xl overflow-hidden bg-stone-50 border border-stone-100">
                                {item.image_url ? (
                                  <img src={item.processed_image_url || item.image_url} alt={item.name || ''} className="w-full h-full object-contain" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Shirt className="w-6 h-6 text-stone-200" />
                                  </div>
                                )}
                              </div>
                            ))}
                            {(slots[0].items?.length || 0) > 6 && (
                              <div className="aspect-[3/4] rounded-xl bg-olive-50 flex items-center justify-center border border-olive-100">
                                <span className="text-sm font-medium text-olive-600">+{slots[0].items.length - 6}</span>
                              </div>
                            )}
                          </div>
                        )}
                        {slots.length > 1 && (
                          <p className="text-xs text-olive-500 mt-2 font-medium">+ {slots.length - 1} more outfit{slots.length - 1 > 1 ? 's' : ''} this day</p>
                        )}
                      </motion.button>
                    );
                  };

                  return (
                    <div className="mt-4 space-y-4">
                      {upcoming.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-stone-700 mb-3 flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-olive-500" />
                            Upcoming Outfits
                          </h3>
                          <div className="space-y-3">
                            {upcoming.slice(0, 5).map(renderOutfitCard)}
                          </div>
                        </div>
                      )}
                      {past.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-stone-700 mb-3 flex items-center gap-2">
                            <Clock className="w-4 h-4 text-stone-400" />
                            Past Outfits
                          </h3>
                          <div className="space-y-3">
                            {past.slice(0, 5).map(renderOutfitCard)}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}

            {/* Full Day Detail View — one-by-one outfit viewer */}
            {viewingCalendarOutfit && (
              <div className="space-y-4">
                {(() => {
                  const slots = getDaySlots(viewingCalendarOutfit.dayData);
                  if (slots.length === 0) return (
                    <div className="text-center py-16 bg-white/50 rounded-2xl border border-dashed border-olive-200">
                      <Shirt className="w-16 h-16 text-olive-200 mx-auto mb-4" />
                      <h3 className="text-xl font-medium text-stone-600 mb-2">No Outfits Planned</h3>
                      <p className="text-stone-400 mb-6">Add an outfit for this day.</p>
                      <Button 
                        onClick={() => openCalendarDate(viewingCalendarOutfit.date)}
                        className="bg-gradient-to-r from-olive-500 to-olive-400 text-white rounded-xl"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Outfit
                      </Button>
                    </div>
                  );

                  // Clamp index
                  const safeIndex = Math.min(viewingSlotIndex, slots.length - 1);
                  const slot = slots[safeIndex];
                  if (!slot) return null;
                  const timeOpt = getTimeOption(slot.timeOfDay);
                  const TimeIcon = timeOpt.icon;
                  const slotItems = slot.items || [];

                  return (
                    <>
                      {/* Outfit navigation bar — "Outfit 1 of 3" with prev/next */}
                      {slots.length > 1 && (
                        <div className="flex items-center justify-between bg-white rounded-2xl px-4 py-3 border border-stone-100 shadow-sm">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={safeIndex === 0}
                            onClick={() => setViewingSlotIndex(Math.max(0, safeIndex - 1))}
                            className="h-8 rounded-lg disabled:opacity-30"
                          >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            Previous
                          </Button>
                          <div className="flex items-center gap-2">
                            {slots.map((_, idx) => (
                              <button
                                key={idx}
                                onClick={() => setViewingSlotIndex(idx)}
                                className={`w-2 h-2 rounded-full transition-all ${
                                  idx === safeIndex ? 'bg-olive-600 w-5' : 'bg-stone-300 hover:bg-stone-400'
                                }`}
                              />
                            ))}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={safeIndex >= slots.length - 1}
                            onClick={() => setViewingSlotIndex(Math.min(slots.length - 1, safeIndex + 1))}
                            className="h-8 rounded-lg disabled:opacity-30"
                          >
                            Next
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      )}

                      {/* Current outfit card */}
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={slot.id}
                          initial={{ opacity: 0, x: 30 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -30 }}
                          transition={{ duration: 0.25 }}
                          className="bg-white rounded-2xl border border-stone-100 overflow-hidden shadow-sm"
                        >
                          {/* Slot header — time of day + occasion + counter */}
                          <div className={`flex items-center justify-between px-5 py-3 ${timeOpt.bgColor} border-b border-stone-100`}>
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${timeOpt.color} flex items-center justify-center text-white shadow-sm`}>
                                <TimeIcon className="w-5 h-5" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className={`font-semibold ${timeOpt.textColor}`}>{timeOpt.label}</span>
                                  {slot.occasion && (
                                    <Badge className={`text-[10px] px-2 py-0.5 ${timeOpt.bgColor} ${timeOpt.textColor} border-none font-medium`}>
                                      {slot.occasion}
                                    </Badge>
                                  )}
                                  <span className="text-[10px] text-stone-400 font-medium">
                                    Outfit {safeIndex + 1} of {slots.length}
                                  </span>
                                </div>
                                <p className="text-xs text-stone-400">{slotItems.length} piece{slotItems.length !== 1 ? 's' : ''}{slot.note ? ` · ${slot.note}` : ''}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              {/* Mark as Worn */}
                              <Button 
                                variant={slot.worn ? "default" : "ghost"}
                                size="sm"
                                onClick={async () => {
                                  if (slot.worn) return;
                                  const key = formatDateKey(viewingCalendarOutfit.date);
                                  setOutfitCalendar(prev => {
                                    const dayData = prev[key];
                                    if (!dayData) return prev;
                                    const updatedSlots = (dayData.slots || []).map(s => 
                                      s.id === slot.id ? { ...s, worn: true } : s
                                    );
                                    return { ...prev, [key]: { slots: updatedSlots } };
                                  });
                                  for (const item of slotItems) {
                                    try {
                                      await axios.post(`${API_URL}/real-wardrobe/${item.id}/wear`, {}, { 
                                        headers: { Authorization: `Bearer ${token}` } 
                                      });
                                    } catch (e) { console.error('Wear log error:', e); }
                                  }
                                  setViewingCalendarOutfit(prev => ({
                                    ...prev,
                                    dayData: {
                                      slots: getDaySlots(prev.dayData).map(s => 
                                        s.id === slot.id ? { ...s, worn: true } : s
                                      )
                                    }
                                  }));
                                  fetchWardrobe();
                                  toast({ title: "✅ Outfit Worn!", description: `Logged ${slotItems.length} item${slotItems.length !== 1 ? 's' : ''} as worn` });
                                }}
                                className={slot.worn 
                                  ? "h-8 px-2 rounded-lg bg-green-100 text-green-700 text-xs" 
                                  : "h-8 px-2 rounded-lg hover:bg-green-50 text-xs"
                                }
                                title={slot.worn ? "Marked as worn" : "Mark as worn"}
                              >
                                {slot.worn ? <><Check className="w-3.5 h-3.5 mr-1" />Worn</> : <><Check className="w-3.5 h-3.5 mr-1 text-stone-400" />Wore it</>}
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openCalendarDate(viewingCalendarOutfit.date, slot)}
                                className="h-8 w-8 p-0 rounded-lg hover:bg-white/60"
                                title="Edit"
                              >
                                <Pencil className="w-3.5 h-3.5 text-stone-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  const key = formatDateKey(viewingCalendarOutfit.date);
                                  removeCalendarSlot(key, slot.id);
                                  const updatedDayData = outfitCalendar[key];
                                  if (updatedDayData) {
                                    const remainingSlots = getDaySlots(updatedDayData).filter(s => s.id !== slot.id);
                                    if (remainingSlots.length > 0) {
                                      setViewingCalendarOutfit({ date: viewingCalendarOutfit.date, dayData: { slots: remainingSlots } });
                                      setViewingSlotIndex(Math.min(safeIndex, remainingSlots.length - 1));
                                    } else {
                                      setViewingCalendarOutfit(null);
                                      setViewingSlotIndex(0);
                                    }
                                  } else {
                                    setViewingCalendarOutfit(null);
                                    setViewingSlotIndex(0);
                                  }
                                }}
                                className="h-8 w-8 p-0 rounded-lg hover:bg-red-50"
                                title="Remove"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              </Button>
                            </div>
                          </div>

                          {/* Slot items grouped by category */}
                          {(() => {
                            const grouped = {};
                            slotItems.forEach(item => {
                              const cat = item.primary_category || 'other';
                              if (!grouped[cat]) grouped[cat] = [];
                              grouped[cat].push(item);
                            });
                            // Sort items by color within each category
                            for (const k of Object.keys(grouped)) grouped[k].sort(sortItemsByColor);
                            const categoryOrder = ['tops', 'bottoms', 'dresses', 'outerwear', 'sportswear', 'footwear', 'accessories', 'swimwear', 'other'];
                            const sortedCategories = Object.keys(grouped).sort((a, b) => {
                              const ai = categoryOrder.indexOf(a);
                              const bi = categoryOrder.indexOf(b);
                              return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                            });

                            return (
                              <div className="divide-y divide-stone-50">
                                {sortedCategories.map(catKey => {
                                  const catItems = grouped[catKey];
                                  const IconComp = CATEGORY_ICONS[catKey] || Shirt;
                                  const catLabel = categories?.categories?.[catKey]?.name || catKey.charAt(0).toUpperCase() + catKey.slice(1);

                                  return (
                                    <div key={catKey} className="px-4 py-3">
                                      <div className="flex items-center gap-2 mb-3">
                                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-olive-500 to-olive-400 flex items-center justify-center text-white">
                                          {typeof IconComp === 'function' ? <IconComp className="w-3.5 h-3.5" /> : <Shirt className="w-3.5 h-3.5" />}
                                        </div>
                                        <span className="font-medium text-sm text-stone-600">{catLabel}</span>
                                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{catItems.length}</Badge>
                                      </div>
                                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                        {catItems.map((item) => (
                                          <motion.div
                                            key={item.id}
                                            initial={{ opacity: 0, scale: 0.95 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            className="bg-white rounded-xl overflow-hidden shadow-sm border border-stone-100 hover:shadow-md transition-shadow group relative"
                                          >
                                            <div className="relative aspect-[3/4] bg-stone-50">
                                              {item.image_url ? (
                                                <img src={item.processed_image_url || item.image_url} alt={item.name} className="w-full h-full object-contain" />
                                              ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                  <Shirt className="w-10 h-10 text-stone-200" />
                                                </div>
                                              )}
                                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                <button 
                                                  onClick={() => { setSelectedItem(item); setShowItemDetail(true); }}
                                                  className="bg-white/90 p-2 rounded-full hover:bg-white transition-colors"
                                                  title="View"
                                                >
                                                  <Eye className="w-4 h-4 text-stone-700" />
                                                </button>
                                                <button 
                                                  onClick={() => {
                                                    setEditingItemId(item.id);
                                                    setEditForm({
                                                      name: item.name || '', primaryCategory: item.primary_category || '', clothingType: item.clothing_type || '',
                                                      brand: item.brand || '', price: item.purchase_price || '', color: item.color || '',
                                                      size: item.size || '', material: item.material || '', season: item.season || '',
                                                      fitType: item.fit_type || '', condition: item.condition || '', notes: item.notes || ''
                                                    });
                                                    setShowEditDialog(true);
                                                  }}
                                                  className="bg-white/90 p-2 rounded-full hover:bg-white transition-colors"
                                                  title="Edit"
                                                >
                                                  <Pencil className="w-4 h-4 text-olive-600" />
                                                </button>
                                              </div>
                                            </div>
                                            <div className="p-3">
                                              <h4 className="font-medium text-sm text-stone-800 truncate">{item.name}</h4>
                                              <div className="flex items-center justify-between mt-1">
                                                <span className="text-xs text-stone-400">{item.clothing_type || item.primary_category}</span>
                                                {item.color && (
                                                  <div className="flex items-center gap-1">
                                                    <div className="w-3 h-3 rounded-full border border-stone-200" style={{ backgroundColor: item.color?.toLowerCase() }} />
                                                    <span className="text-xs text-stone-400">{item.color}</span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </motion.div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}

                          {/* Slot note */}
                          {slot.note && (
                            <div className="mx-4 mb-4 bg-stone-50 rounded-lg p-3 border border-stone-100">
                              <p className="text-xs text-stone-400 font-medium mb-0.5">Note</p>
                              <p className="text-sm text-stone-600">{slot.note}</p>
                            </div>
                          )}
                        </motion.div>
                      </AnimatePresence>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* ========== IN-DEPTH ANALYTICS VIEW ========== */}
        {wardrobeMode === 'analytics' && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-6 mb-6 border border-orange-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center">
                  <PieChart className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-stone-800">In-Depth Analytics</h2>
                  <p className="text-sm text-stone-500">Understand your wardrobe trends, value, and usage</p>
                </div>
              </div>
            </div>

            {/* Currency selector for analytics */}
            <div className="bg-white rounded-2xl p-4 mb-6 border border-stone-200 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-stone-700">💱 Display Currency</p>
                  <p className="text-xs text-stone-400 mt-0.5">All values below are shown in your selected currency. Change it here or in Account → Settings.</p>
                </div>
                <select
                  value={getCurrency()}
                  onChange={(e) => { setGlobalCurrency(e.target.value); window.location.reload(); }}
                  className="w-24 rounded-xl border border-stone-200 bg-stone-50 text-sm text-center py-2 px-1 focus:outline-none focus:ring-2 focus:ring-suede-500/30 font-medium"
                >
                  {getSupportedCurrencies().map(c => (
                    <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-orange-100">
                <p className="text-sm text-stone-500 mb-1">Total Value</p>
                <p className="text-2xl font-bold text-stone-800">{cs()}{analyticsData.valueAnalysis.totalValue?.toFixed(0) || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-orange-100">
                <p className="text-sm text-stone-500 mb-1">Avg Item Value</p>
                <p className="text-2xl font-bold text-stone-800">{cs()}{analyticsData.valueAnalysis.avgItemValue?.toFixed(0) || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-orange-100">
                <p className="text-sm text-stone-500 mb-1">Total Wears</p>
                <p className="text-2xl font-bold text-stone-800">{analyticsData.valueAnalysis.totalWears || 0}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-orange-100">
                <p className="text-sm text-stone-500 mb-1">Avg Cost/Wear</p>
                <p className="text-2xl font-bold text-stone-800">{cs()}{analyticsData.valueAnalysis.avgCostPerWear?.toFixed(2) || 0}</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Most Worn */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100">
                <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-olive-500" />
                  Most Worn Items
                </h3>
                <div className="space-y-3">
                  {analyticsData.mostWorn.map((item, i) => (
                    <div key={item.id} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-olive-100 text-olive-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-stone-100">
                        {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-stone-700 truncate">{item.name}</p>
                        <p className="text-xs text-stone-400">{item.times_worn} wears</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Least Worn */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100">
                <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-500" />
                  Least Worn Items
                </h3>
                <div className="space-y-3">
                  {analyticsData.leastWorn.map((item, i) => (
                    <div key={item.id} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-stone-100">
                        {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-stone-700 truncate">{item.name}</p>
                        <p className="text-xs text-stone-400">{item.times_worn || 0} wears</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Category Breakdown */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100">
                <h3 className="font-semibold text-stone-800 mb-4">Category Breakdown</h3>
                <div className="space-y-3">
                  {analyticsData.categoryBreakdown.map((cat, i) => (
                    <div key={i}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-stone-600">{cat.category}</span>
                        <span className="text-stone-400">{cat.count} items</span>
                      </div>
                      <Progress value={(cat.count / Math.max(items.length, 1)) * 100} className="h-2" />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Color Distribution */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100">
                <h3 className="font-semibold text-stone-800 mb-4">Color Distribution</h3>
                <div className="flex flex-wrap gap-2">
                  {analyticsData.colorDistribution.slice(0, 10).map((c, i) => (
                    <div key={i} className="px-3 py-1.5 bg-stone-100 rounded-full text-sm flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full border border-stone-300" 
                        style={{ backgroundColor: c.color.toLowerCase() }}
                      />
                      <span className="text-stone-600">{c.color}</span>
                      <span className="text-stone-400">({c.count})</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========== INSPIRATION BOARDS VIEW ========== */}
        {wardrobeMode === 'boards' && (
          <div className="mb-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-stone-100 to-stone-50 rounded-2xl p-6 mb-6 border border-stone-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {viewingBoard && (
                    <button onClick={() => { setViewingBoardId(null); setBoardImageUrl(''); setBoardImageNote(''); }} className="p-2 hover:bg-stone-200 rounded-xl transition-colors">
                      <ArrowLeft className="w-5 h-5 text-stone-600" />
                    </button>
                  )}
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-stone-600 to-stone-500 flex items-center justify-center">
                    <Bookmark className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-stone-800">
                      {viewingBoard ? viewingBoard.name : 'Inspiration Boards'}
                    </h2>
                    <p className="text-sm text-stone-500">
                      {viewingBoard 
                        ? (viewingBoard.description || `${viewingBoard.inspiration.length} pins`)
                        : 'Collect mood boards & style inspiration from anywhere — paste image URLs or upload photos to pin.'}
                    </p>
                  </div>
                </div>
                {!viewingBoard && (
                  <Button 
                    onClick={() => { setCurrentBoard({ name: '', description: '', inspiration: [] }); setEditingBoardId(null); setShowBoardDialog(true); }}
                    className="bg-gradient-to-r from-stone-600 to-stone-500 text-white rounded-xl"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Board
                  </Button>
                )}
              </div>
            </div>
            
            {/* Board Detail View */}
            {viewingBoard ? (
              <div className="space-y-6">
                {/* Add Pin Section */}
                <div className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm">
                  <h3 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add a Pin
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <Input 
                        placeholder="Paste image URL..."
                        value={boardImageUrl}
                        onChange={(e) => setBoardImageUrl(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <Input 
                        placeholder="Note (optional)"
                        value={boardImageNote}
                        onChange={(e) => setBoardImageNote(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <Button 
                      onClick={() => addImageToBoard(viewingBoard.id)}
                      className="bg-gradient-to-r from-stone-600 to-stone-500 text-white rounded-xl text-sm"
                      disabled={!boardImageUrl.trim()}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Pin
                    </Button>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs text-stone-400">or</span>
                    <label className="cursor-pointer flex items-center gap-2 px-3 py-1.5 bg-stone-50 hover:bg-stone-100 rounded-lg text-xs text-stone-600 transition-colors border border-stone-200">
                      <Upload className="w-3.5 h-3.5" />
                      Upload Image
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={(e) => {
                          if (e.target.files[0]) addUploadedImageToBoard(viewingBoard.id, e.target.files[0]);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>

                {/* Pin Count */}
                {viewingBoard.inspiration.length > 0 && (
                  <p className="text-sm text-stone-500 font-medium">{viewingBoard.inspiration.length} {viewingBoard.inspiration.length === 1 ? 'pin' : 'pins'}</p>
                )}

                {/* Pins Grid */}
                {viewingBoard.inspiration.length === 0 ? (
                  <div className="text-center py-16 bg-white/50 rounded-2xl border border-dashed border-stone-200">
                    <ImageIcon className="w-16 h-16 text-stone-200 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-stone-600 mb-2">No Pins Yet</h3>
                    <p className="text-stone-400 max-w-sm mx-auto text-sm">
                      Add images by pasting URLs or uploading photos to build your mood board.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {viewingBoard.inspiration.map((pin) => (
                      <motion.div 
                        key={pin.id} 
                        initial={{ opacity: 0, scale: 0.95 }} 
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-2xl overflow-hidden shadow-sm border border-stone-100 group relative"
                      >
                        <div className="aspect-[3/4] bg-stone-50 overflow-hidden">
                          {failedPinImages[pin.id] || pin.imageUrl === 'data:saved-locally' ? (
                            <div className="w-full h-full flex flex-col items-center justify-center text-stone-300">
                              <ImageIcon className="w-10 h-10" />
                              <p className="text-xs mt-2">{pin.imageUrl === 'data:saved-locally' ? 'Image too large to store' : 'Image unavailable'}</p>
                            </div>
                          ) : (
                            <img 
                              src={pin.imageUrl} 
                              alt={pin.note || ''} 
                              className="w-full h-full object-cover"
                              onError={() => setFailedPinImages(prev => ({ ...prev, [pin.id]: true }))}
                            />
                          )}
                        </div>
                        <div className="p-3">
                          {pin.note && <p className="text-sm text-stone-700 font-medium line-clamp-2">{pin.note}</p>}
                          <p className="text-xs text-stone-400 mt-1 truncate">{pin.imageUrl.startsWith('data:') ? 'Uploaded image' : pin.imageUrl.substring(0, 40) + (pin.imageUrl.length > 40 ? '...' : '')}</p>
                        </div>
                        <button 
                          onClick={() => removePinFromBoard(viewingBoard.id, pin.id)}
                          className="absolute top-2 right-2 p-1.5 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Board List View */
              <>
                {inspirationBoards.length === 0 ? (
                  <div className="text-center py-16 bg-white/50 rounded-2xl border border-dashed border-stone-200">
                    <Bookmark className="w-16 h-16 text-stone-200 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-stone-600 mb-2">No Inspiration Boards Yet</h3>
                    <p className="text-stone-400 mb-6 max-w-md mx-auto">
                      Create boards to collect outfit ideas, mood boards and style inspiration from anywhere.
                    </p>
                    <Button 
                      onClick={() => { setCurrentBoard({ name: '', description: '', inspiration: [] }); setEditingBoardId(null); setShowBoardDialog(true); }}
                      className="bg-gradient-to-r from-stone-600 to-stone-500 text-white rounded-xl"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Create Your First Board
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
                    {inspirationBoards.map((board) => (
                      <motion.div 
                        key={board.id} 
                        whileHover={{ y: -3, shadow: 'lg' }}
                        className="bg-white rounded-2xl overflow-hidden shadow-sm border border-stone-100 cursor-pointer group hover:shadow-md transition-shadow"
                        onClick={() => setViewingBoardId(board.id)}
                      >
                        {/* 2x2 Preview Grid */}
                        <div className="grid grid-cols-2 gap-0.5 aspect-square bg-stone-100">
                          {[0,1,2,3].map(i => (
                            <div key={i} className="bg-stone-50 overflow-hidden">
                              {board.inspiration[i]?.imageUrl && board.inspiration[i].imageUrl !== 'data:saved-locally' ? (
                                <img 
                                  src={board.inspiration[i].imageUrl} 
                                  alt="" 
                                  className="w-full h-full object-cover" 
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <ImageIcon className="w-6 h-6 text-stone-200" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-stone-800 truncate">{board.name}</h3>
                              {board.description && <p className="text-xs text-stone-400 mt-0.5 line-clamp-1">{board.description}</p>}
                              <p className="text-xs text-stone-400 mt-1">{board.inspiration.length} {board.inspiration.length === 1 ? 'pin' : 'pins'}</p>
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <button 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setEditingBoardId(board.id);
                                  setCurrentBoard({ name: board.name, description: board.description || '', inspiration: board.inspiration });
                                  setShowBoardDialog(true);
                                }}
                                className="p-1.5 hover:bg-stone-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                              >
                                <Pencil className="w-3.5 h-3.5 text-stone-400" />
                              </button>
                              <button 
                                onClick={(e) => { e.stopPropagation(); deleteBoard(board.id); }}
                                className="p-1.5 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ========== PARTNER WARDROBE VIEW ========== */}
        {wardrobeMode === 'partner' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-pink-50 to-stone-50 rounded-2xl p-6 border border-pink-100/50">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-400 flex items-center justify-center shadow-lg">
                  <HeartHandshake className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-stone-800">Partner Wardrobe</h3>
                  <p className="text-sm text-stone-500">Style your partner, pack for trips, plan outfits together 💕</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { setPartnerDialogType('partner'); setPartnerDialogTab('link'); setPartnerDialogOpen(true); fetchPartners('partner'); }}
                className="p-4 bg-white rounded-2xl border border-stone-100 hover:border-pink-200 transition-all text-left"
              >
                <Link className="w-5 h-5 text-pink-500 mb-2" />
                <p className="text-sm font-medium text-stone-800">Link Partner</p>
                <p className="text-xs text-stone-400">Share code or enter one</p>
              </button>
              <button
                onClick={() => { setPartnerDialogType('partner'); setPartnerDialogTab('suggestions'); setPartnerDialogOpen(true); fetchReceivedSuggestions(); fetchSentSuggestions(); }}
                className="relative p-4 bg-white rounded-2xl border border-stone-100 hover:border-pink-200 transition-all text-left"
              >
                <MessageCircle className="w-5 h-5 text-pink-500 mb-2" />
                <p className="text-sm font-medium text-stone-800">Suggestions</p>
                <p className="text-xs text-stone-400">View outfit ideas</p>
                {receivedSuggestions.filter(s => s.status === 'pending').length > 0 && (
                  <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-pink-500 text-white text-[10px] flex items-center justify-center font-bold">
                    {receivedSuggestions.filter(s => s.status === 'pending').length}
                  </span>
                )}
              </button>
            </div>

            {/* Partners List */}
            {partners.length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-400 font-medium">Your Partners</p>
                {partners.map(partner => (
                  <div key={partner.id} className="p-4 bg-white rounded-2xl border border-stone-100 hover:border-pink-200 transition-all">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-rose-400 flex items-center justify-center text-white font-bold text-sm">
                        {(partner.display_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-stone-800">{partner.display_name}</p>
                        <p className="text-xs text-stone-400">
                          Linked {new Date(partner.created_at).toLocaleDateString()}
                          {partner.pending_suggestions > 0 && (
                            <span className="ml-2 text-pink-500 font-medium">{partner.pending_suggestions} new suggestion{partner.pending_suggestions > 1 ? 's' : ''}</span>
                          )}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => { openPartnerWardrobe(partner, 'partner'); setPartnerDialogOpen(true); }}
                        className="bg-stone-800 text-white text-xs rounded-xl px-4"
                      >
                        <Eye className="w-3 h-3 mr-1.5" />
                        View Closet
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-2xl border border-stone-100">
                <HeartHandshake className="w-14 h-14 text-stone-200 mx-auto mb-3" />
                <p className="text-stone-600 font-medium mb-1">No partners linked yet</p>
                <p className="text-sm text-stone-400 mb-4 px-8">Generate an invite code and share it with your partner, or enter their code to get started</p>
                <Button
                  onClick={() => { setPartnerDialogType('partner'); setPartnerDialogTab('link'); setPartnerDialogOpen(true); }}
                  className="bg-gradient-to-r from-pink-500 to-rose-400 text-white rounded-xl"
                >
                  <Link className="w-4 h-4 mr-2" />
                  Link Your First Partner
                </Button>
              </div>
            )}

            {/* Received Suggestions Preview */}
            {receivedSuggestions.filter(s => s.status === 'pending').length > 0 && (
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-400 font-medium">Pending Outfit Suggestions</p>
                {receivedSuggestions.filter(s => s.status === 'pending').slice(0, 3).map(sug => (
                  <div key={sug.id} className="p-4 bg-gradient-to-r from-pink-50 to-white rounded-2xl border border-pink-100/50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-rose-400 flex items-center justify-center text-white text-xs font-bold">
                          {(sug.from_name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-stone-700">{sug.from_name}</p>
                          {sug.occasion && <p className="text-xs text-pink-500">{sug.occasion}</p>}
                        </div>
                      </div>
                      <span className="text-xs text-stone-400">{new Date(sug.created_at).toLocaleDateString()}</span>
                    </div>
                    {sug.note && <p className="text-sm text-stone-600 mb-3 italic">"{sug.note}"</p>}
                    <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
                      {(sug.items || []).map(item => (
                        <div key={item.id} className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-stone-100 border border-stone-200">
                          <img src={item.processed_image_url || item.image_url} alt={item.name} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => respondToSuggestion(sug.id, 'accepted')} className="bg-olive-500 text-white text-xs flex-1 rounded-xl">
                        <Check className="w-3 h-3 mr-1" /> Love it!
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => respondToSuggestion(sug.id, 'declined')} className="text-xs flex-1 rounded-xl">
                        Not this time
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ========== FRIENDS WARDROBE VIEW ========== */}
        {wardrobeMode === 'friends' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-50 to-stone-50 rounded-2xl p-6 border border-violet-100/50">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-400 flex items-center justify-center shadow-lg">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-stone-800">Friends Wardrobe</h3>
                  <p className="text-sm text-stone-500">Browse wardrobes, share outfits & get inspired 🤝</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { setPartnerDialogType('friend'); setPartnerDialogTab('link'); setPartnerDialogOpen(true); fetchPartners('friend'); }}
                className="p-4 bg-white rounded-2xl border border-stone-100 hover:border-violet-200 transition-all text-left"
              >
                <UserPlus className="w-5 h-5 text-violet-500 mb-2" />
                <p className="text-sm font-medium text-stone-800">Add Friend</p>
                <p className="text-xs text-stone-400">Share code or enter one</p>
              </button>
              <button
                onClick={() => { setPartnerDialogType('friend'); setPartnerDialogTab('suggestions'); setPartnerDialogOpen(true); fetchReceivedSuggestions(); fetchSentSuggestions(); }}
                className="relative p-4 bg-white rounded-2xl border border-stone-100 hover:border-violet-200 transition-all text-left"
              >
                <MessageCircle className="w-5 h-5 text-violet-500 mb-2" />
                <p className="text-sm font-medium text-stone-800">Suggestions</p>
                <p className="text-xs text-stone-400">View outfit ideas</p>
                {receivedSuggestions.filter(s => s.status === 'pending').length > 0 && (
                  <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-violet-500 text-white text-[10px] flex items-center justify-center font-bold">
                    {receivedSuggestions.filter(s => s.status === 'pending').length}
                  </span>
                )}
              </button>
            </div>

            {/* Friends List */}
            {friends.length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-400 font-medium">Your Friends</p>
                {friends.map(friend => (
                  <div key={friend.id} className="p-4 bg-white rounded-2xl border border-stone-100 hover:border-violet-200 transition-all">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-400 flex items-center justify-center text-white font-bold text-sm">
                        {(friend.display_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-stone-800">{friend.display_name}</p>
                        <p className="text-xs text-stone-400">
                          Added {new Date(friend.created_at).toLocaleDateString()}
                          {friend.pending_suggestions > 0 && (
                            <span className="ml-2 text-violet-500 font-medium">{friend.pending_suggestions} new suggestion{friend.pending_suggestions > 1 ? 's' : ''}</span>
                          )}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => { openPartnerWardrobe(friend, 'friend'); setPartnerDialogOpen(true); }}
                        className="bg-stone-800 text-white text-xs rounded-xl px-4"
                      >
                        <Eye className="w-3 h-3 mr-1.5" />
                        View Closet
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-2xl border border-stone-100">
                <Users className="w-14 h-14 text-stone-200 mx-auto mb-3" />
                <p className="text-stone-600 font-medium mb-1">No friends linked yet</p>
                <p className="text-sm text-stone-400 mb-4 px-8">Generate an invite code and share it with your friends, or enter their code to get started</p>
                <Button
                  onClick={() => { setPartnerDialogType('friend'); setPartnerDialogTab('link'); setPartnerDialogOpen(true); }}
                  className="bg-gradient-to-r from-violet-500 to-purple-400 text-white rounded-xl"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Your First Friend
                </Button>
              </div>
            )}

            {/* Received Suggestions Preview */}
            {receivedSuggestions.filter(s => s.status === 'pending').length > 0 && (
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-400 font-medium">Pending Outfit Suggestions</p>
                {receivedSuggestions.filter(s => s.status === 'pending').slice(0, 3).map(sug => (
                  <div key={sug.id} className="p-4 bg-gradient-to-r from-violet-50 to-white rounded-2xl border border-violet-100/50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-purple-400 flex items-center justify-center text-white text-xs font-bold">
                          {(sug.from_name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-stone-700">{sug.from_name}</p>
                          {sug.occasion && <p className="text-xs text-violet-500">{sug.occasion}</p>}
                        </div>
                      </div>
                      <span className="text-xs text-stone-400">{new Date(sug.created_at).toLocaleDateString()}</span>
                    </div>
                    {sug.note && <p className="text-sm text-stone-600 mb-3 italic">"{sug.note}"</p>}
                    <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
                      {(sug.items || []).map(item => (
                        <div key={item.id} className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-stone-100 border border-stone-200">
                          <img src={item.processed_image_url || item.image_url} alt={item.name} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => respondToSuggestion(sug.id, 'accepted')} className="bg-olive-500 text-white text-xs flex-1 rounded-xl">
                        <Check className="w-3 h-3 mr-1" /> Love it!
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => respondToSuggestion(sug.id, 'declined')} className="text-xs flex-1 rounded-xl">
                        Not this time
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ========== PET WARDROBE SECTION ========== */}
        {wardrobeMode === 'pets' && (
          <div className="space-y-6">
            {/* Pet Profiles Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-olive-500 to-olive-400 flex items-center justify-center shadow-lg">
                  <Dog className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-stone-800">Pet Wardrobe</h3>
                  <p className="text-sm text-stone-500">Dress up your furry friends 🐾</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  onClick={() => { setEditingPetId(null); setPetForm({ name: '', species: 'dog', breed: '', size: 'medium', color: '' }); setPetPhotoPreview(null); setShowAddPetDialog(true); }} 
                  className="bg-gradient-to-r from-olive-500 to-olive-400 text-white rounded-xl gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Pet
                </Button>
              </div>
            </div>

            {/* Pet Profile Cards */}
            {petProfiles.length === 0 ? (
              <div className="text-center py-16 bg-white/80 rounded-3xl border border-stone-200/50">
                <div className="text-6xl mb-4">🐾</div>
                <h4 className="text-lg font-medium text-stone-700 mb-2">No pets yet!</h4>
                <p className="text-stone-500 mb-6">Add your furry, feathery, or scaly friends</p>
                <Button 
                  onClick={() => setShowAddPetDialog(true)} 
                  className="bg-gradient-to-r from-olive-500 to-olive-400 text-white rounded-xl gap-2"
                >
                  <Plus className="w-4 h-4" /> Add Your First Pet
                </Button>
              </div>
            ) : (
              <>
                {/* Pet Selector */}
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {petProfiles.map(pet => (
                    <button
                      key={pet.id}
                      onClick={() => setSelectedPet(pet)}
                      className={`flex-shrink-0 flex items-center gap-3 px-4 py-3 rounded-2xl border-2 transition-all ${
                        selectedPet?.id === pet.id 
                          ? 'border-amber-400 bg-amber-50 shadow-md' 
                          : 'border-stone-200 bg-white hover:border-amber-200'
                      }`}
                    >
                      {pet.photo_url || pet.photo_base64 ? (
                        <img src={pet.photo_url || pet.photo_base64} alt={pet.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center text-white text-lg">
                          {pet.species === 'dog' ? '🐕' : pet.species === 'cat' ? '🐈' : '🐾'}
                        </div>
                      )}
                      <div className="text-left">
                        <div className="font-medium text-stone-800">{pet.name}</div>
                        <div className="text-xs text-stone-500 capitalize">{pet.breed || pet.species} • {pet.size}</div>
                      </div>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleEditPetStart(pet); }}
                        className="ml-2 p-1 text-stone-300 hover:text-amber-500 transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeletePet(pet.id); }}
                        className="p-1 text-stone-300 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </button>
                  ))}
                </div>

                {/* Selected Pet's Wardrobe */}
                {selectedPet && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-stone-700">{selectedPet.name}'s Wardrobe</h4>
                      <Button 
                        onClick={() => setShowAddPetItemDialog(true)} 
                        className="rounded-xl gap-2 bg-gradient-to-r from-olive-500 to-olive-400 text-white"
                      >
                        <Plus className="w-4 h-4" />
                        Add Item
                      </Button>
                    </div>

                    {petWardrobeItems.length === 0 ? (
                      <div className="text-center py-12 bg-white/60 rounded-2xl border border-dashed border-amber-200">
                        <div className="text-4xl mb-3">👕</div>
                        <p className="text-stone-500 mb-3">No items in {selectedPet.name}'s wardrobe yet</p>
                        <Button 
                          variant="outline" 
                          onClick={() => setShowAddPetItemDialog(true)}
                          className="rounded-xl gap-2 border-amber-300 text-amber-600"
                        >
                          <Plus className="w-4 h-4" /> Add First Item
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {petWardrobeItems.map(item => (
                          <div key={item.id} className="group relative bg-white rounded-2xl border border-stone-200/50 overflow-hidden hover:shadow-lg transition-all">
                            <div className="aspect-square bg-stone-100">
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-4xl">
                                  {item.category === 'clothing' ? '👕' : item.category === 'accessories' ? '🎀' : item.category === 'costumes' ? '🎃' : item.category === 'rainwear' ? '☔' : '🐾'}
                                </div>
                              )}
                            </div>
                            <div className="p-3">
                              <p className="font-medium text-sm text-stone-800 truncate">{item.name}</p>
                              <p className="text-xs text-stone-500 capitalize">{item.category}</p>
                            </div>
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                              <button 
                                onClick={() => {
                                  setPetItemForm({ name: item.name, category: item.category || 'clothing', color: item.color || '', brand: item.brand || '', size: item.size || '' });
                                  setPetItemImage(item.image_url || null);
                                  setEditingPetItemId(item.id);
                                  setShowAddPetItemDialog(true);
                                }}
                                className="p-1.5 bg-white/90 rounded-full shadow-sm hover:bg-suede-50 text-stone-400 hover:text-suede-600"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button 
                                onClick={() => handleDeletePetItem(item.id)}
                                className="p-1.5 bg-white/90 rounded-full shadow-sm hover:bg-red-50 text-stone-400 hover:text-red-500"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ========== ADD PET DIALOG ========== */}
        <Dialog open={showAddPetDialog} onOpenChange={(open) => { setShowAddPetDialog(open); if (!open) { setEditingPetId(null); setPetForm({ name: '', species: 'dog', breed: '', size: 'medium', color: '' }); setPetPhotoPreview(null); } }}>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">🐾 {editingPetId ? 'Edit Pet' : 'Add a Pet'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Pet Photo */}
              <div className="flex justify-center">
                <label className="cursor-pointer">
                  <div className="w-24 h-24 rounded-full border-2 border-dashed border-amber-300 flex items-center justify-center overflow-hidden hover:border-amber-500 transition-colors bg-amber-50">
                    {petPhotoPreview ? (
                      <img src={petPhotoPreview} alt="Pet" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center">
                        <CameraIcon className="w-6 h-6 text-amber-400 mx-auto" />
                        <span className="text-xs text-amber-500">Photo</span>
                      </div>
                    )}
                  </div>
                  <input type="file" accept="image/*" className="hidden" onChange={handlePetPhotoUpload} />
                </label>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700">Name *</label>
                <input 
                  value={petForm.name} onChange={(e) => setPetForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="What's your pet's name?"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-stone-700">Type</label>
                  <select 
                    value={petForm.species} onChange={(e) => setPetForm(p => ({ ...p, species: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  >
                    <option value="dog">🐕 Dog</option>
                    <option value="cat">🐈 Cat</option>
                    <option value="other">🐾 Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700">Size</label>
                  <select 
                    value={petForm.size} onChange={(e) => setPetForm(p => ({ ...p, size: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  >
                    <option value="tiny">Tiny</option>
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                    <option value="xlarge">X-Large</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700">Breed</label>
                <input 
                  value={petForm.breed} onChange={(e) => setPetForm(p => ({ ...p, breed: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="e.g. Golden Retriever"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700">Color / Markings</label>
                <input 
                  value={petForm.color} onChange={(e) => setPetForm(p => ({ ...p, color: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="e.g. Golden, Black & White spots"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => { setShowAddPetDialog(false); setEditingPetId(null); setPetForm({ name: '', species: 'dog', breed: '', size: 'medium', color: '' }); setPetPhotoPreview(null); }} className="flex-1 rounded-xl">Cancel</Button>
              <Button onClick={handleCreatePet} disabled={savingPet} className="flex-1 rounded-xl bg-gradient-to-r from-olive-500 to-olive-400 text-white">
                {savingPet ? 'Saving...' : (editingPetId ? 'Save Changes 🐾' : 'Add Pet 🐾')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ========== ADD PET WARDROBE ITEM DIALOG ========== */}
        <Dialog open={showAddPetItemDialog} onOpenChange={(open) => { setShowAddPetItemDialog(open); if (!open) { setEditingPetItemId(null); setPetItemForm({ name: '', category: 'clothing', color: '', brand: '', size: '' }); setPetItemImage(null); } }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">👕 {editingPetItemId ? 'Edit' : 'Add'} Item for {selectedPet?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Item Photo */}
              <div className="flex justify-center">
                <label className="cursor-pointer">
                  <div className="w-20 h-20 rounded-xl border-2 border-dashed border-amber-300 flex items-center justify-center overflow-hidden hover:border-amber-500 transition-colors bg-amber-50">
                    {petItemImage ? (
                      <img src={petItemImage} alt="Item" className="w-full h-full object-cover" />
                    ) : (
                      <CameraIcon className="w-6 h-6 text-amber-400" />
                    )}
                  </div>
                  <input type="file" accept="image/*" className="hidden" onChange={handlePetItemImageUpload} />
                </label>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700">Item Name *</label>
                <input 
                  value={petItemForm.name} onChange={(e) => setPetItemForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="e.g. Winter Jacket, Bandana"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-stone-700">Category</label>
                  <select 
                    value={petItemForm.category} onChange={(e) => setPetItemForm(p => ({ ...p, category: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  >
                    <option value="clothing">👕 Clothing</option>
                    <option value="accessories">🎀 Accessories</option>
                    <option value="costumes">🎃 Costumes</option>
                    <option value="seasonal">🎄 Seasonal</option>
                    <option value="rainwear">☔ Rainwear</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700">Size</label>
                  <input 
                    value={petItemForm.size} onChange={(e) => setPetItemForm(p => ({ ...p, size: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="e.g. S, M, L"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-stone-700">Color</label>
                  <input 
                    value={petItemForm.color} onChange={(e) => setPetItemForm(p => ({ ...p, color: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="e.g. Red"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700">Brand</label>
                  <input 
                    value={petItemForm.brand} onChange={(e) => setPetItemForm(p => ({ ...p, brand: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 rounded-xl border border-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="Optional"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAddPetItemDialog(false)} className="flex-1 rounded-xl">Cancel</Button>
              <Button onClick={handleAddPetWardrobeItem} className="flex-1 rounded-xl bg-gradient-to-r from-olive-500 to-olive-400 text-white">
                {editingPetItemId ? 'Save Changes' : 'Add Item'} 👕
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modern Outfit Builder - Show for owned mode or builder tab */}
        {(wardrobeMode === 'owned' || wardrobeMode === 'builder') && (
        <AnimatePresence>
          {(showOutfitBuilder || wardrobeMode === 'builder') && (
            <motion.div 
              initial={{ opacity: 0, y: -20 }} 
              animate={{ opacity: 1, y: 0 }} 
              exit={{ opacity: 0, y: -20 }} 
              className="mb-8"
            >
              <div className="bg-gradient-to-br from-stone-50 via-white to-stone-50 rounded-3xl p-8 shadow-xl border border-stone-200/50">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-suede-500 via-terracotta-500 to-amber-400 flex items-center justify-center shadow-lg">
                      <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold bg-gradient-to-r from-suede-600 to-terracotta-600 bg-clip-text text-transparent">
                        Outfit Builder
                      </h3>
                      <p className="text-sm text-stone-500">Select items from your wardrobe below to build your look</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* AI Suggestion Button */}
                    <Button 
                      variant="outline" 
                      onClick={() => setShowAiSuggestDialog(true)}
                      disabled={styleMaidLoading}
                      className="rounded-xl border-suede-200 hover:border-suede-400 hover:bg-suede-50 text-suede-600"
                    >
                      <Bot className="w-4 h-4 mr-2" />
                      AI Suggest
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => { setShowOutfitBuilder(false); if (wardrobeMode === 'builder') setWardrobeMode('owned'); }} className="rounded-full">
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                {/* Outfit Canvas - Category Cards */}
                <div className="relative bg-gradient-to-b from-stone-100 to-stone-50 rounded-2xl p-6 mb-6">
                  {/* Selected Items Strip */}
                  {currentOutfit.items.length > 0 && (
                    <div className="mb-5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-stone-600 uppercase tracking-wide">
                          Selected Items ({currentOutfit.items.length})
                        </span>
                        <button 
                          onClick={() => setCurrentOutfit(p => ({ ...p, items: [] }))}
                          className="text-xs text-stone-400 hover:text-red-500 transition-colors"
                        >
                          Clear all
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {currentOutfit.items.map(item => (
                          <motion.div 
                            key={item.id}
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            className="relative group"
                          >
                            <div className="w-16 h-16 rounded-xl overflow-hidden bg-white shadow-md ring-2 ring-suede-300">
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-stone-100">
                                  <Shirt className="w-5 h-5 text-stone-300" />
                                </div>
                              )}
                            </div>
                            <button 
                              onClick={() => removeFromOutfit(item.id)}
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            <p className="text-[9px] text-stone-500 text-center mt-1 max-w-[64px] truncate">{item.name}</p>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Category Buttons Grid */}
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3 max-w-4xl mx-auto">
                    {[
                      { key: 'top', label: 'Tops', icon: Shirt, emoji: null, color: 'suede' },
                      { key: 'bottom', label: 'Bottoms', icon: null, emoji: '👖', color: 'mud' },
                      { key: 'dress', label: 'Dresses', icon: null, emoji: '👗', color: 'terracotta' },
                      { key: 'outerwear', label: 'Outerwear', icon: null, emoji: '🧥', color: 'amber' },
                      { key: 'shoes', label: 'Shoes', icon: Footprints, emoji: null, color: 'amber' },
                      { key: 'bag', label: 'Bags', icon: ShoppingBag, emoji: null, color: 'suede' },
                      { key: 'accessories', label: 'Accessories', icon: Crown, emoji: null, color: 'suede' },
                    ].map(cat => {
                      const catItems = getOutfitItemsByCategory(cat.key);
                      const hasItems = catItems.length > 0;
                      return (
                        <button
                          key={cat.key}
                          onClick={() => openOutfitPicker(cat.key)}
                          className={`
                            relative flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all
                            ${hasItems 
                              ? `border-${cat.color}-400 bg-${cat.color}-50 shadow-md` 
                              : `border-dashed border-stone-200 bg-white/80 hover:border-${cat.color}-300 hover:bg-${cat.color}-50/50`
                            }
                          `}
                        >
                          {hasItems && (
                            <div className="absolute -top-2 -right-2 w-6 h-6 bg-suede-500 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-sm">
                              {catItems.length}
                            </div>
                          )}
                          {hasItems ? (
                            <div className="flex -space-x-2">
                              {catItems.slice(0, 3).map((item, i) => (
                                <div key={item.id} className="w-10 h-10 rounded-lg overflow-hidden bg-white shadow-sm ring-2 ring-white" style={{ zIndex: 3 - i }}>
                                  <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                                </div>
                              ))}
                              {catItems.length > 3 && (
                                <div className="w-10 h-10 rounded-lg bg-stone-200 flex items-center justify-center text-xs font-medium text-stone-600 ring-2 ring-white">
                                  +{catItems.length - 3}
                                </div>
                              )}
                            </div>
                          ) : (
                            <>
                              {cat.icon ? <cat.icon className={`w-6 h-6 text-stone-300`} /> : <span className="text-2xl opacity-40">{cat.emoji}</span>}
                              <Plus className="w-4 h-4 text-stone-300" />
                            </>
                          )}
                          <span className="text-xs font-medium text-stone-600">{cat.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Instruction */}
                  <div className="mt-5 flex items-center justify-center gap-3 text-sm text-stone-500 bg-white/80 rounded-full py-2 px-6 mx-auto w-fit shadow-sm">
                    <span>Tap a category to pick items — add as many as you like</span>
                  </div>
                </div>

                {/* Outfit Picker Dialog */}
                <Dialog open={showOutfitPickerDialog} onOpenChange={setShowOutfitPickerDialog}>
                  <DialogContent className="max-w-lg max-h-[80vh]">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        {outfitPickerCategory === 'top' && <><Shirt className="w-5 h-5 text-suede-500" /> Select Tops</>}
                        {outfitPickerCategory === 'bottom' && <>👖 Select Bottoms</>}
                        {outfitPickerCategory === 'dress' && <>👗 Select Dresses</>}
                        {outfitPickerCategory === 'outerwear' && <>🧥 Select Outerwear</>}
                        {outfitPickerCategory === 'shoes' && <><Footprints className="w-5 h-5 text-amber-500" /> Select Shoes</>}
                        {outfitPickerCategory === 'bag' && <><ShoppingBag className="w-5 h-5 text-suede-500" /> Select Bags</>}
                        {outfitPickerCategory === 'accessories' && <><Crown className="w-5 h-5 text-suede-500" /> Select Accessories</>}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto max-h-[55vh] pr-1">
                      {(() => {
                        const filtered = items.filter(item => {
                          const cat = (item.primary_category || item.category || '').toLowerCase();
                          const clothingType = (item.clothing_type || '').toLowerCase();
                          if (outfitPickerCategory === 'top') return cat === 'tops' || cat === 'top';
                          if (outfitPickerCategory === 'bottom') return cat === 'bottoms' || cat === 'bottom';
                          if (outfitPickerCategory === 'dress') return cat === 'dresses' || cat === 'dress';
                          if (outfitPickerCategory === 'outerwear') return cat === 'outerwear' || clothingType.includes('jacket') || clothingType.includes('coat');
                          if (outfitPickerCategory === 'shoes') return cat === 'footwear' || cat === 'shoes' || clothingType.includes('shoe') || clothingType.includes('boot');
                          if (outfitPickerCategory === 'bag') return clothingType.includes('bag') || item.name?.toLowerCase().includes('bag') || cat === 'bags';
                          if (outfitPickerCategory === 'accessories') return cat === 'accessories' || cat === 'accessory';
                          return false;
                        });
                        if (filtered.length === 0) {
                          return (
                            <div className="text-center py-12 text-stone-400">
                              <div className="text-4xl mb-3">📦</div>
                              <p className="font-medium">No items in this category</p>
                              <p className="text-sm mt-1">Add items to your wardrobe first!</p>
                            </div>
                          );
                        }
                        return (
                          <div className="grid grid-cols-3 gap-3">
                            {filtered.map(item => {
                              const selected = isInCurrentOutfit(item.id);
                              return (
                                <motion.button
                                  key={item.id}
                                  whileHover={{ scale: 1.03 }}
                                  whileTap={{ scale: 0.97 }}
                                  onClick={() => selected ? removeFromOutfit(item.id) : addToOutfit(item)}
                                  className={`
                                    relative rounded-xl overflow-hidden transition-all text-left
                                    ${selected 
                                      ? 'ring-3 ring-suede-500 shadow-lg' 
                                      : 'ring-1 ring-stone-200 hover:ring-suede-300 hover:shadow-md'
                                    }
                                  `}
                                >
                                  <div className="aspect-square bg-stone-50 overflow-hidden">
                                    {item.image_url ? (
                                      <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <Shirt className="w-8 h-8 text-stone-200" />
                                      </div>
                                    )}
                                    {selected && (
                                      <div className="absolute inset-0 bg-suede-500/20 flex items-center justify-center">
                                        <div className="w-8 h-8 rounded-full bg-suede-500 flex items-center justify-center shadow-lg">
                                          <Check className="w-4 h-4 text-white" />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="p-2">
                                    <p className="text-xs font-medium text-stone-800 truncate">{item.name}</p>
                                    {item.color && <p className="text-[10px] text-stone-400">{item.color}</p>}
                                  </div>
                                </motion.button>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex justify-end pt-2 border-t border-stone-100">
                      <Button onClick={() => setShowOutfitPickerDialog(false)} className="rounded-xl bg-gradient-to-r from-suede-500 to-suede-400 text-white">
                        Done
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Save Section */}
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                  <div className="flex gap-3 items-center flex-1 w-full sm:w-auto">
                    <Input 
                      placeholder="Give your outfit a name..." 
                      value={currentOutfit.name} 
                      onChange={(e) => setCurrentOutfit(p => ({ ...p, name: e.target.value }))} 
                      className="rounded-xl border-stone-200 focus:ring-2 focus:ring-suede-500" 
                    />
                  </div>
                  <div className="flex gap-3">
                    {/* Preview Current Outfit Button */}
                    {currentOutfit.items.length > 0 && (
                      <Button 
                        variant="outline"
                        onClick={() => {
                          const topItem = currentOutfit.items.find(i => getItemOutfitCategory(i) === 'top');
                          const bottomItem = currentOutfit.items.find(i => getItemOutfitCategory(i) === 'bottom');
                          const dressItem = currentOutfit.items.find(i => getItemOutfitCategory(i) === 'dress');
                          const outerwearItem = currentOutfit.items.find(i => getItemOutfitCategory(i) === 'outerwear');
                          const shoesItem = currentOutfit.items.find(i => getItemOutfitCategory(i) === 'shoes');
                          const bagItem = currentOutfit.items.find(i => getItemOutfitCategory(i) === 'bag');
                          const accItems = currentOutfit.items.filter(i => getItemOutfitCategory(i) === 'accessories');
                          openOutfitPreview({
                            name: currentOutfit.name || 'Current Outfit',
                            top_image: topItem?.image_url,
                            bottom_image: bottomItem?.image_url,
                            dress_image: dressItem?.image_url,
                            outerwear_image: outerwearItem?.image_url,
                            shoes_image: shoesItem?.image_url,
                            bag_image: bagItem?.image_url,
                            accessories: accItems.map(a => ({ id: a.id, name: a.name, image_url: a.image_url })),
                            _topItem: topItem,
                            _dressItem: dressItem,
                            times_worn: 0,
                            is_current: true
                          });
                        }}
                        className="rounded-xl border-suede-200 text-suede-600 hover:bg-suede-50"
                      >
                        <Maximize2 className="w-4 h-4 mr-2" />
                        Preview
                      </Button>
                    )}
                    <Button 
                      variant="outline"
                      onClick={() => setCurrentOutfit({ name: '', items: [] })}
                      className="rounded-xl"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Clear
                    </Button>
                    <Button 
                      onClick={saveOutfit} 
                      disabled={!currentOutfit.name || currentOutfit.items.length === 0 || savingOutfit} 
                      className="bg-gradient-to-r from-suede-500 to-suede-400 hover:from-suede-600 hover:to-terracotta-600 text-white rounded-xl shadow-lg"
                    >
                      {savingOutfit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
                      {savingOutfit ? 'Saving...' : 'Save Outfit'}
                    </Button>
                  </div>
                </div>
                
                {/* Saved Outfits */}
                {savedOutfits.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-stone-200">
                    <p className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                      <Award className="w-5 h-5 text-suede-500" />
                      Your Saved Outfits
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {savedOutfits.map(o => (
                        <motion.div 
                          key={o.id} 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-white rounded-2xl p-4 shadow-sm border border-stone-100 hover:shadow-lg transition-all cursor-pointer group"
                          onClick={() => openOutfitPreview(o)}
                        >
                          {/* Visual Grid Preview */}
                          <div className="relative bg-gradient-to-br from-stone-50 to-stone-100 rounded-xl p-3 mb-3 overflow-hidden">
                            {/* Favorite badge */}
                            {o.is_favorite && (
                              <div className="absolute top-2 left-2 z-10">
                                <Heart className="w-5 h-5 text-red-500 fill-red-500" />
                              </div>
                            )}
                            
                            {/* Grid of outfit items */}
                            {(() => {
                              const outfitImgs = [];
                              if (o.top_image) outfitImgs.push({ src: o.top_image, label: 'Top' });
                              if (o.dress_image && !o.top_image) outfitImgs.push({ src: o.dress_image, label: 'Dress' });
                              if (o.bottom_image) outfitImgs.push({ src: o.bottom_image, label: 'Bottom' });
                              if (o.outerwear_image) outfitImgs.push({ src: o.outerwear_image, label: 'Outerwear' });
                              if (o.shoes_image) outfitImgs.push({ src: o.shoes_image, label: 'Shoes' });
                              if (o.bag_image) outfitImgs.push({ src: o.bag_image, label: 'Bag' });
                              if (o.accessories?.length > 0) {
                                o.accessories.forEach(acc => {
                                  if (acc.image_url) outfitImgs.push({ src: acc.image_url, label: acc.name || 'Accessory' });
                                });
                              }
                              if (outfitImgs.length === 0) {
                                return (
                                  <div className="h-[140px] flex items-center justify-center text-stone-300">
                                    <Layers className="w-8 h-8 opacity-30" />
                                  </div>
                                );
                              }
                              const cols = outfitImgs.length <= 2 ? 2 : outfitImgs.length <= 4 ? 2 : 3;
                              return (
                                <div className={`grid gap-2 ${cols === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                                  {outfitImgs.slice(0, 6).map((img, idx) => (
                                    <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-white shadow-sm ring-1 ring-stone-200">
                                      <img src={img.src} alt={img.label} className="w-full h-full object-cover" />
                                    </div>
                                  ))}
                                  {outfitImgs.length > 6 && (
                                    <div className="aspect-square rounded-lg bg-stone-200 flex items-center justify-center text-sm font-medium text-stone-600">
                                      +{outfitImgs.length - 6}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            
                            {/* Expand button */}
                            <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="bg-white/90 backdrop-blur-sm rounded-full p-1.5 shadow-md">
                                <Maximize2 className="w-4 h-4 text-suede-600" />
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-semibold text-stone-800">{o.name}</p>
                              <p className="text-xs text-stone-400">Worn {o.times_worn || 0} times</p>
                            </div>
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); toggleOutfitFavorite(o.id); }}
                                className="rounded-full"
                                title="Add to favorites"
                              >
                                <Heart className={`w-4 h-4 ${o.is_favorite ? 'text-red-500 fill-red-500' : 'text-stone-400'}`} />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); deleteOutfit(o.id); }}
                                disabled={deletingOutfitId === o.id}
                                className="rounded-full text-stone-400 hover:text-red-500"
                              >
                                {deletingOutfitId === o.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        )}

        {/* Bulk Delete Bar - Only for owned mode */}
        {wardrobeMode === 'owned' && (
        <AnimatePresence>
          {selectionMode && selectedItems.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }} 
              animate={{ opacity: 1, y: 0 }} 
              exit={{ opacity: 0, y: -10 }} 
              className="mb-4 bg-gradient-to-r from-suede-600 to-suede-500 text-white rounded-2xl p-4 flex items-center justify-between"
            >
              <span className="font-medium">{selectedItems.length} items selected</span>
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={handleBulkDelete} 
                className="bg-white/20 hover:bg-white/30 text-white rounded-2xl"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
        )}

        {/* Search & Filters - Only for owned mode */}
        {wardrobeMode === 'owned' && (
        <div className="mb-8 space-y-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input 
                placeholder="Search your wardrobe..." 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)} 
                className="pl-11 rounded-full py-5 border-stone-200 bg-white/80 text-sm placeholder:text-stone-300 focus:border-stone-400" 
              />
            </div>
            
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)} 
              className={`rounded-full border-stone-200 text-stone-500 hover:text-stone-700 ${activeFiltersCount > 0 ? "border-stone-400 text-stone-700 bg-stone-50" : ""}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
            </Button>
            
            <div className="flex border border-stone-200 rounded-full overflow-hidden">
              <button 
                onClick={() => setViewMode('magazine')} 
                className={`p-2.5 px-3 ${viewMode === 'magazine' ? 'bg-stone-800 text-white' : 'bg-white text-stone-400 hover:text-stone-600'}`}
                title="Magazine View"
              >
                <BookOpen className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setViewMode('category')} 
                className={`p-2.5 px-3 ${viewMode === 'category' ? 'bg-stone-800 text-white' : 'bg-white text-stone-400 hover:text-stone-600'}`}
                title="Category View"
              >
                <Layers className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setViewMode('grid')} 
                className={`p-2.5 px-3 ${viewMode === 'grid' ? 'bg-stone-800 text-white' : 'bg-white text-stone-400 hover:text-stone-600'}`}
                title="Grid View"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setViewMode('list')} 
                className={`p-2.5 px-3 ${viewMode === 'list' ? 'bg-stone-800 text-white' : 'bg-white text-stone-400 hover:text-stone-600'}`}
                title="List View"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Filters Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }} 
                animate={{ opacity: 1, height: 'auto' }} 
                exit={{ opacity: 0, height: 0 }} 
                className="glass-card rounded-2xl p-5 overflow-hidden"
              >
                <div className="flex items-end gap-4">
                  <div className="flex-1 max-w-xs">
                    <Label className="text-xs text-gray-500">Color</Label>
                    <Select value={filters.color || "all"} onValueChange={(v) => setFilters({ ...filters, color: v === "all" ? "" : v })}>
                      <SelectTrigger className="mt-1 rounded-2xl"><SelectValue placeholder="All colors" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All colors</SelectItem>
                        {categories?.colors?.map(c => (
                          <SelectItem key={c.value} value={c.value}>
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full border" style={{ background: c.hex }} />
                              {c.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500 rounded-2xl">
                    <X className="w-4 h-4 mr-1" />Clear
                  </Button>
                </div>
                
                <div className="mt-4 flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={filters.favorite} 
                      onChange={(e) => setFilters({ ...filters, favorite: e.target.checked })} 
                      className="rounded" 
                    />
                    <Heart className="w-4 h-4 text-terracotta-500" />
                    <span className="text-sm">Favorites only</span>
                  </label>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        )}

        {/* Loading State - Only for owned mode */}
        {wardrobeMode === 'owned' && loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[1,2,3,4,5,6,7,8,9,10,11,12].map(i => (
              <div key={i} className="bg-gray-100 rounded-2xl aspect-square animate-pulse" />
            ))}
          </div>
        ) : wardrobeMode === 'owned' && items.length === 0 ? (
          /* Empty State */
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-suede-100 to-stone-100 flex items-center justify-center mx-auto mb-6">
              <Shirt className="w-10 h-10 text-suede-400" />
            </div>
            <h3 className="text-2xl font-light text-gray-900 mb-3">
              {searchQuery || activeFiltersCount > 0 ? 'No items match your filters' : 'Your wardrobe is empty'}
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              {searchQuery || activeFiltersCount > 0 
                ? 'Try adjusting your search or filters' 
                : 'Start adding your real clothes to build your virtual wardrobe. Take photos or upload images of your clothing items.'}
            </p>
            {searchQuery || activeFiltersCount > 0 ? (
              <Button variant="outline" onClick={clearFilters} className="rounded-2xl">
                Clear Filters
              </Button>
            ) : (
              <Button 
                onClick={() => setAddDialogOpen(true)}
                className="bg-gradient-to-r from-suede-600 to-suede-500 text-white rounded-2xl px-8"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Item
              </Button>
            )}
          </div>
        ) : wardrobeMode === 'owned' && viewMode === 'magazine' ? (
          /* Magazine View */
          <div className="space-y-16">
            {/* Magazine Header */}
            <div className="text-center border-b border-stone-200 pb-8">
              <p className="text-xs uppercase tracking-[0.4em] text-stone-400 mb-2">Your Personal</p>
              <h2 className="text-5xl md:text-6xl font-extralight tracking-tight text-stone-800" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                Fashion Edit
              </h2>
              <div className="flex items-center justify-center gap-4 mt-4 text-xs uppercase tracking-[0.25em] text-stone-400">
                <span>{items.length} Pieces</span>
                <span className="w-1 h-1 rounded-full bg-stone-300" />
                <span>{Object.keys(groupedItems).filter(k => groupedItems[k]?.items?.length > 0).length} Categories</span>
                <span className="w-1 h-1 rounded-full bg-stone-300" />
                <span>{new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
              </div>
            </div>

            {/* Magazine Spreads by Category */}
            {Object.entries(groupedItems)
              .filter(([_, data]) => data.items && data.items.length > 0)
              .map(([key, data], catIndex) => {
                const catItems = data.items;
                const heroItem = catItems[0];
                const restItems = catItems.slice(1);
                const isReversed = catIndex % 2 === 1;
                
                return (
                  <div key={key} className="space-y-6">
                    {/* Category Divider */}
                    <div className="flex items-center gap-6">
                      <div className="h-px flex-1 bg-gradient-to-r from-transparent to-stone-200" />
                      <h3 className="text-xs uppercase tracking-[0.35em] text-stone-400 font-medium">{data.name}</h3>
                      <div className="h-px flex-1 bg-gradient-to-l from-transparent to-stone-200" />
                    </div>

                    {/* Hero + Supporting Layout */}
                    <div className={`grid grid-cols-1 ${restItems.length > 0 ? 'md:grid-cols-12' : ''} gap-4 md:gap-6`}>
                      {/* Hero Item — large editorial */}
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className={`${restItems.length > 0 ? (isReversed ? 'md:col-span-5 md:order-2' : 'md:col-span-7') : 'md:col-span-12 max-w-2xl mx-auto'} group cursor-pointer relative`}
                        onClick={() => { setSelectedItem(heroItem); setShowItemDetail(true); }}
                      >
                        <div className={`relative ${restItems.length > 0 ? 'aspect-[3/4]' : 'aspect-[4/5] max-h-[600px]'} overflow-hidden bg-stone-50 rounded-sm`}>
                          {heroItem.image_url ? (
                            <img 
                              src={heroItem.image_url} 
                              alt={heroItem.name} 
                              className="w-full h-full object-contain" 
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Shirt className="w-20 h-20 text-stone-200" />
                            </div>
                          )}
                          {/* Editorial overlay on hover */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                            <div className="absolute bottom-0 left-0 right-0 p-6">
                              <div className="flex items-center gap-3">
                                {canTryOn(heroItem) && (
                                  <Button size="icon" className="rounded-full bg-white/90 text-stone-800 hover:bg-white" onClick={(e) => { e.stopPropagation(); openTryOn(heroItem); }}>
                                    <Sparkles className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button size="icon" className="rounded-full bg-white/90 text-stone-800 hover:bg-white" onClick={(e) => { e.stopPropagation(); openEditItem(heroItem); }}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button size="icon" className={`rounded-full ${heroItem.is_favorite ? 'bg-amber-500 text-white' : 'bg-white/90 text-stone-800'}`} onClick={(e) => { e.stopPropagation(); handleToggleFavorite(heroItem.id); }}>
                                  <Star className={`w-4 h-4 ${heroItem.is_favorite ? 'fill-white' : ''}`} />
                                </Button>
                              </div>
                            </div>
                          </div>
                          {heroItem.is_favorite && (
                            <div className="absolute top-4 right-4">
                              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                            </div>
                          )}
                        </div>
                        <div className="mt-4">
                          <h4 className="text-xl md:text-2xl font-light text-stone-800 mb-1" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>{heroItem.name}</h4>
                          <div className="flex items-center gap-3 text-sm text-stone-400">
                            {heroItem.brand && <span className="uppercase tracking-wider text-xs font-medium text-stone-500">{heroItem.brand}</span>}
                            {heroItem.color && <><span className="w-1 h-1 rounded-full bg-stone-300" /><span className="capitalize">{heroItem.color}</span></>}
                            {heroItem.season && <><span className="w-1 h-1 rounded-full bg-stone-300" /><SeasonIcon season={heroItem.season} /></>}
                          </div>
                        </div>
                      </motion.div>

                      {/* Supporting Items — editorial sidebar */}
                      {restItems.length > 0 && (
                        <div className={`${isReversed ? 'md:col-span-7 md:order-1' : 'md:col-span-5'}`}>
                          <div className={`grid ${restItems.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} gap-4 md:gap-5 h-full`}>
                            {restItems.map((item, idx) => (
                              <motion.div
                                key={item.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 + idx * 0.08, duration: 0.5 }}
                                className="group cursor-pointer relative"
                                onClick={() => { setSelectedItem(item); setShowItemDetail(true); }}
                              >
                                <div className="relative aspect-[3/4] overflow-hidden bg-stone-50 rounded-sm">
                                  {item.image_url ? (
                                    <img 
                                      src={item.image_url} 
                                      alt={item.name} 
                                      className="w-full h-full object-contain" 
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Shirt className="w-10 h-10 text-stone-200" />
                                    </div>
                                  )}
                                  {/* Hover actions */}
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                    {canTryOn(item) && (
                                      <Button size="icon" className="rounded-full bg-white/90 text-stone-800 hover:bg-white w-8 h-8" onClick={(e) => { e.stopPropagation(); openTryOn(item); }}>
                                        <Sparkles className="w-3.5 h-3.5" />
                                      </Button>
                                    )}
                                    <Button size="icon" className={`rounded-full w-8 h-8 ${item.is_favorite ? 'bg-amber-500 text-white' : 'bg-white/90 text-stone-800'}`} onClick={(e) => { e.stopPropagation(); handleToggleFavorite(item.id); }}>
                                      <Star className={`w-3.5 h-3.5 ${item.is_favorite ? 'fill-white' : ''}`} />
                                    </Button>
                                  </div>
                                  {item.is_favorite && (
                                    <div className="absolute top-3 right-3">
                                      <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                                    </div>
                                  )}
                                </div>
                                <div className="mt-2.5">
                                  <p className="text-sm font-light text-stone-700 truncate" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>{item.name}</p>
                                  <p className="text-xs text-stone-400 uppercase tracking-wider mt-0.5">{item.brand || item.color || ''}</p>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

            {/* Magazine Footer */}
            <div className="text-center pt-8 border-t border-stone-200">
              <p className="text-xs uppercase tracking-[0.3em] text-stone-300">— End of Collection —</p>
            </div>
          </div>
        ) : wardrobeMode === 'owned' && viewMode === 'category' ? (
          /* Category View */
          <div className="space-y-6">
            {Object.entries(groupedItems)
              .filter(([k, data]) => data.items && data.items.length > 0 && k !== 'swimwear') // swimwear merged into accessories
              .map(([key, data]) => {
                const Icon = CATEGORY_ICONS[key] || Shirt;
                const isExpanded = expandedCategories.includes(key);
                
                return (
                  <motion.div 
                    key={key}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="glass-card rounded-2xl overflow-hidden"
                  >
                    {/* Category Header */}
                    <button 
                      onClick={() => toggleCategory(key)}
                      className="w-full flex items-center justify-between p-5 hover:bg-white/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center text-white">
                          {typeof Icon === 'function' ? <Icon /> : <Icon className="w-6 h-6" />}
                        </div>
                        <div className="text-left">
                          <h3 className="text-lg font-medium text-gray-900">{data.name}</h3>
                          <p className="text-sm text-gray-500">
                            {key === 'accessories'
                              ? (data.items.length + (groupedItems.swimwear?.items?.length || 0))
                              : data.items.length} items
                          </p>
                        </div>
                      </div>
                      <motion.div
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      </motion.div>
                    </button>
                    
                    {/* Category Items */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          {/* Sub-group footwear & accessories by clothing_type */}
                          {(key === 'footwear' || key === 'accessories') ? (
                            <div className="p-5 pt-0 space-y-6">
                              {(() => {
                                const subGroups = {};
                                // Merge swimwear items into accessories if this is the accessories category
                                const allItems = key === 'accessories' && groupedItems.swimwear?.items?.length
                                  ? [...data.items, ...groupedItems.swimwear.items]
                                  : data.items;
                                allItems.forEach(item => {
                                  if (key === 'accessories') {
                                    // Use consolidated subcategories for accessories
                                    const sub = getAccessorySubcategory(item.clothing_type);
                                    const groupKey = sub ? sub.label : 'Other Accessories';
                                    const groupIcon = sub ? sub.icon : '✨';
                                    if (!subGroups[groupKey]) subGroups[groupKey] = { label: groupKey, icon: groupIcon, items: [] };
                                    subGroups[groupKey].items.push(item);
                                  } else {
                                    // Footwear: keep individual type grouping
                                    const typeKey = item.clothing_type || 'other';
                                    const typeLabel = categories?.categories?.[key]?.types?.find(t => t.value === typeKey)?.label || typeKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                                    if (!subGroups[typeKey]) subGroups[typeKey] = { label: typeLabel, items: [] };
                                    subGroups[typeKey].items.push(item);
                                  }
                                });
                                // Sort subcategories by the order defined in ACCESSORY_SUBCATEGORIES
                                const sortedEntries = key === 'accessories'
                                  ? Object.entries(subGroups).sort((a, b) => {
                                      const aIdx = ACCESSORY_SUBCATEGORIES.findIndex(s => s.label === a[0]);
                                      const bIdx = ACCESSORY_SUBCATEGORIES.findIndex(s => s.label === b[0]);
                                      return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
                                    })
                                  : Object.entries(subGroups);
                                return sortedEntries.map(([subKey, subData]) => {
                                  subData.items.sort(sortItemsByColor);
                                  return (
                                  <div key={subKey}>
                                    <div className="flex items-center gap-2 mb-3">
                                      {subData.icon && <span className="text-base">{subData.icon}</span>}
                                      <h4 className="text-sm font-semibold text-stone-600 uppercase tracking-wider">{subData.label}</h4>
                                      <span className="text-xs text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">{subData.items.length}</span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                      {subData.items.map(item => (
                                        <motion.div
                                          key={item.id}
                                          onClick={() => selectionMode ? toggleItemSelection(item.id) : null}
                                          className={`
                                            relative glass-card rounded-2xl overflow-hidden cursor-pointer group
                                            ${selectedItems.includes(item.id) ? 'ring-2 ring-suede-500' : ''}
                                          `}
                                        >
                                          {selectionMode && (
                                            <div className={`
                                              absolute top-2 left-2 z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center
                                              ${selectedItems.includes(item.id) ? 'bg-suede-600 border-suede-600' : 'bg-white border-gray-300'}
                                            `}>
                                              {selectedItems.includes(item.id) && <Check className="w-4 h-4 text-white" />}
                                            </div>
                                          )}
                                          <div className="aspect-[3/4] overflow-hidden bg-gray-50">
                                            {item.image_url ? (
                                              <img src={item.image_url} alt={item.name} className="w-full h-full object-contain" />
                                            ) : (
                                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-suede-50 to-stone-50">
                                                <Shirt className="w-12 h-12 text-suede-200" />
                                              </div>
                                            )}
                                            {/* Bottom action bar — always visible */}
                                            <div className="absolute bottom-0 left-0 right-0 flex items-center justify-around bg-white/95 backdrop-blur-sm border-t border-stone-100 py-1.5 px-1">
                                              <button onClick={(e) => { e.stopPropagation(); setSelectedItem(item); setShowItemDetail(true); }} className="flex flex-col items-center gap-0.5 p-1"><Eye className="w-4 h-4 text-stone-500" /><span className="text-[8px] text-stone-400">View</span></button>
                                              <button onClick={(e) => { e.stopPropagation(); openEditItem(item); }} className="flex flex-col items-center gap-0.5 p-1"><Pencil className="w-4 h-4 text-stone-500" /><span className="text-[8px] text-stone-400">Edit</span></button>
                                              {canTryOn(item) && (
                                                <button onClick={(e) => { e.stopPropagation(); openTryOn(item); }} className="flex flex-col items-center gap-0.5 p-1"><Sparkles className="w-4 h-4 text-amber-500" /><span className="text-[8px] text-amber-500 font-medium">Try-On</span></button>
                                              )}
                                              <button onClick={(e) => { e.stopPropagation(); handleToggleFavorite(item.id); }} className="flex flex-col items-center gap-0.5 p-1"><Star className={`w-4 h-4 ${item.is_favorite ? 'text-amber-500 fill-amber-500' : 'text-stone-500'}`} /><span className="text-[8px] text-stone-400">Fav</span></button>
                                            </div>
                                            {/* Delete button - top right corner */}
                                            <button className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full w-7 h-7 flex items-center justify-center shadow transition-opacity z-10" onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }} title="Delete Item"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                                          </div>
                                          <div className="p-3">
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                              <p className="text-sm font-medium truncate">{item.name}</p>
                                              {item.is_favorite && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 shrink-0" />}
                                            </div>
                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                              <span>{item.brand || 'No brand'}</span>
                                              <span className="font-medium text-suede-600">{cs()}{item.purchase_price || item.price || 0}</span>
                                            </div>
                                          </div>
                                        </motion.div>
                                      ))}
                                    </div>
                                  </div>
                                  );
                                });
                              })()}
                            </div>
                          ) : (
                          <div className="p-5 pt-0 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            {data.items.map(item => (
                              <motion.div
                                key={item.id}
                                onClick={() => selectionMode ? toggleItemSelection(item.id) : null}
                                className={`
                                  relative glass-card rounded-2xl overflow-hidden cursor-pointer group
                                  ${selectedItems.includes(item.id) ? 'ring-2 ring-suede-500' : ''}
                                `}
                              >
                                {selectionMode && (
                                  <div className={`
                                    absolute top-2 left-2 z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center
                                    ${selectedItems.includes(item.id) ? 'bg-suede-600 border-suede-600' : 'bg-white border-gray-300'}
                                  `}>
                                    {selectedItems.includes(item.id) && <Check className="w-4 h-4 text-white" />}
                                  </div>
                                )}
                                
                                <div className="aspect-[3/4] overflow-hidden bg-gray-50">
                                  {item.image_url ? (
                                    <img 
                                      src={item.image_url} 
                                      alt={item.name} 
                                      className="w-full h-full object-contain" 
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-suede-50 to-stone-50">
                                      <Shirt className="w-12 h-12 text-suede-200" />
                                    </div>
                                  )}
                                  
                                  {/* Bottom action bar — always visible */}
                                  <div className="absolute bottom-0 left-0 right-0 flex items-center justify-around bg-white/95 backdrop-blur-sm border-t border-stone-100 py-1.5 px-1">
                                    <button onClick={(e) => { e.stopPropagation(); setSelectedItem(item); setShowItemDetail(true); }} className="flex flex-col items-center gap-0.5 p-1"><Eye className="w-4 h-4 text-stone-500" /><span className="text-[8px] text-stone-400">View</span></button>
                                    <button onClick={(e) => { e.stopPropagation(); openEditItem(item); }} className="flex flex-col items-center gap-0.5 p-1"><Pencil className="w-4 h-4 text-stone-500" /><span className="text-[8px] text-stone-400">Edit</span></button>
                                    {canTryOn(item) && (
                                      <button onClick={(e) => { e.stopPropagation(); openTryOn(item); }} className="flex flex-col items-center gap-0.5 p-1"><Sparkles className="w-4 h-4 text-amber-500" /><span className="text-[8px] text-amber-500 font-medium">Try-On</span></button>
                                    )}
                                    <button onClick={(e) => { e.stopPropagation(); handleToggleFavorite(item.id); }} className="flex flex-col items-center gap-0.5 p-1"><Star className={`w-4 h-4 ${item.is_favorite ? 'text-amber-500 fill-amber-500' : 'text-stone-500'}`} /><span className="text-[8px] text-stone-400">Fav</span></button>
                                  </div>
                                  {/* Delete button - top right corner */}
                                  <button className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full w-7 h-7 flex items-center justify-center shadow transition-opacity z-10" onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }} title="Delete Item"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                                </div>
                                
                                <div className="p-3">
                                  <div className="flex items-center justify-between gap-2 mb-1">
                                    <p className="text-sm font-medium truncate">{item.name}</p>
                                    {item.is_favorite && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 shrink-0" />}
                                  </div>
                                  <div className="flex items-center justify-between text-xs text-gray-500">
                                    <span>{item.brand || 'No brand'}</span>
                                    <span className="font-medium text-suede-600">{cs()}{item.purchase_price || item.price || 0}</span>
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
          </div>
        ) : viewMode === 'list' ? (
          /* List View */
          <div className="space-y-3">
            {items.map(item => (
              <motion.div 
                key={item.id} 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }}
                onClick={() => selectionMode ? toggleItemSelection(item.id) : (setSelectedItem(item), setShowItemDetail(true))}
                className={`
                  flex items-center gap-4 glass-card rounded-2xl p-4 hover:shadow-md cursor-pointer
                  ${selectedItems.includes(item.id) ? 'ring-2 ring-suede-500' : ''}
                `}
              >
                {selectionMode && (
                  <div className={`
                    w-5 h-5 rounded border-2 flex items-center justify-center shrink-0
                    ${selectedItems.includes(item.id) ? 'bg-suede-600 border-suede-600' : 'border-gray-300'}
                  `}>
                    {selectedItems.includes(item.id) && <Check className="w-3 h-3 text-white" />}
                  </div>
                )}
                
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 shrink-0">
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name} className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Shirt className="w-6 h-6 text-suede-200" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium truncate">{item.name}</h4>
                    {item.is_favorite && <Star className="w-4 h-4 text-amber-500 fill-amber-500 shrink-0" />}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Badge variant="outline" className="text-xs">{item.primary_category || item.category}</Badge>
                    {item.brand && <span>{item.brand}</span>}
                    {item.season && <SeasonIcon season={item.season} />}
                  </div>
                </div>
                
                <div className="text-right shrink-0">
                  <p className="font-semibold">{cs()}{item.purchase_price || item.price || 0}</p>
                </div>
                
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditItem(item); }} title="Edit Details">
                    <Pencil className="w-4 h-4 text-suede-500" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }} title="Delete">
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                  {showOutfitBuilder && (
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); addToOutfit(item); }} className="text-terracotta-600">
                      <Plus className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        ) : wardrobeMode === 'owned' ? (
          /* Grid View */
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {items.map((item, index) => (
              <motion.div 
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => selectionMode ? toggleItemSelection(item.id) : null}
                className={`
                  group relative glass-card rounded-2xl overflow-hidden hover:shadow-xl cursor-pointer
                  ${selectedItems.includes(item.id) ? 'ring-2 ring-suede-500' : ''}
                `}
              >
                {selectionMode && (
                  <div className={`
                    absolute top-3 left-3 z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center
                    ${selectedItems.includes(item.id) ? 'bg-suede-600 border-suede-600' : 'bg-white border-gray-300'}
                  `}>
                    {selectedItems.includes(item.id) && <Check className="w-4 h-4 text-white" />}
                  </div>
                )}
                
                <div className="aspect-[3/4] overflow-hidden bg-gray-50 relative">
                  {item.image_url ? (
                    <img 
                      src={item.image_url} 
                      alt={item.name} 
                      className="w-full h-full object-contain" 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-suede-50 to-stone-50">
                      <Shirt className="w-16 h-16 text-suede-300" />
                    </div>
                  )}
                  
                  {/* Hover Actions */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <Button 
                      size="icon" 
                      variant="secondary" 
                      className="rounded-full bg-white/90"
                      onClick={(e) => { e.stopPropagation(); setSelectedItem(item); setShowItemDetail(true); }}
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="icon" 
                      variant="secondary" 
                      className="rounded-full bg-white/90"
                      onClick={(e) => { e.stopPropagation(); openEditItem(item); }}
                      title="Edit Details"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    {canTryOn(item) && (
                      <Button 
                        size="icon" 
                        variant="secondary" 
                        className="rounded-full bg-gradient-to-r from-suede-500 to-suede-400 text-white"
                        onClick={(e) => { e.stopPropagation(); openTryOn(item); }}
                        title="Virtual Try-On"
                      >
                        <Sparkles className="w-4 h-4" />
                      </Button>
                    )}
                    {showOutfitBuilder && (
                      <Button 
                        size="icon" 
                        variant="secondary" 
                        className="rounded-full bg-terracotta-500 text-white"
                        onClick={(e) => { e.stopPropagation(); addToOutfit(item); }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    )}
                    <Button 
                      size="icon" 
                      variant="secondary" 
                      className={`rounded-full ${item.is_favorite ? 'bg-amber-500 text-white' : 'bg-white/90'}`}
                      onClick={(e) => { e.stopPropagation(); handleToggleFavorite(item.id); }}
                      title="Favorite"
                    >
                      <Star className={`w-4 h-4 ${item.is_favorite ? 'fill-white' : ''}`} />
                    </Button>
                  </div>
                </div>
                
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <Badge variant="outline" className="text-xs">{item.clothing_type?.replace('_', ' ') || item.subcategory || item.category}</Badge>
                    {item.is_favorite && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 shrink-0" />}
                  </div>
                  <h3 className="font-medium text-gray-900 mb-1 truncate">{item.name}</h3>
                  <p className="text-sm text-gray-500 mb-2">{item.brand || 'No brand'}</p>
                </div>
                
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-white/90 rounded-full"
                  onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </motion.div>
            ))}
          </div>
        ) : wardrobeMode === 'favorites' ? (() => {
          const favoriteItems = items.filter(i => i.is_favorite);
          const grouped = {};
          favoriteItems.forEach(item => {
            const cat = item.primary_category || item.display_category || item.category || 'other';
            const key = cat.toLowerCase();
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(item);
          });
          return favoriteItems.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center mx-auto mb-6">
                <Star className="w-10 h-10 text-amber-400" />
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-3">No favorites yet</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">Tap the star on any item in your wardrobe to add it to your favorites.</p>
              <Button variant="outline" onClick={() => setWardrobeMode('owned')} className="rounded-2xl">
                <Shirt className="w-4 h-4 mr-2" />Browse My Clothes
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(grouped).map(([key, catItems]) => {
                const Icon = CATEGORY_ICONS[key] || Shirt;
                return (
                  <div key={key}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-amber-400 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900 capitalize">{key}</h3>
                      <span className="text-xs text-gray-400">{catItems.length} item{catItems.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                      {catItems.map((item, idx) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.03 }}
                          className="group relative glass-card rounded-2xl overflow-hidden hover:shadow-xl cursor-pointer"
                          onClick={() => { setSelectedItem(item); setShowItemDetail(true); }}
                        >
                          <div className="aspect-[3/4] overflow-hidden bg-gray-50 relative">
                            {item.image_url ? (
                              <img src={item.image_url} alt={item.name} className="w-full h-full object-contain" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-suede-50 to-stone-50">
                                <Shirt className="w-12 h-12 text-suede-200" />
                              </div>
                            )}
                          </div>
                          <div className="p-3">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <p className="text-sm font-medium truncate">{item.name}</p>
                              <button onClick={(e) => { e.stopPropagation(); handleToggleFavorite(item.id); }} className="shrink-0">
                                <Star className={`w-4 h-4 ${item.is_favorite ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} />
                              </button>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>{item.brand || 'No brand'}</span>
                              <span className="font-medium text-suede-600">{cs()}{item.purchase_price || item.price || 0}</span>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })() : null}

        {/* Item Detail Dialog */}
        <Dialog open={showItemDetail} onOpenChange={setShowItemDetail}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl">
            {selectedItem && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-light flex items-center gap-2">
                    {selectedItem.name}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="grid md:grid-cols-2 gap-6 py-4">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-gray-100">
                    {selectedItem.image_url ? (
                      <img src={selectedItem.image_url} alt={selectedItem.name} className="w-full h-full object-contain" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Shirt className="w-16 h-16 text-suede-200" />
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      <Badge>{selectedItem.primary_category || selectedItem.category}</Badge>
                      {selectedItem.clothing_type && (
                        <Badge variant="outline">{selectedItem.clothing_type.replace('_', ' ')}</Badge>
                      )}
                      {selectedItem.is_favorite && (
                        <Badge className="bg-terracotta-100 text-terracotta-600">♥ Favorite</Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Brand</p>
                        <p className="font-medium">{selectedItem.brand || '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Price</p>
                        <p className="font-medium">{cs()}{selectedItem.purchase_price || selectedItem.price || 0}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Color</p>
                        <p className="font-medium capitalize">{selectedItem.color || '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Size</p>
                        <p className="font-medium">{selectedItem.size || '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Season</p>
                        <p className="font-medium flex items-center gap-1">
                          <SeasonIcon season={selectedItem.season} />
                          {selectedItem.season || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Material</p>
                        <p className="font-medium">{selectedItem.material || '-'}</p>
                      </div>

                    </div>
                    
                    {selectedItem.storage_location && (
                      <div className="flex items-start gap-2 bg-stone-50 rounded-xl p-3">
                        <MapPin className="w-4 h-4 text-suede-500 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-gray-500 text-xs">Storage Location</p>
                          <p className="text-sm font-medium">{selectedItem.storage_location}</p>
                        </div>
                      </div>
                    )}

                    {selectedItem.notes && (
                      <div>
                        <p className="text-gray-500 text-sm">Notes</p>
                        <p className="text-sm">{selectedItem.notes}</p>
                      </div>
                    )}
                    
                    <div className="flex flex-wrap gap-2 pt-4">
                      <Button 
                        variant={selectedItem.is_favorite ? "default" : "outline"}
                        className={`rounded-2xl ${selectedItem.is_favorite ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'border-amber-300 text-amber-600'}`}
                        onClick={() => handleToggleFavorite(selectedItem.id)}
                      >
                        <Star className={`w-4 h-4 mr-2 ${selectedItem.is_favorite ? 'fill-white' : ''}`} />
                        {selectedItem.is_favorite ? 'Favorited' : 'Favorite'}
                      </Button>
                      <Button 
                        variant="outline" 
                        className="rounded-2xl border-suede-200 text-suede-600" 
                        onClick={() => { openEditItem(selectedItem); setShowItemDetail(false); }}
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit
                      </Button>

                      {canTryOn(selectedItem) && (
                        <Button 
                          variant="outline" 
                          className="border-suede-200 text-suede-600 rounded-2xl" 
                          onClick={() => { openTryOn(selectedItem); setShowItemDetail(false); }}
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          Try On
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        className="border-terracotta-200 text-terracotta-600 rounded-2xl" 
                        onClick={() => { openAddToLookbook(selectedItem); }}
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        Stylebook
                      </Button>
                      {showOutfitBuilder && (
                        <Button 
                          variant="outline" 
                          className="border-terracotta-200 text-terracotta-600 rounded-2xl" 
                          onClick={() => { addToOutfit(selectedItem); setShowItemDetail(false); }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add to Outfit
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        className="rounded-2xl border-red-200 text-red-500 hover:bg-red-50" 
                        onClick={() => { handleDeleteItem(selectedItem.id); setShowItemDetail(false); }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Item Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
            <DialogHeader>
              <DialogTitle className="text-xl font-medium flex items-center gap-2">
                <Pencil className="w-5 h-5 text-suede-500" />
                Edit Item Details
              </DialogTitle>
              <DialogDescription>Update categorization and details for this item.</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              {/* Item Image Preview with Rotation */}
              {editingItem?.image && (
                <div className="space-y-3">
                  <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-center">
                    <div className="relative w-48 h-48">
                      <img 
                        src={editingItem.image} 
                        alt={editingItem.name}
                        className="w-full h-full object-contain transition-transform duration-300"
                        style={{ transform: `rotate(${editImageRotation}deg)` }}
                      />
                    </div>
                  </div>
                  
                  {/* Rotation Controls */}
                  <div className="bg-gradient-to-r from-stone-50 to-mud-50 rounded-xl p-3 border border-stone-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <RotateCcw className="w-4 h-4 text-stone-600" />
                        <span className="text-sm font-medium text-stone-700">Rotate Image</span>
                      </div>
                      {editImageRotation !== 0 && (
                        <span className="text-xs text-stone-500">{editImageRotation}°</span>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setEditImageRotation((prev) => (prev + 90) % 360)}
                        className="flex-col h-auto py-1.5"
                      >
                        <RotateCcw className="w-3 h-3 mb-0.5" />
                        <span className="text-xs">90°</span>
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setEditImageRotation((prev) => (prev + 180) % 360)}
                        className="flex-col h-auto py-1.5"
                      >
                        <RotateCcw className="w-3 h-3 mb-0.5" />
                        <span className="text-xs">180°</span>
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setEditImageRotation((prev) => (prev + 270) % 360)}
                        className="flex-col h-auto py-1.5"
                      >
                        <RotateCcw className="w-3 h-3 mb-0.5" />
                        <span className="text-xs">270°</span>
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setEditImageRotation(0)}
                        disabled={editImageRotation === 0}
                        className="flex-col h-auto py-1.5"
                      >
                        <RotateCcw className="w-3 h-3 mb-0.5" />
                        <span className="text-xs">Reset</span>
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <Label>Name</Label>
                <Input 
                  placeholder="Item name" 
                  className="mt-1 rounded-2xl" 
                  value={editForm.name} 
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})} 
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select 
                    value={editForm.primaryCategory} 
                    onValueChange={(v) => setEditForm({...editForm, primaryCategory: v, clothingType: ''})}
                  >
                    <SelectTrigger className="mt-1 rounded-2xl"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {categories && Object.entries(categories.categories).map(([key, cat]) => (
                        <SelectItem key={key} value={key}>
                          <span className="flex items-center gap-2">
                            {cat.icon} {cat.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  {!showCustomClothingType ? (
                    <Select 
                      value={editForm.clothingType} 
                      onValueChange={(v) => { if (v === '__custom__') { setShowCustomClothingType(true); setCustomClothingTypeValue(''); } else { setEditForm({...editForm, clothingType: v}); } }}
                      disabled={!editForm.primaryCategory}
                    >
                      <SelectTrigger className="mt-1 rounded-2xl"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {categories && editForm.primaryCategory && 
                          categories.categories[editForm.primaryCategory]?.types.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))
                        }
                        <SelectItem value="__custom__">✏️ Custom type...</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex gap-2 mt-1">
                      <Input
                        placeholder="Type your custom clothing type..."
                        className="rounded-2xl text-sm flex-1"
                        value={customClothingTypeValue}
                        onChange={(e) => setCustomClothingTypeValue(e.target.value)}
                        autoFocus
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl px-3 text-xs"
                        onClick={() => { if (customClothingTypeValue.trim()) { setEditForm({...editForm, clothingType: customClothingTypeValue.trim()}); } setShowCustomClothingType(false); setCustomClothingTypeValue(''); }}
                      >
                        {customClothingTypeValue.trim() ? 'Set' : 'Cancel'}
                      </Button>
                    </div>
                  )}
                  {!showCustomClothingType && editForm.primaryCategory && !categories?.categories?.[editForm.primaryCategory]?.types?.find(t => t.value === editForm.clothingType) && editForm.clothingType !== '' && (
                    <div className="mt-1.5 text-xs text-suede-600 bg-suede-50 rounded-lg px-2 py-1 flex items-center justify-between">
                      <span>Custom: {editForm.clothingType}</span>
                      <button onClick={() => { setShowCustomClothingType(true); setCustomClothingTypeValue(editForm.clothingType); }} className="text-suede-500 hover:text-suede-700 ml-2">✏️</button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Price</Label>
                  <div className="flex gap-1.5 mt-1">
                    <select
                      value={getCurrency()}
                      onChange={(e) => { setGlobalCurrency(e.target.value); }}
                      className="w-16 shrink-0 rounded-xl border border-stone-200 bg-stone-50 text-xs text-center py-2 focus:outline-none focus:ring-2 focus:ring-suede-500/30"
                    >
                      {getSupportedCurrencies().map(c => (
                        <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                      ))}
                    </select>
                    <Input 
                      type="number" 
                      placeholder="0" 
                      className="rounded-2xl" 
                      value={editForm.price} 
                      onChange={(e) => setEditForm({...editForm, price: e.target.value})} 
                    />
                  </div>
                </div>
                <div>
                  <Label>Color</Label>
                  <Select value={editForm.color} onValueChange={(v) => setEditForm({...editForm, color: v})}>
                    <SelectTrigger className="mt-1 rounded-2xl"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {categories?.colors.map(c => (
                        <SelectItem key={c.value} value={c.value}>
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full border" style={{ background: c.hex }} />
                            {c.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Size</Label>
                  <Input 
                    placeholder="M, 8, etc" 
                    className="mt-1 rounded-2xl" 
                    value={editForm.size} 
                    onChange={(e) => setEditForm({...editForm, size: e.target.value})} 
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Brand</Label>
                  <Input 
                    placeholder="e.g., Zara" 
                    className="mt-1 rounded-2xl" 
                    value={editForm.brand} 
                    onChange={(e) => setEditForm({...editForm, brand: e.target.value})} 
                  />
                </div>
                <div>
                  <Label>Season</Label>
                  <Select value={editForm.season} onValueChange={(v) => setEditForm({...editForm, season: v})}>
                    <SelectTrigger className="mt-1 rounded-2xl"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {categories?.seasons.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Material</Label>
                  <Input 
                    placeholder="e.g., Cotton, Leather" 
                    className="mt-1 rounded-2xl" 
                    value={editForm.material} 
                    onChange={(e) => setEditForm({...editForm, material: e.target.value})} 
                  />
                </div>
                <div>
                  <Label>Fit</Label>
                  <Select value={editForm.fitType} onValueChange={(v) => setEditForm({...editForm, fitType: v})}>
                    <SelectTrigger className="mt-1 rounded-2xl"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {categories?.fitTypes.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Condition</Label>
                <Select value={editForm.condition} onValueChange={(v) => setEditForm({...editForm, condition: v})}>
                  <SelectTrigger className="mt-1 rounded-2xl"><SelectValue placeholder="Select condition" /></SelectTrigger>
                  <SelectContent>
                    {['New', 'Like New', 'Good', 'Fair', 'Worn'].map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-stone-400" />
                  Storage Location
                  <span className="text-xs text-stone-400 font-normal">(optional)</span>
                </Label>
                <Input 
                  placeholder="e.g., Top shelf, Bedroom closet, Drawer 2" 
                  className="mt-1 rounded-2xl" 
                  value={editForm.storageLocation} 
                  onChange={(e) => setEditForm({...editForm, storageLocation: e.target.value})} 
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea 
                  placeholder="Any additional notes about this item..." 
                  className="mt-1 rounded-2xl resize-none" 
                  rows={2}
                  value={editForm.notes} 
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})} 
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button 
                  className="flex-1 bg-gradient-to-r from-suede-600 to-suede-500 text-white rounded-2xl py-5" 
                  onClick={handleUpdateItem} 
                  disabled={!editForm.name || !editForm.primaryCategory || isUpdatingItem}
                >
                  {isUpdatingItem ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Saving...</>
                  ) : (
                    <><Check className="w-5 h-5 mr-2" />Save Changes</>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  className="rounded-2xl py-5" 
                  onClick={() => setShowEditDialog(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Full-Screen Outfit Preview Modal — Draggable Flat-Lay */}
        <AnimatePresence>
          {showOutfitPreview && previewOutfit && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-2 md:p-4"
              onClick={() => setShowOutfitPreview(false)}
            >
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl md:rounded-3xl max-w-2xl w-full max-h-[95vh] overflow-auto flex flex-col"
                onClick={e => e.stopPropagation()}
              >
                {/* Header */}
                <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-stone-100 p-3 md:p-4 flex items-center justify-between z-10 flex-shrink-0">
                  <div className="min-w-0 flex-1 pr-2">
                    <h2 className="text-base md:text-xl font-bold text-stone-800 truncate">{previewOutfit.name}</h2>
                    <p className="text-xs md:text-sm text-stone-500 truncate">
                      {previewOutfit.is_current ? 'Preview — drag items to arrange' : `Worn ${previewOutfit.times_worn || 0} times`}
                    </p>
                  </div>
                  <div className="flex gap-1 md:gap-2 flex-shrink-0">
                    {previewOutfit.id && (
                      <Button
                        size="sm"
                        variant={previewOutfit.is_favorite ? "default" : "outline"}
                        onClick={() => toggleOutfitFavorite(previewOutfit.id)}
                        className={previewOutfit.is_favorite ? "bg-red-500 hover:bg-red-600 text-white" : ""}
                      >
                        <Heart className={`w-3 h-3 md:w-4 md:h-4 ${previewOutfit.is_favorite ? 'fill-white' : ''}`} />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setShowOutfitPreview(false)}>
                      <X className="w-4 h-4 md:w-5 md:h-5" />
                    </Button>
                  </div>
                </div>

                {/* Draggable Canvas */}
                <div className="p-3 md:p-6 bg-white overflow-auto flex-1">
                  <div className="relative mx-auto bg-stone-50 rounded-xl md:rounded-2xl border-2 border-dashed border-stone-200 overflow-hidden" style={{ maxWidth: '450px', height: 'min(520px, 60vh)' }}>
                    <p className="absolute top-3 left-0 right-0 text-center text-[10px] md:text-xs text-stone-300 pointer-events-none z-0">Drag items to reposition</p>
                    {/* Build list of all preview items */}
                    {(() => {
                      const previewItems = [];
                      if (previewOutfit.outerwear_image) previewItems.push({ key: 'outerwear', src: previewOutfit.outerwear_image, label: '🧥 Outerwear', initX: 10, initY: 10, w: 130, h: 150 });
                      if (previewOutfit.top_image) previewItems.push({ key: 'top', src: previewOutfit.top_image, label: '👕 Top', initX: 140, initY: 20, w: 160, h: 190 });
                      if (previewOutfit.dress_image && !previewOutfit.top_image) previewItems.push({ key: 'dress', src: previewOutfit.dress_image, label: '👗 Dress', initX: 140, initY: 20, w: 160, h: 240 });
                      if (previewOutfit.bottom_image) previewItems.push({ key: 'bottom', src: previewOutfit.bottom_image, label: '👖 Bottom', initX: 140, initY: 220, w: 150, h: 180 });
                      if (previewOutfit.shoes_image) previewItems.push({ key: 'shoes', src: previewOutfit.shoes_image, label: '👟 Shoes', initX: 170, initY: 410, w: 110, h: 90 });
                      if (previewOutfit.bag_image) previewItems.push({ key: 'bag', src: previewOutfit.bag_image, label: '👜 Bag', initX: 330, initY: 160, w: 100, h: 120 });
                      // Accessories from saved outfits
                      if (previewOutfit.accessories?.length > 0) {
                        previewOutfit.accessories.forEach((acc, idx) => {
                          if (acc.image_url) {
                            previewItems.push({ key: `acc-${acc.id || idx}`, src: acc.image_url, label: `✨ ${acc.name || 'Accessory'}`, initX: 320, initY: 20 + idx * 70, w: 80, h: 80 });
                          }
                        });
                      }

                      if (previewItems.length === 0) {
                        return (
                          <div className="absolute inset-0 flex items-center justify-center text-stone-300">
                            <div className="text-center">
                              <Layers className="w-12 h-12 mx-auto mb-2 opacity-30" />
                              <p className="text-sm">No items in this outfit</p>
                            </div>
                          </div>
                        );
                      }

                      return previewItems.map((item, idx) => (
                        <motion.div
                          key={item.key}
                          drag
                          dragMomentum={false}
                          dragElastic={0}
                          initial={{ x: item.initX, y: item.initY, opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: idx * 0.08 }}
                          style={{ width: item.w, height: item.h, position: 'absolute', top: 0, left: 0, zIndex: 10 + idx }}
                          whileDrag={{ scale: 1.08, zIndex: 50 }}
                          className="cursor-grab active:cursor-grabbing group"
                        >
                          <img
                            src={item.src}
                            alt={item.label}
                            className="w-full h-full object-contain drop-shadow-lg pointer-events-none select-none"
                            draggable={false}
                          />
                          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-white/80 backdrop-blur-sm rounded-full px-2 py-0.5 text-[10px] font-medium text-stone-500 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm whitespace-nowrap">
                            {item.label}
                          </div>
                        </motion.div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Actions Footer */}
                <div className="sticky bottom-0 bg-white/95 backdrop-blur-md border-t border-stone-100 p-3 md:p-4 flex gap-2 md:gap-3 flex-shrink-0">
                  <Button
                    className="flex-1 bg-olive-600 hover:bg-olive-700 text-white rounded-xl text-sm md:text-base py-2 md:py-2.5"
                    onClick={() => setShowOutfitPreview(false)}
                  >
                    Done
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ========== STYLE MAID AI AGENT (Portal to body root) ========== */}
        {createPortal(<>
        {/* Floating Style Maid Button — fixed bottom-right, stacked above Guide Agent */}
        <AnimatePresence>
          {!showStyleMaid && !styleMaidDismissed && !guideAgentOpen && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              className="fixed z-[45]"
              style={{ 
                bottom: 'calc(140px + env(safe-area-inset-bottom, 0px))',
                right: '16px'
              }}
            >
              <button
                onClick={() => setShowStyleMaid(true)}
                className="relative w-12 h-12 rounded-full shadow-lg flex items-center justify-center bg-gradient-to-br from-suede-500 via-terracotta-500 to-amber-400 active:scale-95 transition-transform"
                aria-label="Open Style Maid"
              >
                <Bot className="w-5 h-5 text-white" />
                <motion.span 
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-olive-400 rounded-full border-2 border-white"
                />
              </button>
              {/* Dismiss button */}
              <button
                onClick={(e) => { 
                  e.stopPropagation(); 
                  setStyleMaidDismissed(true);
                  localStorage.setItem('styleMaidDismissed', 'true');
                }}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-500/80 rounded-full flex items-center justify-center shadow active:scale-90 transition-transform"
                aria-label="Hide Style Maid"
              >
                <X className="w-2.5 h-2.5 text-white" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Style Maid Chat Panel — centered, resizable, mobile-friendly */}
        <AnimatePresence>
          {showStyleMaid && (
              <motion.div
                ref={styleMaidOverlayRef}
                initial={{ opacity: 0, y: '100%' }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="fixed inset-0 z-[100] bg-white flex flex-col"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-suede-500 via-terracotta-500 to-amber-400 flex-shrink-0" style={{ paddingTop: 'calc(12px + env(safe-area-inset-top, 0px))' }}>
                  <div className="flex items-center justify-between px-4 pb-3 pt-1">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Bot className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white text-base">Style Maid ✨</h3>
                        <p className="text-white/70 text-xs">Your AI fashion assistant</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowStyleMaid(false)}
                      className="p-2.5 bg-white/20 hover:bg-white/30 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                      aria-label="Close Style Maid"
                    >
                      <X className="w-5 h-5 text-white" />
                    </button>
                  </div>
                  {/* Quick Actions */}
                  <div className="flex gap-2 mt-3 px-4 pb-3 overflow-x-auto scrollbar-hide">
                    <button 
                      onClick={() => sendStyleMaidMessage(null, "What should I wear today?")}
                      className="flex-shrink-0 px-3 py-2 bg-white/20 hover:bg-white/30 active:bg-white/40 text-white text-xs rounded-full backdrop-blur-sm transition-colors"
                    >
                      👗 Today
                    </button>
                    <button 
                      onClick={() => {
                        setRecommendationCriteria(prev => ({ ...prev, occasion: 'work' }));
                        getAiRecommendations();
                      }}
                      className="flex-shrink-0 px-3 py-2 bg-white/20 hover:bg-white/30 active:bg-white/40 text-white text-xs rounded-full backdrop-blur-sm transition-colors"
                    >
                      💼 Work
                    </button>
                    <button 
                      onClick={() => {
                        setRecommendationCriteria(prev => ({ ...prev, occasion: 'date night' }));
                        getAiRecommendations();
                      }}
                      className="flex-shrink-0 px-3 py-2 bg-white/20 hover:bg-white/30 active:bg-white/40 text-white text-xs rounded-full backdrop-blur-sm transition-colors"
                    >
                      💕 Date
                    </button>
                    <button 
                      onClick={() => getQuickAiSuggestion()}
                      disabled={styleMaidLoading}
                      className="flex-shrink-0 px-3 py-2 bg-white/20 hover:bg-white/30 active:bg-white/40 text-white text-xs rounded-full backdrop-blur-sm transition-colors disabled:opacity-50"
                    >
                      {styleMaidLoading ? '⏳ Thinking...' : '✨ Surprise'}
                    </button>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-stone-50">
                  {styleMaidMessages.length === 0 && (
                    <div className="text-center py-8">
                      <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-suede-100 to-terracotta-100 flex items-center justify-center">
                        <Sparkles className="w-7 h-7 text-suede-500" />
                      </div>
                      <p className="text-stone-600 font-medium text-base">Hey there! 👋</p>
                      <p className="text-stone-400 text-sm mt-1.5 px-6">
                        Ask me about outfits, color matching, or let me suggest looks from your wardrobe!
                      </p>
                    </div>
                  )}
                  {styleMaidMessages.map((msg, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${
                        msg.role === 'user' 
                          ? 'bg-gradient-to-r from-suede-500 to-suede-400 text-white rounded-br-sm' 
                          : 'bg-white text-stone-700 shadow-sm border border-stone-100 rounded-bl-sm'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                        {msg.upgradeNeeded && (
                          <button
                            onClick={() => navigate('/account')}
                            className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-terracotta-500 to-suede-500 text-white text-xs font-semibold rounded-lg active:scale-95 transition-transform"
                          >
                            👑 Upgrade Plan
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                  {styleMaidLoading && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                      <div className="bg-white rounded-2xl px-3.5 py-3 shadow-sm border border-stone-100 rounded-bl-sm">
                        <div className="flex gap-1">
                          <motion.span animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0 }} className="w-2 h-2 bg-suede-400 rounded-full" />
                          <motion.span animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }} className="w-2 h-2 bg-terracotta-400 rounded-full" />
                          <motion.span animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }} className="w-2 h-2 bg-orange-400 rounded-full" />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Input */}
                <form onSubmit={(e) => sendStyleMaidMessage(e)} className="p-3 bg-white border-t border-stone-100 flex-shrink-0" style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={styleMaidInput}
                      onChange={(e) => setStyleMaidInput(e.target.value)}
                      placeholder="Ask about outfits, colors..."
                      className="flex-1 px-4 py-3 bg-stone-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-suede-300 focus:bg-white transition-all"
                      style={{ fontSize: '16px' }}
                    />
                    <button
                      type="submit"
                      disabled={!styleMaidInput.trim() || styleMaidLoading}
                      className="w-11 h-11 rounded-xl bg-gradient-to-r from-suede-500 to-suede-400 text-white flex items-center justify-center disabled:opacity-50 active:scale-95 transition-transform flex-shrink-0"
                      aria-label="Send"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              </motion.div>
          )}
        </AnimatePresence>
        </>, document.body)}

        {/* AI Recommendations Modal */}
        <AnimatePresence>
          {showRecommendations && aiRecommendations.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowRecommendations(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl"
              >
                <div className="bg-gradient-to-r from-suede-500 via-terracotta-500 to-amber-400 p-6 text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <Lightbulb className="w-6 h-6" />
                    <h2 className="text-xl font-bold">AI Outfit Suggestions</h2>
                  </div>
                  <p className="text-white/80 text-sm">
                    Based on your wardrobe and style preferences
                  </p>
                </div>
                
                <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
                  {aiRecommendations.map((rec, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="bg-gradient-to-r from-suede-50 to-terracotta-50 rounded-2xl p-4 border border-suede-100"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-stone-800">{rec.name}</h3>
                          <p className="text-sm text-stone-500">{rec.style}</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => applyRecommendation(rec)}
                          className="bg-gradient-to-r from-suede-500 to-suede-400 text-white"
                        >
                          Apply
                        </Button>
                      </div>
                      
                      {/* Item previews */}
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {rec.items?.map((item, itemIdx) => (
                          <div key={itemIdx} className="flex-shrink-0">
                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-white shadow-sm">
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-stone-100">
                                  <span className="text-2xl">{
                                    item.role === 'top' ? '👕' :
                                    item.role === 'bottom' ? '👖' :
                                    item.role === 'dress' ? '👗' :
                                    item.role === 'shoes' ? '👟' :
                                    item.role === 'bag' ? '👜' : '✨'
                                  }</span>
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-center text-stone-500 mt-1 capitalize">{item.role}</p>
                          </div>
                        ))}
                      </div>
                      
                      {rec.reasoning && (
                        <p className="text-xs text-suede-600 mt-2 italic">"{rec.reasoning}"</p>
                      )}
                    </motion.div>
                  ))}
                </div>
                
                <div className="p-4 bg-stone-50 border-t border-stone-100 flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowRecommendations(false)}
                  >
                    Close
                  </Button>
                  <Button 
                    className="flex-1 bg-gradient-to-r from-suede-500 to-suede-400 text-white"
                    onClick={getAiRecommendations}
                    disabled={styleMaidLoading}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Get More Ideas
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Virtual Try-On Modal */}
        {showTryOn && (
          <VirtualTryOn 
            onClose={() => { setShowTryOn(false); setTryOnItem(null); }}
            selectedGarment={tryOnItem}
          />
        )}

        {/* ========== CAPSULE WARDROBE DIALOG ========== */}
        <Dialog open={showCapsuleDialog} onOpenChange={setShowCapsuleDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden p-0">
            <div className="px-6 pt-6 pb-2">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center">
                  <Archive className="w-5 h-5 text-white" />
                </div>
                {editingCapsule !== null ? 'Edit Capsule Wardrobe' : 'Create Capsule Wardrobe'}
              </DialogTitle>
            </DialogHeader>
            </div>
            <div className="flex-1 overflow-y-auto px-6">
            <div className="space-y-4 py-4">
              <div>
                <Label>Capsule Name</Label>
                <Input 
                  placeholder="e.g., Summer Vacation, Minimal Work Capsule"
                  value={currentCapsule.name}
                  onChange={(e) => setCurrentCapsule(p => ({ ...p, name: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={currentCapsule.type} onValueChange={(v) => setCurrentCapsule(p => ({ ...p, type: v, customType: v === 'custom' ? p.customType : '' }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seasonal">Seasonal</SelectItem>
                    <SelectItem value="travel">Travel</SelectItem>
                    <SelectItem value="minimalist">Minimalist Challenge</SelectItem>
                    <SelectItem value="work">Work Capsule</SelectItem>
                    <SelectItem value="casual">Casual Capsule</SelectItem>
                    <SelectItem value="custom">Custom...</SelectItem>
                  </SelectContent>
                </Select>
                {currentCapsule.type === 'custom' && (
                  <Input
                    placeholder="Enter your custom type..."
                    value={currentCapsule.customType || ''}
                    onChange={(e) => setCurrentCapsule(p => ({ ...p, customType: e.target.value }))}
                    className="mt-2"
                  />
                )}
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea 
                  placeholder="What's this capsule for?"
                  value={currentCapsule.description}
                  onChange={(e) => setCurrentCapsule(p => ({ ...p, description: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Add Items ({currentCapsule.items.length} selected)</Label>
                <div className="mt-2 max-h-64 overflow-y-auto p-2 bg-stone-50 rounded-xl space-y-1">
                  {(() => {
                    const grouped = {};
                    items.forEach(item => {
                      const cat = item.primary_category || 'other';
                      if (!grouped[cat]) grouped[cat] = [];
                      grouped[cat].push(item);
                    });
                    // Sort items by color within each category
                    for (const k of Object.keys(grouped)) grouped[k].sort(sortItemsByColor);
                    const categoryOrder = ['tops', 'bottoms', 'dresses', 'outerwear', 'sportswear', 'footwear', 'accessories', 'other'];
                    return Object.keys(grouped).sort((a, b) => {
                      const ai = categoryOrder.indexOf(a); const bi = categoryOrder.indexOf(b);
                      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                    }).map(catKey => {
                      const IconComp = CATEGORY_ICONS[catKey] || Shirt;
                      const isExpanded = expandedPickerCategories[`capsule-${catKey}`] !== false;
                      const selectedInCat = grouped[catKey].filter(item => currentCapsule.items.find(i => i.id === item.id)).length;
                      return (
                        <div key={catKey} className="border border-stone-100 rounded-xl overflow-hidden bg-white">
                          <button
                            onClick={() => setExpandedPickerCategories(prev => ({ ...prev, [`capsule-${catKey}`]: !isExpanded }))}
                            className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-stone-50 transition-colors"
                          >
                            <span className="w-5 h-5 flex items-center justify-center text-sm">{typeof IconComp === 'function' ? <IconComp className="w-3.5 h-3.5 text-stone-500" /> : <Shirt className="w-3.5 h-3.5 text-stone-500" />}</span>
                            <span className="text-xs font-semibold uppercase tracking-wider text-stone-600 flex-1 text-left">{catKey.charAt(0).toUpperCase() + catKey.slice(1)}</span>
                            <span className="text-[10px] text-stone-400">({grouped[catKey].length})</span>
                            {selectedInCat > 0 && <span className="text-[10px] font-semibold text-suede-600 bg-suede-50 px-1.5 py-0.5 rounded-full">{selectedInCat} ✓</span>}
                            <ChevronDown className={`w-3.5 h-3.5 text-stone-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                          {isExpanded && (
                            <div className="grid grid-cols-4 gap-2 p-2 pt-0 border-t border-stone-50">
                              {grouped[catKey].map(item => (
                                <button
                                  key={item.id}
                                  onClick={() => {
                                    if (currentCapsule.items.find(i => i.id === item.id)) {
                                      setCurrentCapsule(p => ({ ...p, items: p.items.filter(i => i.id !== item.id) }));
                                    } else {
                                      setCurrentCapsule(p => ({ ...p, items: [...p.items, item] }));
                                    }
                                  }}
                                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${currentCapsule.items.find(i => i.id === item.id) ? 'border-suede-500 ring-2 ring-suede-200' : 'border-transparent hover:border-stone-300'}`}
                                >
                                  {item.image_url ? (
                                    <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full bg-stone-200 flex items-center justify-center"><Shirt className="w-4 h-4 text-stone-400" /></div>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
            </div>
            <div className="px-6 py-4 border-t border-stone-100 bg-white flex-shrink-0">
              <Button onClick={saveCapsule} disabled={savingCapsule} className="w-full bg-gradient-to-r from-suede-500 to-suede-400 text-white rounded-xl">
                {savingCapsule ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Capsule'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ========== PACKING LIST DIALOG ========== */}
        <Dialog open={showPackingDialog} onOpenChange={setShowPackingDialog}>
          <DialogContent className="max-w-2xl flex flex-col overflow-hidden" style={{ maxHeight: '80vh' }}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-mud-500 to-mud-400 flex items-center justify-center">
                  <Luggage className="w-5 h-5 text-white" />
                </div>
                {editingPackingList !== null ? 'Edit Packing List' : 'Create Packing List'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 overflow-y-auto overflow-x-hidden flex-1 min-h-0">
              <div>
                <Label>Trip Name</Label>
                <Input 
                  placeholder="e.g., Paris Vacation, Business Trip NYC"
                  value={currentPackingList.name}
                  onChange={(e) => setCurrentPackingList(p => ({ ...p, name: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Destination</Label>
                <Input 
                  placeholder="Where are you going?"
                  value={currentPackingList.destination}
                  onChange={(e) => setCurrentPackingList(p => ({ ...p, destination: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start Date</Label>
                  <Input 
                    type="date"
                    value={currentPackingList.startDate}
                    onChange={(e) => setCurrentPackingList(p => ({ ...p, startDate: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input 
                    type="date"
                    value={currentPackingList.endDate}
                    onChange={(e) => setCurrentPackingList(p => ({ ...p, endDate: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>
              {/* Cover Photo — fixed height container to prevent layout shift */}
              <div>
                <Label>Trip Cover Photo <span className="text-stone-400 font-normal">(optional)</span></Label>
                <div className="mt-2 h-24 relative">
                  {currentPackingList.coverImage ? (
                    <div className="relative rounded-xl overflow-hidden h-full">
                      <img 
                        src={currentPackingList.coverImage} 
                        alt="Trip cover" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 flex items-center justify-end gap-2">
                        <label className="cursor-pointer inline-flex items-center gap-1 bg-white/90 rounded-full px-3 py-1.5 hover:bg-white transition-colors text-xs font-medium text-stone-700">
                          <Camera className="w-3.5 h-3.5" />
                          Change
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = (ev) => setCurrentPackingList(p => ({ ...p, coverImage: ev.target.result }));
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </label>
                        <button 
                          onClick={() => setCurrentPackingList(p => ({ ...p, coverImage: '' }))}
                          className="inline-flex items-center gap-1 bg-white/90 rounded-full px-3 py-1.5 hover:bg-white transition-colors text-xs font-medium text-red-500"
                        >
                          <X className="w-3.5 h-3.5" />
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className="cursor-pointer flex flex-col items-center justify-center h-full rounded-xl border-2 border-dashed border-mud-200 bg-mud-50/50 hover:bg-mud-50 transition-colors">
                      <ImageIcon className="w-5 h-5 text-mud-300 mb-1" />
                      <span className="text-xs text-mud-400">Tap to add a cover photo</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (ev) => setCurrentPackingList(p => ({ ...p, coverImage: ev.target.result }));
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
              <div>
                <Label>Select Items to Pack ({currentPackingList.items.length} selected)</Label>
                <div className="mt-2 max-h-64 overflow-y-auto p-2 bg-stone-50 rounded-xl space-y-1">
                  {(() => {
                    const grouped = {};
                    items.forEach(item => {
                      const cat = item.primary_category || 'other';
                      if (!grouped[cat]) grouped[cat] = [];
                      grouped[cat].push(item);
                    });
                    // Sort items by color within each category
                    for (const k of Object.keys(grouped)) grouped[k].sort(sortItemsByColor);
                    const categoryOrder = ['tops', 'bottoms', 'dresses', 'outerwear', 'sportswear', 'footwear', 'accessories', 'other'];
                    return Object.keys(grouped).sort((a, b) => {
                      const ai = categoryOrder.indexOf(a); const bi = categoryOrder.indexOf(b);
                      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                    }).map(catKey => {
                      const IconComp = CATEGORY_ICONS[catKey] || Shirt;
                      const isExpanded = expandedPickerCategories[`packing-${catKey}`] !== false;
                      const selectedInCat = grouped[catKey].filter(item => currentPackingList.items.find(i => i.id === item.id)).length;
                      return (
                        <div key={catKey} className="border border-stone-100 rounded-xl overflow-hidden bg-white">
                          <button
                            onClick={() => setExpandedPickerCategories(prev => ({ ...prev, [`packing-${catKey}`]: !isExpanded }))}
                            className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-stone-50 transition-colors"
                          >
                            <span className="w-5 h-5 flex items-center justify-center text-sm">{typeof IconComp === 'function' ? <IconComp className="w-3.5 h-3.5 text-stone-500" /> : <Shirt className="w-3.5 h-3.5 text-stone-500" />}</span>
                            <span className="text-xs font-semibold uppercase tracking-wider text-stone-600 flex-1 text-left">{catKey.charAt(0).toUpperCase() + catKey.slice(1)}</span>
                            <span className="text-[10px] text-stone-400">({grouped[catKey].length})</span>
                            {selectedInCat > 0 && <span className="text-[10px] font-semibold text-mud-600 bg-mud-50 px-1.5 py-0.5 rounded-full">{selectedInCat} ✓</span>}
                            <ChevronDown className={`w-3.5 h-3.5 text-stone-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                          {isExpanded && (
                            <div className="grid grid-cols-4 gap-2 p-2 pt-0 border-t border-stone-50">
                              {grouped[catKey].map(item => (
                                <button
                                  key={item.id}
                                  onClick={() => {
                                    if (currentPackingList.items.find(i => i.id === item.id)) {
                                      setCurrentPackingList(p => ({ ...p, items: p.items.filter(i => i.id !== item.id) }));
                                    } else {
                                      setCurrentPackingList(p => ({ ...p, items: [...p.items, item] }));
                                    }
                                  }}
                                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${currentPackingList.items.find(i => i.id === item.id) ? 'border-mud-500 ring-2 ring-mud-200' : 'border-transparent hover:border-stone-300'}`}
                                >
                                  {item.image_url ? (
                                    <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full bg-stone-200 flex items-center justify-center"><Shirt className="w-4 h-4 text-stone-400" /></div>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 pt-3 border-t border-stone-100">
              <Button onClick={savePackingList} disabled={savingPacking} className="w-full bg-gradient-to-r from-mud-500 to-mud-400 text-white rounded-xl">
                {savingPacking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Packing List'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ========== CAPSULE PICKER FOR PACKING ========== */}
        <Dialog open={showCapsulePickerForPacking} onOpenChange={(open) => { setShowCapsulePickerForPacking(open); if (!open) { setSelectedCapsuleForPacking(null); setSelectedCapsuleItems(new Set()); } }}>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Archive className="w-5 h-5 text-suede-500" />
                {selectedCapsuleForPacking ? 'Select Items' : 'Choose a Capsule'}
              </DialogTitle>
            </DialogHeader>

            {!selectedCapsuleForPacking ? (
              /* Step 1: Pick a capsule */
              <div className="space-y-2 py-2">
                {capsules.map(cap => {
                  const existingIds = new Set((viewingPackingList?.items || []).map(i => i.id));
                  const availableCount = cap.items.filter(i => !existingIds.has(i.id)).length;
                  return (
                    <button
                      key={cap.name}
                      onClick={() => {
                        setSelectedCapsuleForPacking(cap);
                        // Pre-select all items not already in the packing list
                        const newIds = new Set(cap.items.filter(i => !existingIds.has(i.id)).map(i => i.id));
                        setSelectedCapsuleItems(newIds);
                      }}
                      className="w-full flex items-center gap-3 p-3 rounded-xl border border-stone-200 hover:border-suede-300 hover:bg-suede-50/50 transition-all text-left"
                    >
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-suede-400 to-suede-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                        {cap.items.length}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-stone-800 truncate">{cap.name}</div>
                        <div className="text-xs text-stone-500">{cap.items.length} items{availableCount < cap.items.length ? ` · ${cap.items.length - availableCount} already added` : ''}</div>
                      </div>
                      {availableCount === 0 ? (
                        <span className="text-xs text-stone-400 flex-shrink-0">All added ✓</span>
                      ) : (
                        <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              /* Step 2: Pick items from that capsule */
              <div className="space-y-3 py-2">
                <div className="flex items-center justify-between">
                  <button onClick={() => { setSelectedCapsuleForPacking(null); setSelectedCapsuleItems(new Set()); }} className="flex items-center gap-1 text-sm text-suede-600 hover:text-suede-700">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <span className="text-sm font-medium text-stone-600">{selectedCapsuleForPacking.name}</span>
                </div>

                {/* Select All / Deselect All */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" size="sm"
                    onClick={() => {
                      const existingIds = new Set((viewingPackingList?.items || []).map(i => i.id));
                      const allIds = new Set(selectedCapsuleForPacking.items.filter(i => !existingIds.has(i.id)).map(i => i.id));
                      setSelectedCapsuleItems(allIds);
                    }}
                    className="flex-1 rounded-xl text-xs"
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="outline" size="sm"
                    onClick={() => setSelectedCapsuleItems(new Set())}
                    className="flex-1 rounded-xl text-xs"
                  >
                    Deselect All
                  </Button>
                </div>

                {/* Item grid */}
                <div className="grid grid-cols-3 gap-2 max-h-[40vh] overflow-y-auto">
                  {selectedCapsuleForPacking.items.map(item => {
                    const existingIds = new Set((viewingPackingList?.items || []).map(i => i.id));
                    const alreadyInList = existingIds.has(item.id);
                    const isSelected = selectedCapsuleItems.has(item.id);
                    return (
                      <button
                        key={item.id}
                        disabled={alreadyInList}
                        onClick={() => {
                          setSelectedCapsuleItems(prev => {
                            const next = new Set(prev);
                            if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
                            return next;
                          });
                        }}
                        className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                          alreadyInList ? 'opacity-40 border-stone-200 cursor-not-allowed' 
                          : isSelected ? 'border-suede-500 ring-2 ring-suede-200' 
                          : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        {item.image_url ? (
                          <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-stone-100 flex items-center justify-center">
                            <Shirt className="w-6 h-6 text-stone-300" />
                          </div>
                        )}
                        {isSelected && !alreadyInList && (
                          <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-suede-500 flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                        {alreadyInList && (
                          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                            <span className="text-[10px] font-medium text-stone-500 bg-white/80 px-1.5 py-0.5 rounded">Added</span>
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-1">
                          <span className="text-[10px] text-white font-medium truncate block">{item.name}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Add button */}
                <Button
                  disabled={selectedCapsuleItems.size === 0}
                  onClick={() => {
                    const itemsToAdd = selectedCapsuleForPacking.items.filter(i => selectedCapsuleItems.has(i.id));
                    if (itemsToAdd.length === 0) return;
                    const updated = { ...viewingPackingList, items: [...viewingPackingList.items, ...itemsToAdd] };
                    setPackingLists(prev => prev.map(p => p.id === viewingPackingList.id ? updated : p));
                    setViewingPackingList(updated);
                    setShowCapsulePickerForPacking(false);
                    setSelectedCapsuleForPacking(null);
                    setSelectedCapsuleItems(new Set());
                    toast({ title: '✅ Items Added', description: `${itemsToAdd.length} items from "${selectedCapsuleForPacking.name}" added to your packing list` });
                  }}
                  className="w-full bg-gradient-to-r from-suede-500 to-suede-400 text-white rounded-xl"
                >
                  Add {selectedCapsuleItems.size} Item{selectedCapsuleItems.size !== 1 ? 's' : ''} to Packing List
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ========== CALENDAR OUTFIT DIALOG ========== */}
        <Dialog open={showCalendarOutfitDialog} onOpenChange={(open) => { setShowCalendarOutfitDialog(open); if (!open) { setSelectedCalendarDate(null); setEditingSlotId(null); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {(() => {
                  const timeOpt = getTimeOption(calendarOutfitTimeOfDay);
                  const TimeIcon = timeOpt.icon;
                  return (
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${timeOpt.color} flex items-center justify-center`}>
                      <TimeIcon className="w-5 h-5 text-white" />
                    </div>
                  );
                })()}
                <div>
                  <span>{editingSlotId ? 'Edit Outfit' : 'Add Outfit'}</span>
                  {selectedCalendarDate && (
                    <p className="text-sm font-normal text-stone-500">
                      {selectedCalendarDate.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  )}
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Time of Day selector */}
              <div>
                <Label className="mb-2 block">Time of Day</Label>
                <div className="grid grid-cols-5 gap-2">
                  {TIME_OF_DAY_OPTIONS.map(opt => {
                    const OptIcon = opt.icon;
                    const isActive = calendarOutfitTimeOfDay === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setCalendarOutfitTimeOfDay(opt.value)}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all ${
                          isActive 
                            ? `border-current ${opt.textColor} ${opt.bgColor} shadow-sm` 
                            : 'border-stone-100 hover:border-stone-200 text-stone-400 hover:text-stone-500'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? `bg-gradient-to-br ${opt.color} text-white` : 'bg-stone-100'}`}>
                          <OptIcon className="w-4 h-4" />
                        </div>
                        <span className="text-[11px] font-medium">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Occasion */}
              <div>
                <Label className="mb-2 block">Occasion</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {['Work', 'Casual', 'Date Night', 'Dinner', 'Brunch', 'Gym', 'Party', 'Travel', 'Wedding', 'Meeting'].map(occ => (
                    <button
                      key={occ}
                      onClick={() => setCalendarOutfitOccasion(calendarOutfitOccasion === occ ? '' : occ)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        calendarOutfitOccasion === occ
                          ? 'bg-olive-500 text-white border-olive-500'
                          : 'bg-white text-stone-500 border-stone-200 hover:border-olive-300 hover:text-olive-600'
                      }`}
                    >
                      {occ}
                    </button>
                  ))}
                </div>
                <Input
                  placeholder="Or type a custom occasion..."
                  value={calendarOutfitOccasion}
                  onChange={(e) => setCalendarOutfitOccasion(e.target.value)}
                  className="text-sm"
                />
              </div>

              {/* Selected items preview */}
              {calendarOutfitItems.length > 0 && (
                <div>
                  <Label className="mb-2 block">Selected Items ({calendarOutfitItems.length})</Label>
                  <div className="flex flex-wrap gap-2 p-3 bg-olive-50 rounded-xl border border-olive-100">
                    {calendarOutfitItems.map((item) => (
                      <div key={item.id} className="relative group">
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-stone-100 border-2 border-olive-300">
                          {item.image_url ? (
                            <img src={item.processed_image_url || item.image_url} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Shirt className="w-6 h-6 text-stone-300" />
                            </div>
                          )}
                        </div>
                        <button 
                          onClick={() => setCalendarOutfitItems(prev => prev.filter(i => i.id !== item.id))}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <p className="text-[10px] text-stone-500 mt-1 truncate w-16 text-center">{item.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Item selector — categorized */}
              <div>
                <Label>Select Items from Your Wardrobe</Label>
                <div className="mt-2 max-h-64 overflow-y-auto bg-stone-50 rounded-xl border border-stone-100">
                  {(() => {
                    const grouped = {};
                    items.forEach(item => {
                      const cat = item.primary_category || 'other';
                      if (!grouped[cat]) grouped[cat] = [];
                      grouped[cat].push(item);
                    });
                    // Sort items by color within each category
                    for (const k of Object.keys(grouped)) grouped[k].sort(sortItemsByColor);
                    const categoryOrder = ['tops', 'bottoms', 'dresses', 'outerwear', 'sportswear', 'footwear', 'accessories', 'swimwear', 'other'];
                    const sortedCats = Object.keys(grouped).sort((a, b) => {
                      const ai = categoryOrder.indexOf(a);
                      const bi = categoryOrder.indexOf(b);
                      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                    });
                    
                    return sortedCats.map(catKey => {
                      const catItems = grouped[catKey];
                      const IconComp = CATEGORY_ICONS[catKey] || Shirt;
                      const catLabel = categories?.categories?.[catKey]?.name || catKey.charAt(0).toUpperCase() + catKey.slice(1);
                      const selectedInCat = catItems.filter(item => calendarOutfitItems.find(i => i.id === item.id)).length;
                      const isExpanded = expandedPickerCategories[`calendar-${catKey}`] !== false;
                      
                      return (
                        <div key={catKey} className="border-b border-stone-100 last:border-b-0">
                          <button
                            onClick={() => setExpandedPickerCategories(prev => ({ ...prev, [`calendar-${catKey}`]: !isExpanded }))}
                            className="w-full flex items-center gap-2 px-3 py-2 bg-stone-100/60 sticky top-0 z-10 hover:bg-stone-100 transition-colors"
                          >
                            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-olive-500 to-olive-400 flex items-center justify-center text-white">
                              {typeof IconComp === 'function' ? <IconComp className="w-3 h-3" /> : <Shirt className="w-3 h-3" />}
                            </div>
                            <span className="text-xs font-semibold text-stone-600 flex-1 text-left">{catLabel}</span>
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{catItems.length}</Badge>
                            {selectedInCat > 0 && (
                              <Badge className="text-[10px] px-1.5 py-0 bg-olive-500 text-white">{selectedInCat} ✓</Badge>
                            )}
                            <ChevronDown className={`w-3.5 h-3.5 text-stone-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                          {isExpanded && <div className="grid grid-cols-5 gap-2 p-2">
                            {catItems.map(item => {
                              const isSelected = calendarOutfitItems.find(i => i.id === item.id);
                              return (
                                <button
                                  key={item.id}
                                  onClick={() => {
                                    if (isSelected) {
                                      setCalendarOutfitItems(prev => prev.filter(i => i.id !== item.id));
                                    } else {
                                      setCalendarOutfitItems(prev => [...prev, item]);
                                    }
                                  }}
                                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-all relative ${
                                    isSelected 
                                      ? 'border-olive-500 ring-2 ring-olive-200' 
                                      : 'border-transparent hover:border-stone-300'
                                  }`}
                                >
                                  {item.image_url ? (
                                    <img src={item.processed_image_url || item.image_url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full bg-stone-200 flex items-center justify-center">
                                      <Shirt className="w-4 h-4 text-stone-400" />
                                    </div>
                                  )}
                                  {isSelected && (
                                    <div className="absolute top-1 right-1 w-5 h-5 bg-olive-500 rounded-full flex items-center justify-center">
                                      <Check className="w-3 h-3 text-white" />
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Note */}
              <div>
                <Label>Note (optional)</Label>
                <Textarea 
                  placeholder="e.g., Need to bring a jacket, wear the new earrings..."
                  value={calendarOutfitNote}
                  onChange={(e) => setCalendarOutfitNote(e.target.value)}
                  className="mt-1 resize-none"
                  rows={2}
                />
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button onClick={saveCalendarOutfit} className="flex-1 bg-gradient-to-r from-olive-500 to-olive-400 text-white rounded-xl">
                  {editingSlotId ? 'Update Outfit' : 'Save Outfit'}
                </Button>
                {editingSlotId && selectedCalendarDate && (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      const key = formatDateKey(selectedCalendarDate);
                      removeCalendarSlot(key, editingSlotId);
                      setShowCalendarOutfitDialog(false);
                      setSelectedCalendarDate(null);
                      setEditingSlotId(null);
                      // Update viewing state if open
                      if (viewingCalendarOutfit) {
                        const remaining = getDaySlots(viewingCalendarOutfit.dayData).filter(s => s.id !== editingSlotId);
                        if (remaining.length > 0) {
                          setViewingCalendarOutfit({ date: viewingCalendarOutfit.date, dayData: { slots: remaining } });
                        } else {
                          setViewingCalendarOutfit(null);
                        }
                      }
                    }}
                    className="rounded-xl border-red-200 text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ========== PARTNER / FRIENDS WARDROBE DIALOG ========== */}
        <Dialog open={partnerDialogOpen} onOpenChange={(open) => { setPartnerDialogOpen(open); if (!open) { setActivePartner(null); setPartnerItems([]); setPartnerSelectedItems([]); setPartnerInviteCode(''); } }}>
          <DialogContent className="max-w-2xl w-[95vw] max-h-[95vh] flex flex-col overflow-hidden p-0 sm:p-0 rounded-2xl">
            <DialogHeader className="px-5 pt-5 pb-3 flex-shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center flex-shrink-0 ${partnerDialogType === 'friend' ? 'from-violet-500 to-purple-400' : 'from-pink-500 to-rose-400'}`}>
                  {partnerDialogType === 'friend' ? <Users className="w-5 h-5 text-white" /> : <HeartHandshake className="w-5 h-5 text-white" />}
                </div>
                <div>
                  <span className="block">{activePartner ? `${activePartner.display_name}'s Closet` : (partnerDialogType === 'friend' ? 'Friends Wardrobe' : 'Partner Wardrobe')}</span>
                  {activePartner && partnerDialogTab === 'browse' && (
                    <span className="block text-xs font-normal text-stone-400">{partnerItems.length} items</span>
                  )}
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 pb-5 space-y-4">
              {/* Tab Toggle */}
              <div className="flex rounded-xl border border-stone-200 p-1">
                <button
                  onClick={() => { setPartnerDialogTab('link'); fetchPartners(partnerDialogType); }}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${partnerDialogTab === 'link' ? `bg-gradient-to-r text-white ${partnerDialogType === 'friend' ? 'from-violet-500 to-purple-400' : 'from-pink-500 to-rose-400'}` : 'text-stone-600'}`}
                >
                  Link
                </button>
                <button
                  onClick={() => { setPartnerDialogTab('browse'); if (activePartner) fetchPartnerItems(activePartner.linked_user_id); }}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${partnerDialogTab === 'browse' ? `bg-gradient-to-r text-white ${partnerDialogType === 'friend' ? 'from-violet-500 to-purple-400' : 'from-pink-500 to-rose-400'}` : 'text-stone-600'}`}
                >
                  Browse
                </button>
                <button
                  onClick={() => { setPartnerDialogTab('suggestions'); fetchReceivedSuggestions(); fetchSentSuggestions(); }}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${partnerDialogTab === 'suggestions' ? `bg-gradient-to-r text-white ${partnerDialogType === 'friend' ? 'from-violet-500 to-purple-400' : 'from-pink-500 to-rose-400'}` : 'text-stone-600'}`}
                >
                  Suggestions
                </button>
              </div>

              {/* ===== LINK TAB ===== */}
              {partnerDialogTab === 'link' && (
                <div className="space-y-5">
                  {/* Generate Code */}
                  <div className={`p-4 bg-gradient-to-r rounded-2xl border ${partnerDialogType === 'friend' ? 'from-violet-50 to-stone-50 border-violet-100/50' : 'from-pink-50 to-stone-50 border-pink-100/50'}`}>
                    <p className="text-sm font-medium text-stone-700 mb-2">Share your invite code</p>
                    <p className="text-xs text-stone-500 mb-3">Your {partnerDialogType === 'friend' ? 'friend' : 'partner'} enters this code to link wardrobes</p>
                    {partnerInviteCode ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-white rounded-xl border border-stone-200 px-4 py-3 text-center">
                          <span className="text-2xl font-mono font-bold tracking-[0.3em] text-stone-800">{partnerInviteCode}</span>
                        </div>
                        <Button
                          onClick={() => { navigator.clipboard.writeText(partnerInviteCode); toast({ title: "Copied!", description: "Code copied to clipboard" }); }}
                          className="bg-stone-800 text-white rounded-xl h-12 w-12 p-0"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button onClick={() => generatePartnerCode(partnerDialogType)} className={`w-full bg-gradient-to-r text-white rounded-xl ${partnerDialogType === 'friend' ? 'from-violet-500 to-purple-400' : 'from-pink-500 to-rose-400'}`}>
                        <Link className="w-4 h-4 mr-2" />
                        Generate Code
                      </Button>
                    )}
                  </div>

                  {/* Enter Code */}
                  <div className="p-4 bg-stone-50 rounded-2xl border border-stone-100">
                    <p className="text-sm font-medium text-stone-700 mb-2">Enter {partnerDialogType === 'friend' ? "friend's" : "partner's"} code</p>
                    <p className="text-xs text-stone-500 mb-3">Enter the 6-character code shared with you</p>
                    <div className="flex gap-2">
                      <Input
                        value={partnerCodeInput}
                        onChange={(e) => setPartnerCodeInput(e.target.value.toUpperCase().slice(0, 6))}
                        placeholder="ABC123"
                        className="flex-1 text-center text-lg font-mono tracking-[0.2em] uppercase"
                        maxLength={6}
                        onKeyPress={(e) => e.key === 'Enter' && acceptPartnerCode(partnerDialogType)}
                      />
                      <Button onClick={() => acceptPartnerCode(partnerDialogType)} className="bg-stone-800 text-white rounded-xl" disabled={partnerCodeInput.length !== 6}>
                        <Check className="w-4 h-4 mr-1" />
                        Link
                      </Button>
                    </div>
                  </div>

                  {/* Linked list */}
                  {(() => {
                    const connectionList = partnerDialogType === 'friend' ? friends : partners;
                    const label = partnerDialogType === 'friend' ? 'Linked Friends' : 'Linked Partners';
                    const gradientClass = partnerDialogType === 'friend' ? 'from-violet-500 to-purple-400' : 'from-pink-500 to-rose-400';
                    return connectionList.length > 0 ? (
                      <div className="pt-2">
                        <p className="text-xs uppercase tracking-[0.2em] text-stone-400 font-medium mb-3">{label}</p>
                        <div className="space-y-2">
                          {connectionList.map(person => (
                            <div key={person.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-stone-100">
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${gradientClass} flex items-center justify-center text-white text-sm font-bold`}>
                                  {(person.display_name || '?').charAt(0).toUpperCase()}
                                </div>
                                <span className="text-sm font-medium text-stone-700">{person.display_name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" onClick={() => openPartnerWardrobe(person, partnerDialogType)} className="text-xs rounded-lg">
                                  <Eye className="w-3 h-3 mr-1" /> Browse
                                </Button>
                                <button onClick={() => unlinkPartner(person.linked_user_id, partnerDialogType)} className="p-1.5 text-stone-400 hover:text-red-500 transition-colors" title="Unlink">
                                  <Unlink className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              {/* ===== BROWSE TAB ===== */}
              {partnerDialogTab === 'browse' && (
                <div className="space-y-4">
                  {!activePartner ? (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                      <p className="text-sm text-stone-500 mb-3">Select {partnerDialogType === 'friend' ? 'a friend' : 'a partner'} to browse their wardrobe</p>
                      {(() => {
                        const connectionList = partnerDialogType === 'friend' ? friends : partners;
                        const gradientClass = partnerDialogType === 'friend' ? 'from-violet-500 to-purple-400' : 'from-pink-500 to-rose-400';
                        return connectionList.length > 0 ? (
                        <div className="space-y-2">
                          {connectionList.map(p => (
                            <button key={p.id} onClick={() => openPartnerWardrobe(p, partnerDialogType)} className="w-full p-3 bg-stone-50 rounded-xl text-left hover:bg-stone-100 transition-colors flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${gradientClass} flex items-center justify-center text-white text-sm font-bold`}>
                                {(p.display_name || '?').charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm font-medium text-stone-700">{p.display_name}</span>
                              <ChevronRight className="w-4 h-4 text-stone-400 ml-auto" />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-stone-400">Link {partnerDialogType === 'friend' ? 'a friend' : 'a partner'} first from the Link tab</p>
                      );
                      })()}
                    </div>
                  ) : (
                    <>
                      {/* Category filter — horizontally scrollable pills */}
                      <div className="overflow-x-auto overflow-y-hidden -mx-5 px-5 pb-2" style={{ WebkitOverflowScrolling: 'touch' }}>
                        <div className="flex gap-2 min-w-max">
                          {(() => {
                            // Build category counts with fuzzy matching
                            const typeMap = {
                              tops: ['t_shirts','shirts','blouses','sweaters','hoodies','tank_tops','crop_tops','cardigans'],
                              bottoms: ['jeans','pants','trousers','shorts','skirts','leggings','joggers','chinos'],
                              outerwear: ['jackets','coats','blazers','vests','parkas','leather_jackets','denim_jackets','bombers'],
                              dresses: ['dresses','jumpsuits','rompers'],
                              sportswear: ['activewear','gym_tops','gym_bottoms','sports_bras','sportswear'],
                              footwear: ['sneakers','boots','heels','sandals','flats','loafers','mules','slippers','platforms','shoes'],
                              accessories: ['bags','purses','clutches','backpacks','belts','hats','caps','scarves','jewelry']
                            };
                            const matchCategory = (item, f) => {
                              const cat = (item.primary_category || '').toLowerCase();
                              const rawCat = (item.category || '').toLowerCase();
                              const ct = (item.clothing_type || '').toLowerCase();
                              if (cat === f || rawCat === f || rawCat.includes(f)) return true;
                              if (typeMap[f] && typeMap[f].includes(ct)) return true;
                              return false;
                            };
                            const categories = ['all', 'tops', 'bottoms', 'dresses', 'outerwear', 'sportswear', 'footwear', 'accessories'];
                            // Only show categories that have items (always show 'all')
                            return categories.filter(cat => {
                              if (cat === 'all') return true;
                              return partnerItems.some(item => matchCategory(item, cat));
                            }).map(cat => {
                              const count = cat === 'all' ? partnerItems.length : partnerItems.filter(item => matchCategory(item, cat)).length;
                              return (
                                <button
                                  key={cat}
                                  onClick={() => setPartnerItemFilter(cat)}
                                  className={`px-4 py-2 rounded-full text-xs font-medium capitalize whitespace-nowrap transition-all ${
                                    partnerItemFilter === cat ? 'bg-stone-800 text-white shadow-sm' : 'bg-stone-100 text-stone-500 hover:bg-stone-200 active:bg-stone-300'
                                  }`}
                                >
                                  {cat === 'all' ? `All (${count})` : `${cat} (${count})`}
                                </button>
                              );
                            });
                          })()}
                        </div>
                      </div>

                      {/* Items grid — organized like real wardrobe */}
                      {loadingPartnerItems ? (
                        <div className="flex items-center justify-center py-16">
                          <Loader2 className="w-6 h-6 text-stone-300 animate-spin" />
                        </div>
                      ) : (
                        <>
                          {/* Show all items in a clean grid — no grouping */}
                          {partnerItemFilter === 'all' ? (
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                              {partnerItems.map(item => {
                                const isSelected = partnerSelectedItems.includes(item.id);
                                return (
                                  <button
                                    key={item.id}
                                    onClick={() => togglePartnerItemSelection(item.id)}
                                    className={`relative rounded-xl overflow-hidden border-2 transition-all aspect-square bg-stone-50 ${
                                      isSelected ? `ring-2 ${partnerDialogType === 'friend' ? 'border-violet-500 ring-violet-200' : 'border-pink-500 ring-pink-200'}` : 'border-transparent hover:border-stone-200'
                                    }`}
                                  >
                                    <img
                                      src={item.processed_image_url || item.image_url}
                                      alt={item.name}
                                      className="w-full h-full object-cover"
                                    />
                                    {isSelected && (
                                      <div className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center shadow-sm ${partnerDialogType === 'friend' ? 'bg-violet-500' : 'bg-pink-500'}`}>
                                        <Check className="w-3 h-3 text-white" />
                                      </div>
                                    )}
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1.5 pt-4">
                                      <p className="text-[10px] text-white truncate font-medium">{item.name}</p>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            /* Filtered single category view */
                            <>
                              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                                {partnerItems
                                  .filter(item => {
                                    const cat = (item.primary_category || '').toLowerCase();
                                    const rawCat = (item.category || '').toLowerCase();
                                    const clothingType = (item.clothing_type || '').toLowerCase();
                                    const f = partnerItemFilter;
                                    // Direct match on primary_category
                                    if (cat === f) return true;
                                    // Match raw category
                                    if (rawCat === f || rawCat.includes(f)) return true;
                                    // Fuzzy: map clothing_type to category
                                    const typeMap = {
                                      tops: ['t_shirts','shirts','blouses','sweaters','hoodies','tank_tops','crop_tops','cardigans'],
                                      bottoms: ['jeans','pants','trousers','shorts','skirts','leggings','joggers','chinos'],
                                      outerwear: ['jackets','coats','blazers','vests','parkas','leather_jackets','denim_jackets','bombers'],
                                      dresses: ['dresses','jumpsuits','rompers'],
                                      sportswear: ['activewear','gym_tops','gym_bottoms','sports_bras','sportswear'],
                                      footwear: ['sneakers','boots','heels','sandals','flats','loafers','mules','slippers','platforms','shoes'],
                                      accessories: ['bags','purses','clutches','backpacks','belts','hats','caps','scarves','jewelry']
                                    };
                                    if (typeMap[f] && typeMap[f].includes(clothingType)) return true;
                                    return false;
                                  })
                                  .map(item => {
                                    const isSelected = partnerSelectedItems.includes(item.id);
                                    return (
                                      <button
                                        key={item.id}
                                        onClick={() => togglePartnerItemSelection(item.id)}
                                        className={`relative rounded-xl overflow-hidden border-2 transition-all aspect-square bg-stone-50 ${
                                          isSelected ? `ring-2 ${partnerDialogType === 'friend' ? 'border-violet-500 ring-violet-200' : 'border-pink-500 ring-pink-200'}` : 'border-transparent hover:border-stone-200'
                                        }`}
                                      >
                                        <img
                                          src={item.processed_image_url || item.image_url}
                                          alt={item.name}
                                          className="w-full h-full object-cover"
                                        />
                                        {isSelected && (
                                          <div className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center shadow-sm ${partnerDialogType === 'friend' ? 'bg-violet-500' : 'bg-pink-500'}`}>
                                            <Check className="w-3 h-3 text-white" />
                                          </div>
                                        )}
                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1.5 pt-4">
                                          <p className="text-[10px] text-white truncate font-medium">{item.name}</p>
                                        </div>
                                      </button>
                                    );
                                  })}
                              </div>
                              {partnerItems.filter(item => {
                                const cat = (item.primary_category || '').toLowerCase();
                                const rawCat = (item.category || '').toLowerCase();
                                const clothingType = (item.clothing_type || '').toLowerCase();
                                const f = partnerItemFilter;
                                if (cat === f || rawCat === f || rawCat.includes(f)) return true;
                                const typeMap = { tops: ['t_shirts','shirts','blouses','sweaters','hoodies','tank_tops','crop_tops','cardigans'], bottoms: ['jeans','pants','trousers','shorts','skirts','leggings','joggers','chinos'], outerwear: ['jackets','coats','blazers','vests','parkas','leather_jackets','denim_jackets','bombers'], dresses: ['dresses','jumpsuits','rompers'], sportswear: ['activewear','gym_tops','gym_bottoms','sports_bras','sportswear'], footwear: ['sneakers','boots','heels','sandals','flats','loafers','mules','slippers','platforms','shoes'], accessories: ['bags','purses','clutches','backpacks','belts','hats','caps','scarves','jewelry'] };
                                if (typeMap[f] && typeMap[f].includes(clothingType)) return true;
                                return false;
                              }).length === 0 && (
                                <p className="text-center text-sm text-stone-400 py-8">No items in this category</p>
                              )}
                            </>
                          )}

                          {/* Suggestion builder */}
                          {partnerSelectedItems.length > 0 && (
                            <div className={`p-4 bg-gradient-to-r rounded-2xl border space-y-3 ${partnerDialogType === 'friend' ? 'from-violet-50 to-stone-50 border-violet-100/50' : 'from-pink-50 to-stone-50 border-pink-100/50'}`}>
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-stone-700">
                                  {partnerSelectedItems.length} item{partnerSelectedItems.length > 1 ? 's' : ''} selected
                                </p>
                                <button onClick={() => setPartnerSelectedItems([])} className="text-xs text-stone-400 hover:text-stone-600">Clear</button>
                              </div>
                              <div className="flex gap-1.5 overflow-x-auto pb-1">
                                {partnerSelectedItems.map(id => {
                                  const item = partnerItems.find(i => i.id === id);
                                  return item ? (
                                    <div key={id} className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border ${partnerDialogType === 'friend' ? 'border-violet-200' : 'border-pink-200'}`}>
                                      <img src={item.processed_image_url || item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                    </div>
                                  ) : null;
                                })}
                              </div>
                              <Input
                                value={partnerSuggestionNote}
                                onChange={(e) => setPartnerSuggestionNote(e.target.value)}
                                placeholder={partnerDialogType === 'friend' ? "Add a note... e.g. This would look great on you! 🤝" : "Add a note... e.g. Wear this for dinner 💕"}
                                className="text-sm"
                              />
                              <div className="flex gap-2">
                                <Select value={partnerSuggestionOccasion} onValueChange={setPartnerSuggestionOccasion}>
                                  <SelectTrigger className="flex-1 text-xs">
                                    <SelectValue placeholder="Occasion (optional)" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {['Date Night', 'Work', 'Casual', 'Party', 'Travel', 'Gym', 'Brunch', 'Wedding', 'Interview'].map(o => (
                                      <SelectItem key={o} value={o}>{o}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <Button onClick={sendOutfitSuggestion} className={`w-full bg-gradient-to-r text-white rounded-xl ${partnerDialogType === 'friend' ? 'from-violet-500 to-purple-400' : 'from-pink-500 to-rose-400'}`}>
                                <Send className="w-4 h-4 mr-2" />
                                Send Outfit Suggestion
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ===== SUGGESTIONS TAB ===== */}
              {partnerDialogTab === 'suggestions' && (
                <div className="space-y-4">
                  {/* Received */}
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-stone-400 font-medium mb-3">Received</p>
                    {receivedSuggestions.length === 0 ? (
                      <div className="text-center py-6 bg-stone-50 rounded-xl">
                        <MessageCircle className="w-8 h-8 text-stone-200 mx-auto mb-2" />
                        <p className="text-sm text-stone-400">No suggestions yet</p>
                        <p className="text-xs text-stone-400">Your {partnerDialogType === 'friend' ? "friends'" : "partner's"} outfit ideas will appear here</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {receivedSuggestions.map(sug => (
                          <div key={sug.id} className={`p-4 rounded-2xl border ${sug.status === 'pending' ? (partnerDialogType === 'friend' ? 'bg-gradient-to-r from-violet-50 to-white border-violet-100/50' : 'bg-gradient-to-r from-pink-50 to-white border-pink-100/50') : 'bg-stone-50 border-stone-100 opacity-70'}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className={`w-6 h-6 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-[10px] font-bold ${partnerDialogType === 'friend' ? 'from-violet-500 to-purple-400' : 'from-pink-500 to-rose-400'}`}>
                                  {(sug.from_name || '?').charAt(0).toUpperCase()}
                                </div>
                                <p className="text-sm font-medium text-stone-700">{sug.from_name}</p>
                                {sug.occasion && <Badge variant="secondary" className={`text-[10px] ${partnerDialogType === 'friend' ? 'bg-violet-100 text-violet-700' : 'bg-pink-100 text-pink-700'}`}>{sug.occasion}</Badge>}
                              </div>
                              <span className="text-[10px] text-stone-400">{new Date(sug.created_at).toLocaleDateString()}</span>
                            </div>
                            {sug.note && <p className="text-sm text-stone-600 mb-2 italic">"{sug.note}"</p>}
                            <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1">
                              {(sug.items || []).map(item => (
                                <div key={item.id} className="flex-shrink-0">
                                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-stone-100 border border-stone-200">
                                    <img src={item.processed_image_url || item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                  </div>
                                  <p className="text-[8px] text-stone-500 text-center mt-0.5 truncate w-14">{item.name}</p>
                                </div>
                              ))}
                            </div>
                            {sug.status === 'pending' ? (
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => respondToSuggestion(sug.id, 'accepted')} className="bg-olive-500 text-white text-xs flex-1 rounded-xl">
                                  <Check className="w-3 h-3 mr-1" /> Love it!
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => respondToSuggestion(sug.id, 'declined')} className="text-xs flex-1 rounded-xl">
                                  Not this time
                                </Button>
                              </div>
                            ) : (
                              <p className="text-xs text-center text-stone-400 capitalize">{sug.status}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Sent */}
                  <div className="pt-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-stone-400 font-medium mb-3">Sent by you</p>
                    {sentSuggestions.length === 0 ? (
                      <p className="text-sm text-stone-400 text-center py-4">No suggestions sent yet</p>
                    ) : (
                      <div className="space-y-2">
                        {sentSuggestions.slice(0, 5).map(sug => (
                          <div key={sug.id} className="p-3 bg-stone-50 rounded-xl border border-stone-100">
                            <div className="flex items-center justify-between mb-1.5">
                              <p className="text-xs text-stone-600">To {sug.to_name}</p>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${
                                sug.status === 'accepted' ? 'bg-olive-100 text-olive-700' :
                                sug.status === 'declined' ? 'bg-red-100 text-red-600' :
                                'bg-amber-100 text-amber-700'
                              }`}>{sug.status}</span>
                            </div>
                            <div className="flex gap-1">
                              {(sug.items || []).slice(0, 4).map(item => (
                                <div key={item.id} className="w-10 h-10 rounded-lg overflow-hidden bg-stone-100">
                                  <img src={item.processed_image_url || item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                </div>
                              ))}
                              {sug.note && <p className="text-[10px] text-stone-400 ml-2 self-center italic truncate">"{sug.note}"</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* ========== TRIP PICKER FOR CAPSULE PACKING ========== */}
        <Dialog open={showTripPickerForCapsule} onOpenChange={(open) => { setShowTripPickerForCapsule(open); if (!open) setCapsuleForTripPicker(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Luggage className="w-5 h-5 text-mud-500" />
                Choose a Trip
              </DialogTitle>
              <DialogDescription className="text-stone-500 text-sm">
                Select which trip to add items from "{capsuleForTripPicker?.name}" to.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2 max-h-[50vh] overflow-y-auto">
              {packingLists.length === 0 && (
                <div className="text-center py-8 text-stone-400">
                  <Luggage className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No trips yet. Create a packing list first!</p>
                </div>
              )}
              {packingLists.map(list => (
                <button
                  key={list.id}
                  onClick={() => {
                    if (!capsuleForTripPicker) return;
                    const existingIds = new Set(list.items.map(i => i.id));
                    const newItems = capsuleForTripPicker.items.filter(i => !existingIds.has(i.id));
                    if (newItems.length === 0) {
                      toast({ title: 'Already Added', description: `All items from "${capsuleForTripPicker.name}" are already in "${list.name}".` });
                      return;
                    }
                    const updated = { ...list, items: [...list.items, ...newItems], fromCapsule: capsuleForTripPicker.name };
                    setPackingLists(prev => prev.map(p => p.id === list.id ? updated : p));
                    toast({ title: '🧳 Added to Packing', description: `${newItems.length} items added to "${list.name}"` });
                    setShowTripPickerForCapsule(false);
                    setCapsuleForTripPicker(null);
                    setWardrobeMode('packing');
                    setViewingPackingList(updated);
                    setViewingCapsule(null);
                  }}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-stone-200 hover:border-mud-300 hover:bg-mud-50 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-mud-500 to-mud-400 flex items-center justify-center flex-shrink-0">
                    <Luggage className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-800 truncate">{list.name}</p>
                    <p className="text-xs text-stone-500">{list.items.length} items{list.destination ? ` · ${list.destination}` : ''}</p>
                  </div>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* ========== INSPIRATION BOARD DIALOG ========== */}
        <Dialog open={showBoardDialog} onOpenChange={(open) => { setShowBoardDialog(open); if (!open) { setEditingBoardId(null); setCurrentBoard({ name: '', description: '', inspiration: [] }); } }}>
          <DialogContent className="max-h-[85vh] overflow-y-auto top-auto bottom-0 left-0 right-0 translate-x-0 translate-y-0 w-full rounded-t-2xl rounded-b-none pb-[env(safe-area-inset-bottom,1rem)] sm:top-[50%] sm:bottom-auto sm:left-[50%] sm:right-auto sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-2xl sm:max-w-lg sm:pb-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-stone-600 to-stone-500 flex items-center justify-center">
                  <Bookmark className="w-5 h-5 text-white" />
                </div>
                {editingBoardId ? 'Edit Board' : 'Create Inspiration Board'}
              </DialogTitle>
              <DialogDescription className="text-stone-500 text-sm">
                {editingBoardId ? 'Update your board name and description.' : 'Create a mood board to collect outfit ideas and style inspiration.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Board Name</Label>
                <Input 
                  placeholder="e.g., Summer Vibes, Minimalist Goals"
                  value={currentBoard.name}
                  onChange={(e) => setCurrentBoard(p => ({ ...p, name: e.target.value }))}
                  className="mt-1"
                  autoFocus
                />
              </div>
              <div>
                <Label>Description <span className="text-stone-400 font-normal">(optional)</span></Label>
                <Textarea 
                  placeholder="What's this board about?"
                  value={currentBoard.description || ''}
                  onChange={(e) => setCurrentBoard(p => ({ ...p, description: e.target.value }))}
                  className="mt-1 resize-none"
                  rows={2}
                />
              </div>
              <Button onClick={saveBoard} className="w-full bg-gradient-to-r from-stone-600 to-stone-500 text-white rounded-xl">
                {editingBoardId ? 'Save Changes' : 'Create Board'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ========== HD IMAGE MODAL ========== */}
        {hdImageModal && (
          <Dialog open={!!hdImageModal} onOpenChange={() => setHdImageModal(null)}>
            <DialogContent className="max-w-4xl p-0 overflow-hidden">
              <img src={hdImageModal} alt="HD View" className="w-full h-auto" />
              <button 
                onClick={() => setHdImageModal(null)}
                className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </DialogContent>
          </Dialog>
        )}

        {/* ========== ADD TO LOOKBOOK DIALOG ========== */}
        <Dialog open={showLookbookDialog} onOpenChange={setShowLookbookDialog}>
          <DialogContent className="max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-terracotta-500" />
                Add to Stylebook
              </DialogTitle>
              <DialogDescription>
                {lookbookItemToAdd ? `Add "${lookbookItemToAdd.name}" to a stylebook` : 'Select a stylebook'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              {/* Existing Lookbooks */}
              {loadingLookbooks ? (
                <div className="text-center py-4 text-stone-400 text-sm">Loading stylebooks...</div>
              ) : userLookbooks.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-stone-600">Your Stylebooks</p>
                  {userLookbooks.map((lb) => (
                    <button
                      key={lb.id}
                      onClick={() => addItemToExistingLookbook(lb.id, lb.name)}
                      className="w-full text-left p-3 rounded-xl border border-stone-200 hover:border-terracotta-300 hover:bg-terracotta-50/50 transition-all flex items-center justify-between"
                    >
                      <div>
                        <span className="font-medium text-stone-800">{lb.name}</span>
                        {lb.style && <span className="text-xs text-stone-400 ml-2">• {lb.style}</span>}
                      </div>
                      <Plus className="w-4 h-4 text-terracotta-400" />
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-stone-400 text-center py-2">No stylebooks yet. Create one below!</p>
              )}
              
              {/* Create New Lookbook */}
              <div className="border-t border-stone-200 pt-4">
                <p className="text-sm font-medium text-stone-600 mb-2">Create New Stylebook</p>
                <div className="flex gap-2">
                  <Input
                    value={newLookbookName}
                    onChange={(e) => setNewLookbookName(e.target.value)}
                    placeholder="Stylebook name..."
                    className="flex-1 rounded-xl"
                    onKeyDown={(e) => e.key === 'Enter' && createLookbookAndAddItem()}
                  />
                  <Button
                    onClick={createLookbookAndAddItem}
                    disabled={!newLookbookName.trim()}
                    className="bg-gradient-to-r from-terracotta-500 to-terracotta-400 text-white rounded-xl"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Create
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
